import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, createContext, useContext } from "react";
import config from '../config';
import { useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTriangleExclamation,
  faCircle,
  faCircleInfo,
  faLightbulb,
  faArrowRightArrowLeft,
  faTag,
  faPalette,
  faListCheck,
  faCloudArrowUp,
  faCloud,
  faCircleQuestion,
  faPenToSquare,
  faPencil,
  faFloppyDisk,
  faClockRotateLeft,
  faComments,
  faCopy,
  faChevronLeft,
  faChevronRight,
  faArrowUp,
  faArrowDown,
  faEye,
  faEyeSlash,
  faTrash,
  faFileImport,
  faWandMagicSparkles,
  faCropSimple,
  faFileZipper,
  faXmark,
  faTableColumns,
  faMagnifyingGlass,
  faImage,
  faArrowUpRightFromSquare,
  faDownload,
  faFileCsv,
  faFileCode,
  faChevronDown,
  faNoteSticky
} from '@fortawesome/free-solid-svg-icons'
import {faCircleCheck as faCheck, faSquare, faSquareCheck } from '@fortawesome/free-regular-svg-icons';
import {Modal, Container, Form, OverlayTrigger, Popover} from 'react-bootstrap';
import QRCode from "react-qr-code";
import Spinner from 'react-bootstrap/Spinner';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import ReactMarkdown from 'react-markdown'
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort'
import { arrayMoveImmutable } from "array-move";
import EditableTextArea from '../components/EditableTextArea';
import BomMarkdownTextarea from '../components/BomMarkdownTextarea';
import EditableInput from '../components/EditableInput';
import EditableImage from '../components/EditableImage';
import EditableTags from '../components/EditableTags';
import EditableLicense from '../components/EditableLicense';
import LazyImage from '../components/LazyImage';
import EditableMoveStep from '../components/EditableMoveStep';
import HeaderNavbar from '../components/HeaderNavbar';
import { useAuthContext } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import Page404 from './404';
import JSConfetti from 'js-confetti'
import * as markerjs2 from 'markerjs2';
import * as cropro from 'cropro';
import api from "../lib/request.js";
import Axios from 'axios';
import GuideMetadata from '../components/guide/GuideMetadata';
import GuideComments from '../components/guide/GuideComments';
import GuideNotes from '../components/guide/GuideNotes';
import GuideStepAiModal from '../components/guide/GuideStepAiModal';
import useGuideStepAi from '../hooks/useGuideStepAi';
import { buildAuthenticatedWebSocketURL } from '../lib/websocket';
import HistoryViewer from '../components/guide/HistoryViewer';
import { bomReferenceRemarkPlugin, parseMarkdownReferenceHref } from '../lib/bomMarkdown';
import { appendGuideImage, normalizeManagedUploadFilename, replaceGuideImage } from '../lib/guideImages';
import { patchPopupScrollLock, resetPopupScrollLock } from '../lib/popupScrollLock';
import {
  BOM_AUTO_URL_PROVIDERS,
  BOM_AUTO_URL_PROVIDER_MAP,
  BOM_AUTO_URL_PROVIDER_KEYS
} from '../lib/bomProviders';

const STEP_PLACEHOLDER = '/images/step-placeholder.svg';
const GUIDE_INDEX_PREFERENCE_KEY = 'guide-index-visible';
const GUIDE_MAX_STEPS = 300;
const BOM_DEFAULT_CATEGORIES = [
  'mechanics',
  'electronics',
  'fasteners',
  'wiring',
  'printing',
  'enclosure',
  'consumables',
  'tools',
  'software',
  'documentation'
];
const BOM_DEFAULT_URL_TYPES = [
  'supplier',
  'source',
  'model file',
  'datasheet',
  'documentation',
  'download'
];
const BOM_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;
const BOM_TRACKING_INTENT_KEY = 'stepwisedocs-bom-tracking-intent';
const BOM_TRACKING_DEFAULT_NOTE_MAX_LENGTH = 300;
const BOM_OWNERSHIP_FILTER = Object.freeze({
  ALL: 'all',
  OWNED: 'owned',
  MISSING: 'missing'
});
const BOM_CSV_EXPORT_FIELDS = [
  { key: 'position', label: 'Position' },
  { key: 'name', label: 'Name' },
  { key: 'identifier', label: 'Identifier' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'category', label: 'Category' },
  { key: 'versions', label: 'Versions' },
  { key: 'description', label: 'Description' },
  { key: 'urls', label: 'URLs' },
  { key: 'generatedUrls', label: 'Generated URLs' },
  { key: 'autoUrlProviders', label: 'Auto URL Providers' },
  { key: 'images', label: 'Images' }
];
const BOM_IMPORT_METHOD = Object.freeze({
  DIRECT_FILE: 'direct_file',
  EXTERNAL_URL: 'external_url',
  RAW_TEXT: 'raw_text'
});
const BOM_IMPORT_METHOD_OPTIONS = Object.freeze([
  Object.freeze({
    key: BOM_IMPORT_METHOD.DIRECT_FILE,
    title: 'I already have a stepwisedocs-compatible file.',
    description: 'Upload a JSON file generated for StepWiseDocs.'
  }),
  Object.freeze({
    key: BOM_IMPORT_METHOD.EXTERNAL_URL,
    title: 'My BOM is on another public site.',
    description: 'Generate a prompt that asks your preferred AI model to extract your BOM from a URL.'
  }),
  Object.freeze({
    key: BOM_IMPORT_METHOD.RAW_TEXT,
    title: 'I have my BOM in a text file or similar.',
    description: 'Generate a prompt that asks your preferred AI model to convert pasted text into the required schema.'
  })
]);
const BOM_IMPORT_STRATEGY = Object.freeze({
  REPLACE_ALL: 'replace_all',
  MERGE_BY_IDENTIFIER: 'merge_by_identifier',
  APPEND_ONLY: 'append_only'
});
const BOM_IMPORT_STRATEGY_OPTIONS = Object.freeze([
  Object.freeze({
    key: BOM_IMPORT_STRATEGY.MERGE_BY_IDENTIFIER,
    title: 'Overwrite matching identifiers',
    description: 'Update items with the same identifier and append new identifiers.'
  }),
  Object.freeze({
    key: BOM_IMPORT_STRATEGY.REPLACE_ALL,
    title: 'Overwrite entire BOM',
    description: 'Replace all current items with the imported file.'
  }),
  Object.freeze({
    key: BOM_IMPORT_STRATEGY.APPEND_ONLY,
    title: 'Append only',
    description: 'Add all imported items and keep existing BOM items unchanged.'
  })
]);
const BOM_IMPORT_SCHEMA = 'stepwisedocs-bom-import-v1';
const BOM_IMPORT_SCHEMA_VERSION = 1;
const BOM_IMPORT_PROMPT_IMAGE_MAX_KB = 2048;
const MARKDOWN_EXPORT_IMAGE_OPTIONS = Object.freeze([
  Object.freeze({ value: 'big', label: 'Big' }),
  Object.freeze({ value: 'medium', label: 'Medium' }),
  Object.freeze({ value: 'small', label: 'Small' }),
  Object.freeze({ value: 'none', label: 'No images' }),
  Object.freeze({ value: 'original', label: 'Original size' })
]);
const MARKDOWN_EXPORT_DEFAULT_IMAGE_SIZE = 'medium';
const MARKDOWN_EXPORT_IMAGE_VALUES = new Set(MARKDOWN_EXPORT_IMAGE_OPTIONS.map((option) => option.value));
const GUIDE_STEP_AI_MAX_CONTEXT_STEPS = 8;
const GUIDE_STEP_AI_PREVIOUS_CONTEXT_DEFAULT = 2;
const GUIDE_STEP_AI_FOLLOWING_CONTEXT_DEFAULT = 0;
const GUIDE_IMAGE_LIGHTBOX_DEFAULT_VIEWPORT_RATIO = 0.8;
const GUIDE_IMAGE_LIGHTBOX_MIN_VIEWPORT_RATIO = 0.5;
const GUIDE_IMAGE_LIGHTBOX_MAX_VIEWPORT_RATIO = 0.95;
const GUIDE_PUBLISH_VALIDATION_ERROR_CODE = 'GUIDE_PUBLISH_INCOMPLETE_STEPS';
const GUIDE_STEP_AI_CONTEXT_OPTIONS = Object.freeze([
  Object.freeze({ value: 0, label: 'Do not use' }),
  ...Array.from({ length: GUIDE_STEP_AI_MAX_CONTEXT_STEPS }, (_, index) => Object.freeze({
    value: index + 1,
    label: String(index + 1)
  }))
]);

const jsConfetti = new JSConfetti()

function normalizeGuideStepAiContextStepCount(value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  const bounded = Math.max(0, Math.min(GUIDE_STEP_AI_MAX_CONTEXT_STEPS, Math.floor(numeric)));
  return bounded;
}

function normalizeGuideImageLightboxViewportRatio(value) {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) {
    return GUIDE_IMAGE_LIGHTBOX_DEFAULT_VIEWPORT_RATIO;
  }
  return Math.max(
    GUIDE_IMAGE_LIGHTBOX_MIN_VIEWPORT_RATIO,
    Math.min(GUIDE_IMAGE_LIGHTBOX_MAX_VIEWPORT_RATIO, numeric)
  );
}

function isVideoUrl(url) {
  const value = (url || '').toString();
  return value.indexOf('https://www.youtube.com/') === 0 || value.indexOf('https://youtu.be/') === 0;
}

function isGuideStepLightboxImage(stepImage) {
  const type = (stepImage?.type || '').toString().trim().toLowerCase();
  const url = (stepImage?.url || '').toString();
  return !!url && type !== 'video' && !isVideoUrl(url);
}

function getLightboxStepImages(step) {
  const stepImages = Array.isArray(step?.images) ? step.images : [];
  return stepImages.filter(isGuideStepLightboxImage);
}

const createStepId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

function normalizeMarkdownExportImageSize(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  return MARKDOWN_EXPORT_IMAGE_VALUES.has(normalized) ? normalized : MARKDOWN_EXPORT_DEFAULT_IMAGE_SIZE;
}

const getGuideSnapshot = (guide) => {
  if (!guide) return '';
  const snapshot = {
    title: guide.title || '',
    description: guide.description || '',
    whatyouneed: guide.whatyouneed || '',
    difficulty: guide.difficulty || '',
    license: guide.license || '',
    tags: guide.tags || [],
    published: !!guide.published,
    mainImage: guide.mainImage || null,
    steps: Array.isArray(guide.steps) ? guide.steps : []
  };
  return JSON.stringify(snapshot);
};

function normalizeBomReferenceKey(value) {
  return (value || '').toString().trim().toLowerCase();
}

const BOM_URL_VIEW_PARAM = 'bomView';
const BOM_URL_FOCUS_PARAM = 'bomFocus';
const BOM_URL_VERSION_PARAM = 'bomVersion';
const BOM_URL_ITEM_PARAM = 'bomItem';
const BOM_FILTER_TYPES = new Set(['category', 'module', 'version']);

function normalizeBomView(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (normalized === 'category') return 'category';
  if (normalized === 'module') return 'module';
  return 'none';
}

function normalizeBomFilterType(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  return BOM_FILTER_TYPES.has(normalized) ? normalized : '';
}

function getBomUrlState(search) {
  const params = new URLSearchParams(search || '');
  return {
    groupBy: normalizeBomView(params.get(BOM_URL_VIEW_PARAM)),
    focusedGroup: (params.get(BOM_URL_FOCUS_PARAM) || '').toString().trim(),
    versionFilter: (params.get(BOM_URL_VERSION_PARAM) || '').toString().trim()
  };
}

function buildBomGroupAnchorToken(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (!normalized) return 'empty';
  return normalized
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'group';
}

function normalizeBomTrackingPayload(payload) {
  const noteMaxLengthValue = Number(payload?.noteMaxLength);
  const noteMaxLength = Number.isFinite(noteMaxLengthValue) && noteMaxLengthValue > 0
    ? Math.floor(noteMaxLengthValue)
    : BOM_TRACKING_DEFAULT_NOTE_MAX_LENGTH;
  const seen = new Set();
  const items = (Array.isArray(payload?.items) ? payload.items : [])
    .map((item) => {
      const stepId = (item?.stepId || '').toString().trim();
      if (!stepId || seen.has(stepId)) return null;
      seen.add(stepId);
      const note = (item?.note || '').toString().slice(0, noteMaxLength);
      return {
        stepId,
        stepPosition: Number.isFinite(Number(item?.stepPosition)) ? Number(item.stepPosition) : null,
        identifier: (item?.identifier || '').toString().trim(),
        owned: item?.owned === true,
        note,
        hasNote: !!note.trim(),
        updatedAt: item?.updatedAt || null
      };
    })
    .filter(Boolean);
  return { noteMaxLength, items };
}

function toClientBomReferenceItems(rawItems) {
  const items = Array.isArray(rawItems) ? rawItems : [];
  return items
    .map((item) => {
      const identifier = (item?.identifier || '').toString().trim().toLowerCase();
      if (!identifier) return null;

      const title = (item?.name || item?.title || '').toString().trim();
      const description = (item?.description || item?.content || '').toString();
      const quantity = (item?.quantity || '').toString().trim();
      const category = (item?.category || '').toString().trim();
      const module = (item?.module || '').toString().trim();
      const versions = Array.isArray(item?.versions)
        ? item.versions.map((version) => (version || '').toString().trim()).filter(Boolean)
        : [];

      let imageUrl = '';
      if (item?.imageUrl) {
        imageUrl = (item.imageUrl || '').toString().trim();
      } else if (item?.image?.url) {
        imageUrl = item.image.url;
      } else if (Array.isArray(item?.images) && item.images[0]?.url) {
        imageUrl = item.images[0].url;
      }

      return {
        stepId: (item?.stepId || '').toString().trim(),
        position: Number(item?.position) || null,
        identifier,
        name: title || identifier,
        description,
        quantity,
        category,
        module,
        versions,
        imageUrl
      };
    })
    .filter(Boolean);
}

function toClientBomReferences(rawBoms) {
  const boms = Array.isArray(rawBoms) ? rawBoms : [];
  return boms
    .map((bom) => {
      const slugURL = (bom?.slugURL || '').toString().trim();
      if (!slugURL) return null;
      return {
        id: bom?.id,
        slugURL,
        title: (bom?.title || slugURL).toString().trim() || slugURL,
        items: toClientBomReferenceItems(bom?.items)
      };
    })
    .filter(Boolean);
}

function buildBomReferenceLookup(catalog) {
  const boms = Array.isArray(catalog) ? catalog : [];
  const bomMap = new Map();

  boms.forEach((bom) => {
    const bomKey = normalizeBomReferenceKey(bom?.slugURL);
    if (!bomKey) return;
    const itemMap = new Map();
    const items = Array.isArray(bom?.items) ? bom.items : [];
    items.forEach((item) => {
      const itemKey = normalizeBomReferenceKey(item?.identifier);
      if (!itemKey || itemMap.has(itemKey)) return;
      itemMap.set(itemKey, item);
    });
    bomMap.set(bomKey, {
      ...bom,
      itemMap
    });
  });

  return bomMap;
}

function mergeBomReferenceItems(serverItems, draftItems) {
  const mergedByIdentifier = new Map();
  const addItems = (items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      const key = normalizeBomReferenceKey(item?.identifier);
      if (!key) return;
      mergedByIdentifier.set(key, item);
    });
  };
  addItems(serverItems);
  addItems(draftItems);
  return Array.from(mergedByIdentifier.values());
}

function toClientGuideReferences(rawGuides) {
  const guides = Array.isArray(rawGuides) ? rawGuides : [];
  return guides
    .map((entry) => {
      const slugURL = (entry?.slugURL || '').toString().trim();
      if (!slugURL) return null;
      const type = (entry?.type || 'guide').toString().trim().toLowerCase();
      if (type === 'bom') return null;
      return {
        id: entry?.id,
        slugURL,
        title: (entry?.title || slugURL).toString().trim() || slugURL
      };
    })
    .filter(Boolean);
}

function buildGuideReferenceLookup(catalog) {
  const guides = Array.isArray(catalog) ? catalog : [];
  const guideMap = new Map();
  guides.forEach((entry) => {
    const key = normalizeBomReferenceKey(entry?.slugURL);
    if (!key || guideMap.has(key)) return;
    guideMap.set(key, entry);
  });
  return guideMap;
}

function GuideHome() {
  const { isAuthenticated, isSiteAdmin, canAdminOrganization, userInfo } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  let { project, guide } = useParams();

  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeContentIndex, setActiveContentIndex] = useState(null);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [contentValue, setContentValue] = useState('');
  const {
    state: guideStepAiState, change: setGuideStepAiState, open: openAiSession,
    close: closeGuideStepAiModal, refresh: refreshGuideStepAiConnectionState,
    generate: requestGuideStepAiText, selectModel: handleGuideStepAiModelChange
  } = useGuideStepAi(`${project}/${guide}`, isAuthenticated ? userInfo?.username : null);
  const [activeImages, setActiveImages] = useState([]);
  const [uploadingStepId, setUploadingStepId] = useState(null);
  const [hoveredStepIndex, setHoveredStepIndex] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [referenceLightboxImage, setReferenceLightboxImage] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [selectedFullImageIndex, setSelectedFullImageIndex] = useState(0);
  const [connectedClients, setConnectedClients] = useState([]);
  const [guideProgress, setGuideProgress] = useState({ tracking: false, steps: [] });
  const [editorHeight, setEditorHeight] = useState(400);
  const [dragOverStepIndex, setDragOverStepIndex] = useState(null);
  const [openAllCommentsSignal, setOpenAllCommentsSignal] = useState(0);
  const [guideNotes, setGuideNotes] = useState([]);
  const [bomQuickItem, setBomQuickItem] = useState({
    name: '',
    identifier: '',
    quantity: '',
    category: '',
    module: '',
    versions: '',
    urls: [{ type: '', url: '' }],
    description: ''
  });
  const [bomVersionFilter, setBomVersionFilter] = useState(() => getBomUrlState(location.search).versionFilter);
  const [bomGroupBy, setBomGroupBy] = useState(() => getBomUrlState(location.search).groupBy);
  const [bomFocusedGroup, setBomFocusedGroup] = useState(() => getBomUrlState(location.search).focusedGroup);
  const [, setOpenBomItemStepIds] = useState([]);
  const [bomSelectedItemKey, setBomSelectedItemKey] = useState('');
  const [bomSearchTerm, setBomSearchTerm] = useState('');
  const [bomTracking, setBomTracking] = useState(() => normalizeBomTrackingPayload(null));
  const [bomOwnershipFilter, setBomOwnershipFilter] = useState(BOM_OWNERSHIP_FILTER.ALL);
  const [bomNotesOnlyFilter, setBomNotesOnlyFilter] = useState(false);
  const [bomTrackingSavingKey, setBomTrackingSavingKey] = useState('');
  const [bomNoteEditor, setBomNoteEditor] = useState({
    itemKey: '',
    text: '',
    error: ''
  });
  const [openNotesStepKey, setOpenNotesStepKey] = useState(null);
  const [todoFocusNoteId, setTodoFocusNoteId] = useState(null);
  const intervalIdRef = useRef(null);
  const ignoreContentBlurRef = useRef(false);
  const processedSocketMessageRef = useRef(null);
  const pendingCommentIndexRef = useRef(0);
  const pendingTodoIndexRef = useRef(0);
  const progressSyncRef = useRef({ inFlight: false, pending: null });
  const lastSavedSnapshotRef = useRef(null);
  const activeImagesRef = useRef([]);
  const imageLightboxRef = useRef(null);
  const referenceImageLightboxRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showMarkdownExportModal, setShowMarkdownExportModal] = useState(false);
  const [showCsvExportModal, setShowCsvExportModal] = useState(false);
  const [showPublishValidationModal, setShowPublishValidationModal] = useState(false);
  const [publishValidationMessage, setPublishValidationMessage] = useState('');
  const [publishValidationSteps, setPublishValidationSteps] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [markdownExportLoading, setMarkdownExportLoading] = useState(false);
  const [markdownExportError, setMarkdownExportError] = useState('');
  const [csvExportLoading, setCsvExportLoading] = useState(false);
  const [csvExportError, setCsvExportError] = useState('');
  const [stepwisedocsExportLoading, setStepwisedocsExportLoading] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeProjectDescription: true,
    includeIndex: true,
    images: 'small'
  });
  const [markdownExportOptions, setMarkdownExportOptions] = useState({
    includeProjectDescription: true,
    includeIndex: true,
    includeRelatedBoms: false,
    images: MARKDOWN_EXPORT_DEFAULT_IMAGE_SIZE
  });
  const [csvExportOptions, setCsvExportOptions] = useState({
    includeHeader: true,
    fields: BOM_CSV_EXPORT_FIELDS.map((field) => field.key),
    versions: []
  });
  const [showBomImportModal, setShowBomImportModal] = useState(false);
  const [bomImportMethod, setBomImportMethod] = useState('');
  const [bomImportPrompt, setBomImportPrompt] = useState('');
  const [bomImportSourceUrl, setBomImportSourceUrl] = useState('');
  const [bomImportSourceText, setBomImportSourceText] = useState('');
  const [bomImportFile, setBomImportFile] = useState(null);
  const [bomImportLoading, setBomImportLoading] = useState(false);
  const [bomImportErrors, setBomImportErrors] = useState([]);
  const [bomImportStatus, setBomImportStatus] = useState('');
  const [bomImportStrategy, setBomImportStrategy] = useState(BOM_IMPORT_STRATEGY.MERGE_BY_IDENTIFIER);
  const [bomImportPromptGenerated, setBomImportPromptGenerated] = useState(false);
  const [bomImportPromptCopied, setBomImportPromptCopied] = useState(false);
  const exportIntentHandledRef = useRef(false);
  const commentIntentHandledRef = useRef(false);
  const progressIntentHandledRef = useRef(false);
  const bomItemIntentHandledRef = useRef('');
  const bomGroupIntentHandledRef = useRef('');
  const bomEditModeScrollAnchorRef = useRef(null);
  const bomEditModeScrollRestoreRafRef = useRef(null);
  const latestLocationSearchRef = useRef(location.search);
  const fetchDataRef = useRef(null);
  const saveRef = useRef(null);
  const downloadStepwisedocsExportRef = useRef(null);
  const startTrackingRef = useRef(null);
  const [commentAutoOpen, setCommentAutoOpen] = useState(null);
  const [historyMode, setHistoryMode] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historySelectedRev, setHistorySelectedRev] = useState(null);
  const [historySelectedData, setHistorySelectedData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCache, setHistoryCache] = useState({});
  const [showGuideIndex, setShowGuideIndex] = useState(() => {
    try {
      const stored = localStorage.getItem(GUIDE_INDEX_PREFERENCE_KEY);
      if (stored === '1') return true;
      if (stored === '0') return false;
    } catch (e) {
      // no-op: fallback to viewport heuristic
    }
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768;
    }
    return true;
  });

  const [socketUrl, setSocketUrl] = useState(null);
  const [socketRetryKey, setSocketRetryKey] = useState(0);
  const socketReconnectTimerRef = useRef(null);
  const socketRequestIdRef = useRef(0);
  const shouldMaintainSocketRef = useRef(false);
  const socketAccessDeniedRef = useRef(false);

  const projectOrgId = data?.project?.org;
  const guideSteps = useMemo(() => (
    Array.isArray(data?.guide?.steps) ? data.guide.steps : []
  ), [data?.guide?.steps]);
  const guideStepAiStepIndex = useMemo(() => {
    if (!guideStepAiState.stepId) return -1;
    return guideSteps.findIndex((step) => step?.stepId === guideStepAiState.stepId);
  }, [guideStepAiState.stepId, guideSteps]);
  const guideStepAiStep = guideStepAiStepIndex >= 0 ? guideSteps[guideStepAiStepIndex] : null;
  const guideStepAiImagesKey = JSON.stringify(guideStepAiStep?.images || []);
  useEffect(() => {
    // Remote uploads or annotations can change the image context while a request runs.
    setGuideStepAiState((previous) => previous);
  }, [guideStepAiImagesKey, setGuideStepAiState]);
  const canManageGuide = isAuthenticated && projectOrgId !== undefined && canAdminOrganization(projectOrgId);
  const isGuideEditable = editMode && canManageGuide;
  const canModerateComments = isAuthenticated && (isSiteAdmin || canAdminOrganization(projectOrgId));
  const canManageNotes = canManageGuide && editMode;
  const guideComments = Array.isArray(data?.comments) ? data.comments : [];
  const pendingModerationCount = canModerateComments
    ? guideComments.filter(comment => comment?.status === 'pending').length
    : 0;
  const pendingTodoCount = (Array.isArray(guideNotes) ? guideNotes : [])
    .filter(note => note?.isTodo && note?.todoStatus !== 'done')
    .length;
  const pendingTodoLabel = `Pending TODOs (${pendingTodoCount})`;
  const currentSnapshot = getGuideSnapshot(data?.guide);
  const hasUnsavedChanges =
    !!currentSnapshot && !!lastSavedSnapshotRef.current && currentSnapshot !== lastSavedSnapshotRef.current;
  const isDraft = data?.guide ? !data.guide.published : false;
  const guideType = (data?.guide?.type || 'guide').toString().toLowerCase();
  const isBomGuide = guideType === 'bom';
  const entityLabel = isBomGuide ? 'BOM' : 'Guide';
  const showGuideHistoryAction = editMode && !isBomGuide;
  const showCommentsAction = canModerateComments;
  const showTodoAction = canManageNotes;
  const showReviewToolbarActions = showGuideHistoryAction || showCommentsAction || showTodoAction;
  const showBomImportAction = editMode && isBomGuide;
  const showEditModeToolbarActions = editMode;
  const showEditToggleDivider = showReviewToolbarActions || showEditModeToolbarActions;
  const selectedUploadingStep = useMemo(() => {
    if (!guideSteps.length) return null;
    return guideSteps.find((step) => step?.stepId === uploadingStepId) || guideSteps[0] || null;
  }, [guideSteps, uploadingStepId]);
  const remoteUploadSteps = useMemo(() => {
    return guideSteps.reduce((acc, step, index) => {
      const stepId = (step?.stepId || '').toString().trim();
      const position = Number(step?.position) || index + 1;
      if (!stepId) return acc;
      acc.push({
        stepId,
        position,
        title: (step?.title || `Step ${position}`).toString()
      });
      return acc;
    }, []);
  }, [guideSteps]);
  const remoteGuideState = useMemo(() => ({
    cmd: 'remoteGuideState',
    editorEditable: isGuideEditable,
    steps: remoteUploadSteps,
    currentStepId: selectedUploadingStep?.stepId || null,
    currentStepPosition: selectedUploadingStep?.position || null,
    currentStepTitle: selectedUploadingStep?.title || '',
    stepCount: remoteUploadSteps.length,
    canAddStep: remoteUploadSteps.length < GUIDE_MAX_STEPS,
    canDeleteCurrent: remoteUploadSteps.length > 0
  }), [isGuideEditable, remoteUploadSteps, selectedUploadingStep]);
  const markdownRemarkPlugins = useMemo(() => [bomReferenceRemarkPlugin], []);
  const bomReferenceCatalog = useMemo(() => {
    const catalog = toClientBomReferences(data?.bomReferences);
    if (!isBomGuide || !data?.guide?.slugURL) {
      return catalog;
    }

    const currentBomKey = normalizeBomReferenceKey(data.guide.slugURL);
    const serverCurrentBom = catalog.find((entry) => normalizeBomReferenceKey(entry?.slugURL) === currentBomKey);
    const draftItems = toClientBomReferenceItems(data?.guide?.steps);
    const currentBomEntry = {
      id: data?.guide?.id,
      slugURL: data.guide.slugURL,
      title: data.guide.title || data.guide.slugURL,
      items: mergeBomReferenceItems(serverCurrentBom?.items, draftItems)
    };

    let replaced = false;
    const merged = catalog.map((entry) => {
      if (normalizeBomReferenceKey(entry?.slugURL) !== currentBomKey) return entry;
      replaced = true;
      return currentBomEntry;
    });

    if (!replaced) {
      merged.push(currentBomEntry);
    }
    return merged;
  }, [data?.bomReferences, data?.guide?.id, data?.guide?.slugURL, data?.guide?.title, data?.guide?.steps, isBomGuide]);
  const bomReferenceLookup = useMemo(() => buildBomReferenceLookup(bomReferenceCatalog), [bomReferenceCatalog]);
  const guideReferenceCatalog = useMemo(() => {
    const catalog = toClientGuideReferences(data?.guides);
    if (isBomGuide || !data?.guide?.slugURL) {
      return catalog;
    }

    const currentGuideEntry = {
      id: data?.guide?.id,
      slugURL: data.guide.slugURL,
      title: data.guide.title || data.guide.slugURL
    };
    const currentGuideKey = normalizeBomReferenceKey(currentGuideEntry.slugURL);
    let replaced = false;
    const merged = catalog.map((entry) => {
      if (normalizeBomReferenceKey(entry?.slugURL) !== currentGuideKey) return entry;
      replaced = true;
      return currentGuideEntry;
    });
    if (!replaced) {
      merged.push(currentGuideEntry);
    }
    return merged;
  }, [data?.guides, data?.guide?.id, data?.guide?.slugURL, data?.guide?.title, isBomGuide]);
  const guideReferenceLookup = useMemo(() => buildGuideReferenceLookup(guideReferenceCatalog), [guideReferenceCatalog]);
  const bomTrackingByStepId = useMemo(() => {
    const map = new Map();
    (Array.isArray(bomTracking?.items) ? bomTracking.items : []).forEach((item) => {
      const stepId = (item?.stepId || '').toString().trim();
      if (!stepId || map.has(stepId)) return;
      map.set(stepId, item);
    });
    return map;
  }, [bomTracking]);
  const bomItemsCount = Array.isArray(data?.guide?.steps) ? data.guide.steps.length : 0;
  const hasExistingBomItems = bomItemsCount > 0;
  const isBomImportPromptMethod = bomImportMethod === BOM_IMPORT_METHOD.EXTERNAL_URL || bomImportMethod === BOM_IMPORT_METHOD.RAW_TEXT;
  const bomImportSelectedMethod = BOM_IMPORT_METHOD_OPTIONS.find((option) => option.key === bomImportMethod) || null;
  const effectiveBomImportStrategy = hasExistingBomItems
    ? bomImportStrategy
    : BOM_IMPORT_STRATEGY.REPLACE_ALL;
  const currentProjectSlugURL = data?.project?.slugURL || '';
  const currentGuideSlugURL = data?.guide?.slugURL || '';
  const currentGuideId = data?.guide?.id || null;
  const currentProgressSteps = data?.progress?.steps;
  const currentProgressStorageKey = getProgressStorageKey(currentGuideId);
  const currentLegacyProgressKey = getLegacyProgressKey(currentGuideId);

  fetchDataRef.current = fetchData;
  saveRef.current = save;
  downloadStepwisedocsExportRef.current = downloadStepwisedocsExport;
  startTrackingRef.current = startTracking;

  useEffect(() => {
    exportIntentHandledRef.current = false;
    setShowExportModal(false);
    setShowMarkdownExportModal(false);
    setShowCsvExportModal(false);
    setShowPublishValidationModal(false);
    setPublishValidationMessage('');
    setPublishValidationSteps([]);
    setExportError('');
    setMarkdownExportError('');
    setCsvExportError('');
  }, [project, guide]);

  useEffect(() => {
    commentIntentHandledRef.current = false;
    setCommentAutoOpen(null);
    setBomTracking(normalizeBomTrackingPayload(null));
    setBomOwnershipFilter(BOM_OWNERSHIP_FILTER.ALL);
    setBomNotesOnlyFilter(false);
    setBomTrackingSavingKey('');
    setBomNoteEditor({ itemKey: '', text: '', error: '' });
  }, [project, guide]);

  useEffect(() => {
    progressIntentHandledRef.current = false;
    bomItemIntentHandledRef.current = '';
    bomGroupIntentHandledRef.current = '';
  }, [project, guide]);

  useEffect(() => {
    if (!isAuthenticated || !isBomGuide || !data?.project?.slugURL || !data?.guide?.slugURL) return;
    let intent = null;
    try {
      const raw = localStorage.getItem(BOM_TRACKING_INTENT_KEY);
      if (!raw) return;
      intent = JSON.parse(raw);
    } catch (e) {
      localStorage.removeItem(BOM_TRACKING_INTENT_KEY);
      return;
    }

    const createdAt = Number(intent?.createdAt);
    const isExpired = !Number.isFinite(createdAt) || Date.now() - createdAt > 30 * 60 * 1000;
    const sameBom = intent?.projectSlug === data.project.slugURL && intent?.guideSlug === data.guide.slugURL;
    if (isExpired || !sameBom) {
      localStorage.removeItem(BOM_TRACKING_INTENT_KEY);
      return;
    }

    const steps = Array.isArray(data?.guide?.steps) ? data.guide.steps : [];
    const item = steps.find((candidate) => candidate?.stepId === intent.stepId);
    if (!item) return;

    localStorage.removeItem(BOM_TRACKING_INTENT_KEY);
    focusCurrentBomItem(item, { updateUrl: false });

    if (intent.action === 'openNote') {
      openBomMyNote(item);
      return;
    }

    saveBomTrackingItem(item, { owned: true }).catch(() => {});
    // Replay must run only when the target BOM page has loaded; helper functions read current page state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isBomGuide, data?.project?.slugURL, data?.guide?.slugURL, data?.guide?.steps]);

  useEffect(() => {
    setShowBomImportModal(false);
    setBomImportMethod('');
    setBomImportPrompt('');
    setBomImportSourceUrl('');
    setBomImportSourceText('');
    setBomImportFile(null);
    setBomImportErrors([]);
    setBomImportStatus('');
    setBomImportPromptGenerated(false);
    setBomImportPromptCopied(false);
  }, [project, guide]);

  useEffect(() => {
    if (!editMode) {
      setShowBomImportModal(false);
    }
  }, [editMode]);

  useEffect(() => {
    return () => {
      resetPopupScrollLock();
    };
  }, []);

  useEffect(() => {
    latestLocationSearchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    try {
      localStorage.setItem(GUIDE_INDEX_PREFERENCE_KEY, showGuideIndex ? '1' : '0');
    } catch (e) {
      // ignore storage write failures
    }
  }, [showGuideIndex]);

  useEffect(() => {
    setGuideNotes([]);
    setOpenNotesStepKey(null);
    setTodoFocusNoteId(null);
    setOpenBomItemStepIds([]);
    setBomSelectedItemKey('');
    setBomSearchTerm('');
    const urlState = getBomUrlState(latestLocationSearchRef.current);
    setBomVersionFilter(urlState.versionFilter);
    setBomGroupBy(urlState.groupBy);
    setBomFocusedGroup(urlState.focusedGroup);
    setBomQuickItem({
      name: '',
      identifier: '',
      quantity: '',
      category: '',
      module: '',
      versions: '',
      urls: [{ type: '', url: '' }],
      description: ''
    });
  }, [project, guide]);

  useEffect(() => {
    exitHistoryMode();
  }, [project, guide]);

  useEffect(() => {
    if (!editMode) {
      setOpenNotesStepKey(null);
      setTodoFocusNoteId(null);
    }
  }, [editMode]);

  useLayoutEffect(() => {
    if (!isBomGuide) {
      bomEditModeScrollAnchorRef.current = null;
      if (bomEditModeScrollRestoreRafRef.current !== null) {
        window.cancelAnimationFrame(bomEditModeScrollRestoreRafRef.current);
        bomEditModeScrollRestoreRafRef.current = null;
      }
      return;
    }

    const pendingAnchor = bomEditModeScrollAnchorRef.current;
    if (!pendingAnchor || pendingAnchor.targetEditMode !== editMode) {
      return;
    }

    let isCancelled = false;

    const restoreScrollPosition = () => {
      if (isCancelled) return;

      const currentAnchor = bomEditModeScrollAnchorRef.current;
      if (!currentAnchor || currentAnchor.targetEditMode !== editMode) {
        bomEditModeScrollRestoreRafRef.current = null;
        return;
      }

      const element = document.getElementById(currentAnchor.anchorId);
      if (!element) {
        if ((currentAnchor.attempts || 0) >= 3) {
          bomEditModeScrollAnchorRef.current = null;
          bomEditModeScrollRestoreRafRef.current = null;
          return;
        }
        currentAnchor.attempts = (currentAnchor.attempts || 0) + 1;
        bomEditModeScrollRestoreRafRef.current = window.requestAnimationFrame(restoreScrollPosition);
        return;
      }

      const nextTop = element.getBoundingClientRect().top;
      const delta = nextTop - currentAnchor.top;
      if (Math.abs(delta) > 1) {
        window.scrollBy(0, delta);
      }

      bomEditModeScrollAnchorRef.current = null;
      bomEditModeScrollRestoreRafRef.current = null;
    };

    restoreScrollPosition();

    return () => {
      isCancelled = true;
    };
  }, [editMode, isBomGuide, data?.guide?.steps]);

  const guideImageLightboxViewportRatio = useMemo(
    () => normalizeGuideImageLightboxViewportRatio(config.guideImageLightboxViewportRatio),
    []
  );

   // Step tracking.
  const currentStep = data?.guide?.steps[selectedStepIndex];
  const lightboxImages = useMemo(() => getLightboxStepImages(currentStep), [currentStep]);
  const selectedLightboxImage = showImageModal
    ? (lightboxImages[selectedFullImageIndex]?.url || '')
    : '';
  const isReferenceLightboxOpen = !!referenceLightboxImage?.url;

  useEffect(() => {
    if (!showImageModal) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowImageModal(false);
        return;
      }

      if (!lightboxImages.length) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedFullImageIndex(prev => Math.min(prev + 1, lightboxImages.length - 1));
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedFullImageIndex(prev => Math.max(prev - 1, 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showImageModal, lightboxImages]);

  useEffect(() => {
    if (!showImageModal) return;

    if (!lightboxImages.length) {
      setShowImageModal(false);
      return;
    }

    if (selectedFullImageIndex >= lightboxImages.length) {
      setSelectedFullImageIndex(lightboxImages.length - 1);
    }
  }, [showImageModal, lightboxImages, selectedFullImageIndex]);

  useEffect(() => {
    if (!showImageModal || !selectedLightboxImage || selectedStepIndex === null) return;
    setActiveImages(prevImages => {
      const newImages = [...prevImages];
      newImages[selectedStepIndex] = selectedLightboxImage;
      return newImages;
    });
  }, [showImageModal, selectedLightboxImage, selectedStepIndex]);

  useEffect(() => {
    if (!showImageModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    imageLightboxRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showImageModal]);

  useEffect(() => {
    if (!isReferenceLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setReferenceLightboxImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReferenceLightboxOpen]);

  useEffect(() => {
    if (!isReferenceLightboxOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    referenceImageLightboxRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isReferenceLightboxOpen]);

  useEffect(() => {
    if (!currentGuideSlugURL || !currentProjectSlugURL) {
      return;
    }
    if (!isAuthenticated) {
      return;
    }
    if (exportIntentHandledRef.current) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const shouldOpenPdf = params.get('export') === '1';
    const shouldOpenMarkdown = params.get('exportMarkdown') === '1';
    const shouldOpenCsv = params.get('exportCsv') === '1';
    const shouldExportStepwisedocs = params.get('exportStepwisedocs') === '1';
    let storedIntent = null;
    try {
      storedIntent = JSON.parse(localStorage.getItem('guideExportIntent') || 'null');
    } catch (e) {
      storedIntent = null;
    }
    const matchesIntent = storedIntent
      && storedIntent.type === 'guide-export'
      && storedIntent.project === currentProjectSlugURL
      && storedIntent.guide === currentGuideSlugURL;
    const intentMode = (storedIntent?.mode || 'pdf').toString();
    const shouldOpenFromIntent = matchesIntent && (intentMode === 'csv' || intentMode === 'pdf' || intentMode === 'markdown' || intentMode === 'stepwisedocs');

    if (matchesIntent && storedIntent.options) {
      if (intentMode === 'csv') {
        setCsvExportOptions((prev) => ({ ...prev, ...storedIntent.options }));
      } else if (intentMode === 'markdown') {
        setMarkdownExportOptions((prev) => ({
          ...prev,
          ...storedIntent.options,
          images: normalizeMarkdownExportImageSize(storedIntent.options?.images),
          includeRelatedBoms: isBomGuide ? false : !!storedIntent.options?.includeRelatedBoms
        }));
      } else if (intentMode === 'pdf') {
        setExportOptions((prev) => ({ ...prev, ...storedIntent.options }));
      }
    }

    if (matchesIntent && intentMode === 'markdown' && !canManageGuide) {
      localStorage.removeItem('guideExportIntent');
    }

    if (shouldOpenPdf || (shouldOpenFromIntent && intentMode === 'pdf')) {
      setShowExportModal(true);
      exportIntentHandledRef.current = true;
    }
    if ((shouldOpenMarkdown || (shouldOpenFromIntent && intentMode === 'markdown')) && canManageGuide) {
      setShowMarkdownExportModal(true);
      exportIntentHandledRef.current = true;
    }
    if (shouldOpenCsv || (shouldOpenFromIntent && intentMode === 'csv')) {
      setShowCsvExportModal(true);
      exportIntentHandledRef.current = true;
    }
    if (shouldExportStepwisedocs || (shouldOpenFromIntent && intentMode === 'stepwisedocs')) {
      exportIntentHandledRef.current = true;
      downloadStepwisedocsExportRef.current && downloadStepwisedocsExportRef.current();
    }
    if (exportIntentHandledRef.current && matchesIntent) {
      localStorage.removeItem('guideExportIntent');
    }
  }, [canManageGuide, currentGuideSlugURL, currentProjectSlugURL, isAuthenticated, isBomGuide, location.search]);

  useEffect(() => {
    if (!currentGuideSlugURL || !currentProjectSlugURL) {
      return;
    }
    if (commentIntentHandledRef.current) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const commentTarget = params.get('comment');
    const commentStep = params.get('commentStep');
    let storedIntent = null;
    try {
      storedIntent = JSON.parse(localStorage.getItem('guideCommentIntent') || 'null');
    } catch (e) {
      storedIntent = null;
    }
    const matchesIntent = storedIntent
      && storedIntent.type === 'guide-comment'
      && storedIntent.project === currentProjectSlugURL
      && storedIntent.guide === currentGuideSlugURL;

    let target = null;
    if (commentTarget === 'guide') {
      target = { scope: 'guide', key: Date.now() };
    } else if (commentTarget === 'step' && commentStep) {
      const stepPosition = Number(commentStep);
      if (Number.isFinite(stepPosition)) {
        target = { scope: 'step', stepPosition, key: Date.now() };
      }
    }

    if (!target && matchesIntent) {
      target = {
        scope: storedIntent.scope || 'guide',
        stepPosition: storedIntent.stepPosition,
        key: Date.now()
      };
    }

    if (target) {
      setCommentAutoOpen(target);
      commentIntentHandledRef.current = true;
      if (matchesIntent) {
        localStorage.removeItem('guideCommentIntent');
      }
    }
  }, [currentGuideSlugURL, currentProjectSlugURL, location.search]);

  useEffect(() => {
    if (!isBomGuide) return;
    const urlState = getBomUrlState(location.search);
    setBomGroupBy((current) => (current === urlState.groupBy ? current : urlState.groupBy));
    setBomFocusedGroup((current) => (current === urlState.focusedGroup ? current : urlState.focusedGroup));
    setBomVersionFilter((current) => (current === urlState.versionFilter ? current : urlState.versionFilter));
  }, [isBomGuide, location.search]);

  useEffect(() => {
    if (!isBomGuide) return;
    if (!commentAutoOpen || commentAutoOpen.scope !== 'step') return;
    const targetPosition = Number(commentAutoOpen.stepPosition);
    if (!Number.isFinite(targetPosition)) return;
    const targetItem = (Array.isArray(data?.guide?.steps) ? data.guide.steps : [])
      .find((item) => Number(item?.position) === targetPosition);
    if (!targetItem) return;
    const key = targetItem.stepId || `item-${targetPosition}`;
    setBomSelectedItemKey(key);
    setOpenBomItemStepIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, [isBomGuide, commentAutoOpen, data?.guide?.steps]);

  useEffect(() => {
    if (!isBomGuide) return;
    const params = new URLSearchParams(location.search);
    const rawTarget = (params.get(BOM_URL_ITEM_PARAM) || '').toString().trim();
    if (!rawTarget) return;
    const intentKey = `${data?.guide?.slugURL || ''}|${rawTarget}`;
    if (bomItemIntentHandledRef.current === intentKey) return;

    const items = Array.isArray(data?.guide?.steps) ? data.guide.steps : [];
    if (!items.length) return;

    const normalizedTarget = rawTarget.toLowerCase();
    const targetItem = items.find((item) => {
      const stepId = (item?.stepId || '').toString().trim();
      const identifier = (item?.identifier || '').toString().trim().toLowerCase();
      return stepId === rawTarget || identifier === normalizedTarget;
    });
    if (!targetItem) return;

    const itemKey = targetItem.stepId || `item-${targetItem.position}`;
    const anchorToken = targetItem.stepId || `item-${targetItem.position}`;
    const anchorId = `bom-item-${anchorToken}`;
    setBomSelectedItemKey(itemKey);
    setOpenBomItemStepIds((prev) => (prev.includes(itemKey) ? prev : [...prev, itemKey]));

    bomItemIntentHandledRef.current = intentKey;
    window.setTimeout(() => {
      const element = document.getElementById(anchorId);
      if (element) {
        try {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          // no-op
        }
      }
    }, 120);
  }, [isBomGuide, data?.guide?.slugURL, data?.guide?.steps, location.search]);

  useEffect(() => {
    if (!isBomGuide) return;
    const params = new URLSearchParams(location.search);
    const view = normalizeBomView(params.get(BOM_URL_VIEW_PARAM));
    const focus = (params.get(BOM_URL_FOCUS_PARAM) || '').toString().trim();
    if (!focus || (view !== 'category' && view !== 'module')) return;

    const intentKey = `${data?.guide?.slugURL || ''}|${view}|${focus}`;
    if (bomGroupIntentHandledRef.current === intentKey) return;

    const items = Array.isArray(data?.guide?.steps) ? data.guide.steps : [];
    if (!items.length) return;

    const normalizedFocus = normalizeBomReferenceKey(focus);
    const hasMatchingGroup = items.some((item) => normalizeBomReferenceKey(item?.[view]) === normalizedFocus);
    if (!hasMatchingGroup) return;

    bomGroupIntentHandledRef.current = intentKey;
    window.setTimeout(() => {
      const element = document.getElementById(`bom-group-${view}-${buildBomGroupAnchorToken(focus)}`);
      if (element) {
        try {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          // no-op
        }
      }
    }, 120);
  }, [isBomGuide, data?.guide?.slugURL, data?.guide?.steps, location.search]);

  useEffect(() => {
    if (!currentGuideSlugURL || !currentProjectSlugURL || !currentGuideId) {
      return;
    }
    if (!isAuthenticated) {
      return;
    }
    if (progressIntentHandledRef.current) {
      return;
    }
    let storedIntent = null;
    try {
      storedIntent = JSON.parse(localStorage.getItem('guideProgressIntent') || 'null');
    } catch (e) {
      storedIntent = null;
    }
    const matchesIntent = storedIntent
      && storedIntent.type === 'guide-progress'
      && storedIntent.project === currentProjectSlugURL
      && storedIntent.guide === currentGuideSlugURL;
    if (matchesIntent) {
      progressIntentHandledRef.current = true;
      localStorage.removeItem('guideProgressIntent');
      let hasStoredProgress = false;
      let hasLegacyProgress = false;
      try {
        hasStoredProgress = !!(currentProgressStorageKey && localStorage.getItem(currentProgressStorageKey));
        hasLegacyProgress = !!(currentLegacyProgressKey && localStorage.getItem(currentLegacyProgressKey));
      } catch (e) {
        hasStoredProgress = false;
        hasLegacyProgress = false;
      }
      if (!guideProgress?.tracking && !Array.isArray(currentProgressSteps) && !hasStoredProgress && !hasLegacyProgress) {
        startTrackingRef.current && startTrackingRef.current();
      }
    }
  }, [
    currentGuideId,
    currentGuideSlugURL,
    currentLegacyProgressKey,
    currentProgressSteps,
    currentProgressStorageKey,
    currentProjectSlugURL,
    guideProgress?.tracking,
    isAuthenticated
  ]);

  useEffect(() => () => {
    if (socketReconnectTimerRef.current) {
      window.clearTimeout(socketReconnectTimerRef.current);
      socketReconnectTimerRef.current = null;
    }
  }, []);

  function clearSocketReconnectTimer() {
    if (socketReconnectTimerRef.current) {
      window.clearTimeout(socketReconnectTimerRef.current);
      socketReconnectTimerRef.current = null;
    }
  }

  function scheduleSocketReconnect() {
    if (!shouldMaintainSocketRef.current) {
      return;
    }
    clearSocketReconnectTimer();
    socketReconnectTimerRef.current = window.setTimeout(() => {
      if (!shouldMaintainSocketRef.current) {
        return;
      }
      setSocketRetryKey((prev) => prev + 1);
    }, 1000);
  }

  useEffect(() => {
    const shouldMaintain = isAuthenticated && isGuideEditable;
    shouldMaintainSocketRef.current = shouldMaintain;

    if (!shouldMaintain) {
      clearSocketReconnectTimer();
      socketRequestIdRef.current += 1;
      socketAccessDeniedRef.current = false;
      setSocketUrl(null);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const requestId = ++socketRequestIdRef.current;

    (async () => {
      try {
        const nextUrl = await buildAuthenticatedWebSocketURL(config.wsURL, {
          project,
          guide,
          signal: controller.signal
        });
        if (!nextUrl || cancelled || controller.signal.aborted || socketRequestIdRef.current !== requestId || !shouldMaintainSocketRef.current) {
          return;
        }
        socketAccessDeniedRef.current = false;
        setSocketUrl(nextUrl);
      } catch (error) {
        if (cancelled || controller.signal.aborted || socketRequestIdRef.current !== requestId) {
          return;
        }
        setSocketUrl(null);
        setConnectedClients([]);
        if (error?.status === 403) {
          if (!socketAccessDeniedRef.current) {
            socketAccessDeniedRef.current = true;
            toast.error('Remote upload access is no longer available for this guide.');
          }
          return;
        }
        if (error?.status === 401) {
          return;
        }
        clearSocketReconnectTimer();
        socketReconnectTimerRef.current = window.setTimeout(() => {
          if (!shouldMaintainSocketRef.current) {
            return;
          }
          setSocketRetryKey((prev) => prev + 1);
        }, 1000);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [project, guide, isAuthenticated, isGuideEditable, socketRetryKey]);

  const { sendJsonMessage, lastJsonMessage, readyState: guideSocketReadyState } = useWebSocket(socketUrl, {
    onMessage: (m) => {
      // console.log('onMessage', m);
    },
    onClose: () => {
      setConnectedClients([]);
      if (!shouldMaintainSocketRef.current) {
        return;
      }
      setSocketUrl(null);
      scheduleSocketReconnect();
    },
    onError: () => {
      if (!shouldMaintainSocketRef.current) {
        return;
      }
      setSocketUrl(null);
      scheduleSocketReconnect();
    },
    shouldReconnect: () => false
  });

  useEffect(() => {
    if (!guideSteps.length) {
      if (uploadingStepId !== null) {
        setUploadingStepId(null);
      }
      return;
    }
    if (selectedUploadingStep?.stepId && selectedUploadingStep.stepId !== uploadingStepId) {
      setUploadingStepId(selectedUploadingStep.stepId);
    }
  }, [guideSteps, selectedUploadingStep?.stepId, uploadingStepId]);

  useEffect(() => {
    if (!isGuideEditable || guideSocketReadyState !== ReadyState.OPEN) {
      return;
    }
    sendJsonMessage(remoteGuideState);
  }, [isGuideEditable, guideSocketReadyState, remoteGuideState, sendJsonMessage]);

  useEffect(() => {
    if (guideSocketReadyState === ReadyState.OPEN) {
      clearSocketReconnectTimer();
      return;
    }
    setConnectedClients([]);
  }, [guideSocketReadyState]);

  // get guides data
  async function fetchData() {
    try {
      const response = await api.get(`/api/v1/projects/${project}/guides/${guide}`);
      if (response?.guide && !Array.isArray(response.guide.steps)) {
        response.guide.steps = [];
      }
      setData(response);
      setBomTracking(normalizeBomTrackingPayload(response?.bomTracking));
      if (response?.guide) {
        lastSavedSnapshotRef.current = getGuideSnapshot(response.guide);
      }
      setGuideNotes(sortNotesList(Array.isArray(response?.notes) ? response.notes : []));
      if (response?.ok) {
        setIsLoaded(true);
        setError(null);
        if (response?.guide?.id) {
          loadProgress(response.progress, response.guide);
        }
        const responseSteps = Array.isArray(response?.guide?.steps) ? response.guide.steps : [];
        setUploadingStepId((prevStepId) => {
          if (prevStepId && responseSteps.some((step) => step?.stepId === prevStepId)) {
            return prevStepId;
          }
          return responseSteps[0]?.stepId || null;
        });
        setActiveImages(responseSteps.map((step) => step?.images?.[0]?.url || null));
      } else if (response?.statusCode === 404) {
        setError('404');
      } else {
        console.log(response);
        setData(null);
      }
    } catch (e) {
      if (e?.statusCode === 404) {
        return setError('404');
      }
      console.log(e);
      setData(null);
    }
  }

  function mergeCurrentBomStepsFromReferenceCatalog(prevData, bomReferences, options = {}) {
    if (!prevData?.guide || (prevData.guide.type || '').toString().toLowerCase() !== 'bom') {
      return prevData;
    }

    const currentBomKey = normalizeBomReferenceKey(prevData.guide.slugURL);
    if (!currentBomKey) return prevData;

    const createdItem = options?.item || null;
    const createdItemBomKey = normalizeBomReferenceKey(options?.bomSlugURL);
    if (createdItem && (!createdItemBomKey || createdItemBomKey !== currentBomKey)) {
      return prevData;
    }

    const wasClean = getGuideSnapshot(prevData.guide) === lastSavedSnapshotRef.current;
    const currentBomReference = toClientBomReferences(bomReferences)
      .find((entry) => normalizeBomReferenceKey(entry?.slugURL) === currentBomKey);
    const serverItems = createdItem
      ? [createdItem]
      : (wasClean && Array.isArray(currentBomReference?.items) ? currentBomReference.items : []);
    if (!serverItems.length) return prevData;

    const currentSteps = Array.isArray(prevData.guide.steps) ? prevData.guide.steps : [];
    const existingIdentifiers = new Set(
      currentSteps
        .map((item) => normalizeBomReferenceKey(item?.identifier))
        .filter(Boolean)
    );
    const missingItems = serverItems.filter((item) => {
      const identifier = normalizeBomReferenceKey(item?.identifier);
      return identifier && !existingIdentifiers.has(identifier);
    });
    if (!missingItems.length) return prevData;

    const combinedSource = [...currentSteps, ...missingItems];
    const appendedSteps = missingItems.map((item, offset) => normalizeBomItem(
      {
        stepId: item.stepId,
        name: item.name,
        title: item.name,
        identifier: item.identifier,
        quantity: item.quantity,
        category: item.category,
        module: item.module,
        description: item.description,
        content: item.description,
        versions: item.versions,
        images: item.imageUrl ? [{ url: item.imageUrl }] : []
      },
      currentSteps.length + offset + 1,
      combinedSource,
      currentSteps.length + offset
    ));

    const nextGuide = {
      ...prevData.guide,
      steps: [...currentSteps, ...appendedSteps]
    };
    if (wasClean) {
      lastSavedSnapshotRef.current = getGuideSnapshot(nextGuide);
    }

    return {
      ...prevData,
      guide: nextGuide
    };
  }

  async function refreshBomReferences(options = {}) {
    try {
      const response = await api.get(`/api/v1/projects/${project}/bom-references`);
      if (!response?.ok || !Array.isArray(response.bomReferences)) {
        return null;
      }
      setData((prevData) => {
        if (!prevData) return prevData;
        const nextData = {
          ...prevData,
          bomReferences: response.bomReferences
        };
        return mergeCurrentBomStepsFromReferenceCatalog(nextData, response.bomReferences, options);
      });
      return response.bomReferences;
    } catch (err) {
      console.log('Error refreshing BOM references', err);
      return null;
    }
  }

  async function createBomItemFromAutocomplete({ bomSlug, item } = {}) {
    if (!isEditable()) {
      return { ok: false, message: 'This guide is not currently editable.' };
    }
    const targetBomSlug = (bomSlug || '').toString().trim();
    if (!targetBomSlug) {
      return { ok: false, message: 'Select a BOM before creating an item.' };
    }
    const payload = {
      name: (item?.name || '').toString(),
      description: (item?.description || '').toString(),
      quantity: (item?.quantity || '').toString(),
      category: (item?.category || '').toString(),
      module: (item?.module || '').toString()
    };
    const wasClean = getGuideSnapshot(data?.guide) === lastSavedSnapshotRef.current;

    try {
      const response = await api.post(
        `/api/v1/projects/${project}/guides/${encodeURIComponent(targetBomSlug)}/bom/items`,
        payload
      );
      if (!response?.ok) {
        return response || { ok: false, message: 'Unable to create BOM item.' };
      }

      if (Array.isArray(response.bomReferences)) {
        setData((prevData) => {
          if (!prevData) return prevData;
          const nextData = {
            ...prevData,
            bomReferences: response.bomReferences
          };
          const currentBomKey = normalizeBomReferenceKey(prevData?.guide?.slugURL);
          const targetBomKey = normalizeBomReferenceKey(targetBomSlug);
          const createdItem = response.item;
          if (currentBomKey && currentBomKey === targetBomKey && createdItem?.identifier) {
            const currentSteps = Array.isArray(prevData.guide?.steps) ? prevData.guide.steps : [];
            const exists = currentSteps.some((step) => normalizeBomReferenceKey(step?.identifier) === normalizeBomReferenceKey(createdItem.identifier));
            if (!exists) {
              nextData.guide = {
                ...prevData.guide,
                steps: [...currentSteps, createdItem]
              };
              if (wasClean) {
                lastSavedSnapshotRef.current = getGuideSnapshot(nextData.guide);
              }
            }
          }
          return nextData;
        });
      }

      return response;
    } catch (err) {
      if (err?.code === 'BOM_ITEM_ALREADY_EXISTS' && err?.existingItem) {
        await refreshBomReferences();
        return {
          ok: true,
          alreadyExists: true,
          referenceItem: err.existingItem,
          item: err.existingItem
        };
      }
      return {
        ok: false,
        message: err?.message || 'Unable to create BOM item.'
      };
    }
  }

  async function ensureGuideSavedBeforeHistory() {
    if (!editMode) {
      return { ok: true };
    }
    const currentSnapshot = getGuideSnapshot(data?.guide);
    if (currentSnapshot && lastSavedSnapshotRef.current === currentSnapshot) {
      return { ok: true, skipped: true };
    }
    const res = await save(true);
    if (!res?.ok) {
      toast.error('Failed to save current changes before opening history.');
    }
    return res;
  }

  async function loadGuideHistory() {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/api/v1/projects/${project}/guides/${guide}/history?limit=100`);
      if (response?.ok) {
        const history = Array.isArray(response.history) ? response.history : [];
        setHistoryEntries(history);
        return history;
      }
      if (response?.error) {
        toast.error(response.message || 'Failed to load guide history.');
      }
      return [];
    } catch (err) {
      console.log('Error loading guide history', err);
      toast.error('Failed to load guide history.');
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadStepHistory(step) {
    if (!step?.stepId) return [];
    setHistoryLoading(true);
    try {
      const response = await api.get(
        `/api/v1/projects/${project}/guides/${guide}/steps/${step.stepId}/history?limit=100&position=${step.position}`
      );
      if (response?.ok) {
        const history = Array.isArray(response.history) ? response.history : [];
        setHistoryEntries(history);
        return history;
      }
      if (response?.error) {
        toast.error(response.message || 'Failed to load step history.');
      }
      return [];
    } catch (err) {
      console.log('Error loading step history', err);
      toast.error('Failed to load step history.');
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }

  async function selectGuideRevision(rev) {
    if (!rev) return;
    setHistorySelectedRev(rev);
    if (historyCache[rev]) {
      setHistorySelectedData(historyCache[rev]);
      return;
    }
    setHistoryLoading(true);
    try {
      const response = await api.get(`/api/v1/projects/${project}/guides/${guide}/history/${rev}`);
      if (response?.ok && response?.guide) {
        setHistorySelectedData(response.guide);
        setHistoryCache(prev => ({ ...prev, [rev]: response.guide }));
      } else if (response?.error) {
        toast.error(response.message || 'Failed to load revision.');
      }
    } catch (err) {
      console.log('Error loading revision', err);
      toast.error('Failed to load revision.');
    } finally {
      setHistoryLoading(false);
    }
  }

  function selectStepRevision(rev) {
    setHistorySelectedRev(rev);
    const entry = historyEntries.find(item => item.rev === rev);
    setHistorySelectedData(entry?.step || null);
  }

  async function enterGuideHistory() {
    if (!canManageGuide) return;
    const saved = await ensureGuideSavedBeforeHistory();
    if (!saved || saved.ok !== true) {
      return;
    }
    if (saved?.slugChanged) {
      return;
    }
    setHistoryCache({});
    const history = await loadGuideHistory();
    setHistoryMode({ type: 'guide' });
    const firstRev = history[0]?.rev;
    setHistorySelectedRev(firstRev || null);
    if (firstRev) {
      await selectGuideRevision(firstRev);
    } else {
      setHistorySelectedData(null);
    }
  }

  async function enterStepHistory(step) {
    if (!canManageGuide || !step?.stepId) return;
    const saved = await ensureGuideSavedBeforeHistory();
    if (!saved || saved.ok !== true) {
      return;
    }
    if (saved?.slugChanged) {
      return;
    }
    setHistoryCache({});
    const history = await loadStepHistory(step);
    setHistoryMode({ type: 'step', stepId: step.stepId, stepPosition: step.position });
    const firstRev = history[0]?.rev;
    setHistorySelectedRev(firstRev || null);
    setHistorySelectedData(history[0]?.step || null);
  }

  function exitHistoryMode() {
    setHistoryMode(null);
    setHistoryEntries([]);
    setHistorySelectedRev(null);
    setHistorySelectedData(null);
    setHistoryCache({});
  }

  async function restoreGuideRevision(rev) {
    if (!rev) return;
    const confirmRestore = window.confirm('Restore this revision? The current guide will be saved as a new revision.');
    if (!confirmRestore) return;
    setHistoryLoading(true);
    try {
      const response = await api.post(`/api/v1/projects/${project}/guides/${guide}/history/${rev}/restore`);
      if (response?.ok) {
        toast.success(`${entityLabel} restored.`);
        if (response.slugChanged) {
          navigate(`${config.prefixPath}/projects/${project}/guides/${response.slugURL}`);
          return;
        }
        await fetchData();
        exitHistoryMode();
      } else if (response?.error) {
        toast.error(response.message || 'Failed to restore revision.');
      }
    } catch (err) {
      console.log('Error restoring revision', err);
      toast.error('Failed to restore revision.');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function restoreStepRevision(stepId, stepPosition, rev, exitAfter) {
    if (!stepId) return;
    setHistoryLoading(true);
    try {
      const response = await api.post(
        `/api/v1/projects/${project}/guides/${guide}/steps/${stepId}/restore`,
        { rev, position: stepPosition }
      );
      if (response?.ok) {
        toast.success('Step restored.');
        await fetchData();
        if (exitAfter) exitHistoryMode();
      } else if (response?.error) {
        toast.error(response.message || 'Failed to restore step.');
      }
    } catch (err) {
      console.log('Error restoring step', err);
      toast.error('Failed to restore step.');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function undoStep(step) {
    if (!step?.stepId) return;
    const confirmUndo = window.confirm('Undo the last saved change for this step?');
    if (!confirmUndo) return;
    await restoreStepRevision(step.stepId, step.position, null, false);
  }

  useEffect(() => {
    if (isLoaded) {
      // Scroll to the anchor once data is loaded
      const hash = window.location.hash.substring(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [isLoaded]);

  async function save(hideToast) {
    setLoading(true);
    const requestData = {
      method: "put",
      url: `/api/v1/projects/${project}/guides/${guide}`,
      data: data
    };

    try {
      const res = await api(requestData);
      setLoading(false);
      console.log(res);
      if (res.ok) {
        setData(prevData => {
          const nextData = { ...prevData, guide: { ...prevData.guide, updateDate: new Date() } };
          lastSavedSnapshotRef.current = getGuideSnapshot(nextData.guide);
          return nextData;
        });
        if (!hideToast) toast.success(`${entityLabel} successfully saved!`);
        if (!res.slugChanged && Number(res.linkedReferencesUpdated) > 0) {
          await fetchData();
        }
        if (res.slugChanged) {
          navigate(`${config.prefixPath}/projects/${project}/guides/${res.slugURL}`);
        }
        return res;
      }
      if (res.error) {
        alert(res.message);
        toast.error(res.message);
      }
      return res;
    } catch (e) {
      setLoading(false);
      if (handlePublishValidationFailure(e)) {
        return e;
      }
      console.log(e);
      return null;
    }
  }

  function closePublishValidationModal() {
    setShowPublishValidationModal(false);
  }

  function handlePublishValidationFailure(payload) {
    if (payload?.code !== GUIDE_PUBLISH_VALIDATION_ERROR_CODE) {
      return false;
    }
    setPublishValidationMessage(
      payload?.message
      || 'This guide cannot be published until every step has both text and at least one image.'
    );
    setPublishValidationSteps(Array.isArray(payload?.incompleteSteps) ? payload.incompleteSteps : []);
    setShowPublishValidationModal(true);
    return true;
  }

  function duplicateGuide () {
     setLoading(true);
     const newGuide = {...data.guide};
     newGuide.title = 'Copy of ' + newGuide.title;
      const requestData = {
        method: "post",
        url: `/api/v1/projects/${project}/guides`,
        data: newGuide
      };

    api(requestData).then(res => {
      setLoading(false);
      console.log(res);
      if (res.ok) {
        // ok!
        toast.success(`${entityLabel} successfully duplicated!`);
        window.open(`${config.prefixPath}/projects/${project}/guides/${res.createdSlugURL}`, '_blank')
        // navigate();
      } else if (res.error) {
        alert(res.message);
        toast.error(res.message)
      }
    }).catch (e => {
      setLoading(false);
      console.log(e);
    });
  }

  function deleteGuide() {
    if (window.confirm(`Do you want to permanently delete this ${entityLabel}? This cannot be undone.`)) {
      const requestData = {
        method: "delete",
        url: `/api/v1/projects/${project}/guides/${guide}`,
        data: data
      };

      api(requestData).then(res => {
        console.log(res);
        if (res.ok) {
          // ok!
          navigate(`${config.prefixPath}/projects/${project}`);
        } else if (res.error) {
          alert(res.message);
          toast.error(res.message)
        }
      }).catch (e => {
        console.log(e);
      });
    }
  }

  async function togglePublish() {
    if (window.confirm(`Do you want to ${data.guide.published ? 'unpublish' : 'publish'} this ${entityLabel}?`)) {
      const nextPublished = !data?.guide?.published;
      const editedData = {
        ...data,
        guide: {
          ...data.guide,
          published: nextPublished
        }
      };
      const requestData = {
        method: "put",
        url: `/api/v1/projects/${project}/guides/${guide}`,
        data: editedData
      };

      try {
        const res = await api(requestData);
        console.log(res);
        if (res.ok) {
          setData(prevData => {
            if (!prevData?.guide) return prevData;
            return {
              ...prevData,
              guide: {
                ...prevData.guide,
                published: nextPublished
              }
            };
          });
          return;
        }
        if (res.error) {
          if (handlePublishValidationFailure(res)) {
            return;
          }
          alert(res.message);
          toast.error(res.message);
        }
      } catch (e) {
        if (handlePublishValidationFailure(e)) {
          return;
        }
        console.log(e);
        if (e?.message) {
          toast.error(e.message);
        }
      }
    }
  }

  function uploadFilesFromList(fileList, step, stepTitle, stepId) {
    if (!isEditable()) {
      return;
    }
    const files = Array.from(fileList || []).filter(file => file && file.type?.startsWith('image/'));
    if (!files.length) {
      toast.error('Only image files can be uploaded.');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('step', step);
    formData.append('stepTitle', stepTitle);
    if (stepId) {
      formData.append('stepId', stepId);
    }
    files.forEach(file => {
      formData.append('files', file, file.name);
    });
    const requestData = {
      method: 'post',
      url: `/api/v1/projects/${project}/guides/${guide}/upload`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    };

    api(requestData).then(res => {
      setLoading(false);
      console.log(res);
      if (!res.ok && res.error) {
        alert(res.message);
        toast.error(res.message);
      }
    }).catch(err => {
      setLoading(false);
      console.log(err);
      toast.error('Error uploading images.');
    });
  }

  function handleChangeFile (e, step, stepTitle, stepId) {
    if (!e || !e.target) {
      return;
    }
    uploadFilesFromList(e.target.files, step, stepTitle, stepId);
    e.target.value = '';
  }

  function showFileDialog(e, stepPosition) {
    const fileElem = document.getElementById(`fileInput-${stepPosition}`);
    fileElem.click();
  }


  function isFileDragEvent(event) {
    if (!event || !event.dataTransfer) {
      return false;
    }
    const types = Array.from(event.dataTransfer.types || []);
    return types.includes('Files');
  }


  function handleDragEnter(e, stepPosition) {
    if (!isEditable() || !isFileDragEvent(e)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setDragOverStepIndex(stepPosition);
  }

  function handleDragOver(e, stepPosition) {
    if (!isEditable() || !isFileDragEvent(e)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (dragOverStepIndex !== stepPosition) {
      setDragOverStepIndex(stepPosition);
    }
  }

  function handleDragLeave(e, stepPosition) {
    if (!isEditable() || !isFileDragEvent(e)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverStepIndex(prev => (prev === stepPosition ? null : prev));
  }

  function handleDrop(e, stepPosition, stepTitle, stepId) {
    if (!isEditable() || !isFileDragEvent(e)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setDragOverStepIndex(prev => (prev === stepPosition ? null : prev));
    const { files } = e.dataTransfer || {};
    if (files && files.length) {
      uploadFilesFromList(files, stepPosition, stepTitle, stepId);
    }
  }

  function selectUploadingStepById(stepId, options = {}) {
    const normalizedStepId = (stepId || '').toString().trim();
    if (!normalizedStepId) {
      setUploadingStepId(null);
      return false;
    }
    const matchedStep = guideSteps.find((step) => step?.stepId === normalizedStepId);
    if (!matchedStep) {
      if (!options.silent) {
        toast.error('That step is no longer available.');
      }
      return false;
    }
    setUploadingStepId(matchedStep.stepId);
    return true;
  }

  function applyGuideStepMutation(prevSteps, nextSteps, nextActiveImages, nextUploadingStepId) {
    const updatedComments = syncCommentsWithSteps(data?.comments || [], prevSteps, nextSteps);
    setData((prevData) => ({
      ...prevData,
      guide: {
        ...prevData.guide,
        steps: nextSteps
      },
      comments: updatedComments,
    }));
    setGuideNotes((prevNotes) => syncNotesWithSteps(prevNotes, prevSteps, nextSteps));
    if (Array.isArray(nextActiveImages)) {
      setActiveImages(nextActiveImages);
    }
    if (nextUploadingStepId !== undefined) {
      setUploadingStepId(nextUploadingStepId);
    }
  }

  function insertStepAtPosition(position) {
    if (!isEditable()) {
      return null;
    }
    const prevSteps = guideSteps;
    if (prevSteps.length >= GUIDE_MAX_STEPS) {
      toast.error(`Guides can have up to ${GUIDE_MAX_STEPS} steps.`);
      return null;
    }
    const safePosition = Math.min(Math.max(Number(position) || 1, 1), prevSteps.length + 1);
    const newStep = {
      stepId: createStepId(),
      position: safePosition,
      title: 'Next step',
      content: '',
      aiGenerated: false,
      images: []
    };
    const nextSteps = [...prevSteps];
    nextSteps.splice(safePosition - 1, 0, newStep);
    const normalizedSteps = nextSteps.map((item, index) => ({ ...item, position: index + 1 }));
    const nextActiveImages = [...activeImages];
    nextActiveImages.splice(safePosition - 1, 0, null);
    applyGuideStepMutation(prevSteps, normalizedSteps, nextActiveImages, newStep.stepId);
    return normalizedSteps.find((step) => step.stepId === newStep.stepId) || newStep;
  }


  const addStep = (position, e) => {
      if (e) e.stopPropagation();
      insertStepAtPosition(position);
  };

  const addImage = async (image, stepPosition) => {
    await setData(prevData => {
        let newSteps = [...prevData.guide.steps];

        // Find index of the step whose `position` matches `stepPosition`
        let stepIndex = newSteps.findIndex(step => step.position === stepPosition);

        // If such step is found, add the image
        if (stepIndex !== -1) {
            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                images: appendGuideImage(newSteps[stepIndex].images, image)
            };
        }
        return {
            ...prevData,
            guide: {
                ...prevData.guide,
                steps: newSteps
            },
        };
    })

    // Update active image of the step to the new image
    await setActiveImages(prevImages => {
      let newImages = [...prevImages];
      newImages[stepPosition - 1] = image.url;
      return newImages;
    })
  };


  function getImageNodeByUrl(url) {
    const imgSelector = `img[src="${url}"]`;
    const imageNode = document.querySelector(imgSelector);
    return imageNode || null; // Return the node or null if not found
  }

  function getImageIndexByURL (step, url) {
    if (url && step && step.images && step.images.findIndex) {
      const imageIndex = step.images.findIndex(img => img.url === url);
      return imageIndex >= 0 ? imageIndex : 0;
    }
    return 0;
  }

  function openImageLightbox(step, stepIndex, imageUrl) {
    const imageList = getLightboxStepImages(step);
    const imageIndex = imageList.findIndex((stepImage) => stepImage.url === imageUrl);
    if (imageIndex < 0) {
      return;
    }
    setSelectedStepIndex(stepIndex);
    setSelectedFullImageIndex(imageIndex);
    setShowImageModal(true);
  }

  function selectStepMediaByIndex(step, stepIndex, imageIndex) {
    const stepImages = Array.isArray(step?.images) ? step.images : [];
    if (!stepImages.length) {
      return;
    }
    const normalizedIndex = ((Number(imageIndex) || 0) % stepImages.length + stepImages.length) % stepImages.length;
    const nextImage = stepImages[normalizedIndex];
    if (!nextImage?.url) {
      return;
    }
    handleImageClick(nextImage.url, stepIndex);
  }

  function cycleStepMedia(step, stepIndex, direction) {
    const stepImages = Array.isArray(step?.images) ? step.images : [];
    if (stepImages.length <= 1) {
      return;
    }
    const currentIndex = getImageIndexByURL(step, activeImages[stepIndex]);
    selectStepMediaByIndex(step, stepIndex, currentIndex + direction);
  }

  // Ensure external editor popups render above sticky navbars
  function raiseEditorOverlays() {
    try {
      const selectors = [
        '.mjs2-container',
        '.mjs2-popup',
        '.markerjs2',
        '.markerjs2-popup',
        '.markerjs2-container',
        '.cropro',
        '.cropro-popup',
        '.cropro-container',
        '.cp-container',
        '.cp-popup',
        '[class^="mjs2-"]',
        '[class*=" mjs2-"]',
        '[class^="__markerjs2__"]',
        '[class*=" __markerjs2__"]',
        '[class^="__cropro__"]',
        '[class*=" __cropro__"]'
      ];
      const nodes = document.querySelectorAll(selectors.join(','));
      nodes.forEach((n) => {
        try { n.style.zIndex = '2000'; } catch (e) { /* ignore */ }
      });
    } catch (e) { /* ignore */ }
  }

  function fixCroproPopupViewport(cropArea) {
    if (!cropArea?.coverDiv) return;
    cropArea.coverDiv.style.position = 'fixed';
    cropArea.coverDiv.style.top = '0px';
    cropArea.coverDiv.style.left = '0px';
    cropArea.coverDiv.style.width = '100vw';
    cropArea.coverDiv.style.height = `${window.innerHeight}px`;
  }

  function applyNaturalSizeGuideImageRenderSettings(editor) {
    if (!editor) return;
    // Render from the stored image pixels instead of the current viewport box.
    editor.renderAtNaturalSize = true;
    editor.renderImageType = 'image/png';
  }

  const ReshapeImage = (step, stepTitle, imageURL, stepId) => {
    const myImg = getImageNodeByUrl(imageURL);
    
    const cropArea = new cropro.CropArea(myImg);
    patchPopupScrollLock(cropArea);
    applyNaturalSizeGuideImageRenderSettings(cropArea);
    cropArea.displayMode = 'popup';
    cropArea.popupMargin = 100;
    cropArea.isGridVisible = false;
    cropArea.addRenderEventListener(dataUrl => {
      if (myImg) {
        myImg.src = dataUrl;
        saveNewImage (dataUrl, imageURL, step, stepTitle, stepId);
      }
    });
    cropArea.show();
    fixCroproPopupViewport(cropArea);
    // Bring popup above sticky navbars
    setTimeout(raiseEditorOverlays, 0);
  }

  const deleteImage = (stepIndex, imageURL) => {
    setData(prevData => {
      let newSteps = [...prevData.guide.steps];
      const imageIndex = newSteps[stepIndex].images.findIndex(i => i.url === imageURL);
      if (imageIndex > -1) {
       newSteps[stepIndex].images.splice(imageIndex, 1);
      }

      setActiveImages(prevImages => {
        let newImages = [...prevImages];
        newImages[stepIndex] = newSteps[stepIndex].images[imageIndex-1]?.url || newSteps[stepIndex].images[imageIndex]?.url || null; // reset active image to the first remaining image or null
        return newImages;
      });
      
      return {
        ...prevData,
        guide: {
          ...prevData.guide,
          steps: newSteps
        },
      };
    });
  }

  function removeStepByStepId(stepId) {
      if (!isEditable()) {
        return false;
      }
      const normalizedStepId = (stepId || '').toString().trim();
      const prevSteps = guideSteps;
      const removeIndex = prevSteps.findIndex((step) => step?.stepId === normalizedStepId);
      if (removeIndex < 0) {
        return false;
      }

      const removedStep = prevSteps[removeIndex];
      const nextSteps = prevSteps
        .filter((_, index) => index !== removeIndex)
        .map((item, index) => ({ ...item, position: index + 1 }));

      if (removedStep) {
        const removedKey = getStepKey(removedStep);
        if (removedKey && openNotesStepKey === removedKey) {
          setOpenNotesStepKey(null);
          setTodoFocusNoteId(null);
        }
      }

      const nextActiveImages = [...activeImages];
      nextActiveImages.splice(removeIndex, 1);

      const nextSelectedStep =
        nextSteps[removeIndex]
        || nextSteps[removeIndex - 1]
        || null;

      applyGuideStepMutation(
        prevSteps,
        nextSteps,
        nextActiveImages,
        nextSelectedStep?.stepId || null
      );

      return true;
  }

  const removeStep = (position, e) => {
      if (e) e.stopPropagation();
      const targetStep = guideSteps.find((step) => Number(step?.position) === Number(position));
      if (!targetStep?.stepId) {
        return false;
      }
      removeStepByStepId(targetStep.stepId);
      return false;
  };

  function moveStep(path, newValue, originalPosition) {
    moveFromToPosition(originalPosition, newValue);
  }

  const moveFromToPosition = (from, to) => {
      if (!isEditable()) {
        return;
      }
      const prevSteps = guideSteps;
      if (!prevSteps.length) {
        return;
      }
      const boundedFrom = Math.min(Math.max(Number(from) || 1, 1), prevSteps.length);
      const boundedTo = Math.min(Math.max(Number(to) || 1, 1), prevSteps.length);
      if (boundedFrom === boundedTo) {
        return;
      }

      const newSteps = [...prevSteps];
      const movedStep = newSteps.splice(boundedFrom - 1, 1)[0];
      newSteps.splice(boundedTo - 1, 0, movedStep);
      const normalizedSteps = newSteps.map((item, index) => ({ ...item, position: index + 1 }));

      const nextActiveImages = [...activeImages];
      const movedImage = nextActiveImages.splice(boundedFrom - 1, 1)[0];
      nextActiveImages.splice(boundedTo - 1, 0, movedImage);

      applyGuideStepMutation(prevSteps, normalizedSteps, nextActiveImages, uploadingStepId);
  };


  useEffect(() => {
    fetchDataRef.current && fetchDataRef.current();
  }, [project, guide]);

  useEffect(() => {
    if (!isAuthenticated) {
      setGuideProgress({ tracking: false, steps: [] });
      progressSyncRef.current.pending = null;
      progressSyncRef.current.inFlight = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setGuideProgress({ tracking: false, steps: [] });
  }, [project, guide]);

  useEffect(() => {
    if (!data || !autoSave || !editMode) return;
    const snapshot = getGuideSnapshot(data?.guide);
    if (snapshot && snapshot !== lastSavedSnapshotRef.current) {
      saveRef.current && saveRef.current(true);
    }
  }, [data, autoSave, editMode]);

  useEffect(() => {
    activeImagesRef.current = activeImages;
  }, [activeImages]);

  useEffect(() => {
    if (activeContentIndex !== null) {
      document.getElementById('content-' + activeContentIndex).focus();
    }
  }, [activeContentIndex]);

  useEffect(() => {
    if (!showMarkdownHelp && activeContentIndex !== null) {
      const textarea = document.getElementById('content-' + activeContentIndex);
      if (textarea) textarea.focus();
    }
  }, [showMarkdownHelp, activeContentIndex]);

  useEffect(() => {
    if (!showMarkdownHelp) {
      ignoreContentBlurRef.current = false;
    }
  }, [showMarkdownHelp]);

  useEffect(() => {
    if (activeIndex !== null) {
      document.getElementById('titleinput-' + activeIndex).focus();
    }
  }, [activeIndex]);

  const replaceImage = (image, stepPosition, oldImage, currentImageUrl) => {
    setData(prevData => {
      let newSteps = [...prevData.guide.steps];

      // Find index of the step whose `position` matches `stepPosition`
      let stepIndex = newSteps.findIndex(step => step.position === stepPosition);

      if (stepIndex !== -1) {
        newSteps[stepIndex] = {
          ...newSteps[stepIndex],
          images: replaceGuideImage(newSteps[stepIndex].images, image, {
            oldPath: oldImage,
            currentUrl: currentImageUrl
          })
        };
      }

      return {
          ...prevData,
          guide: {
              ...prevData.guide,
              steps: newSteps
          },
      };
    })

    // Update active image
    setActiveImages(prevImages => {
      let newImages = [...prevImages];
      newImages[stepPosition - 1] = image.url;
      return newImages;
    })
  };

  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      const cutoff = Date.now() - 10000;
      setConnectedClients((currentClients) => (
        currentClients.some((item) => !item?.lastSeen || item.lastSeen.getTime() < cutoff)
          ? currentClients.filter((item) => item?.lastSeen && item.lastSeen.getTime() >= cutoff)
          : currentClients
      ));
    }, 1000);

    return () => clearInterval(intervalIdRef.current); 
  }, []);

  function seenClient(id) {
    if (!id) return;
    const existIndex = connectedClients.findIndex(c => c.clientId === id)
    if (existIndex === -1){
      // add it
      const newConnectedClients = [...connectedClients, {clientId: id, lastSeen: new Date()}];
      setConnectedClients(newConnectedClients);
    } else {
      // update it
      const newConnectedClients = [...connectedClients]
      newConnectedClients[existIndex].lastSeen = new Date();
      setConnectedClients(newConnectedClients);
    }
  }

  function removeClient(id) {
    if (!id) return;
    const existIndex = connectedClients.findIndex(c => c.clientId === id)
    if (existIndex > -1) {
      const newConnectedClients = [...connectedClients]
      newConnectedClients.splice(existIndex, 1); 
      setConnectedClients(newConnectedClients);
    }
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    //console.log('lastJsonMessage', lastJsonMessage);
    if (lastJsonMessage !== null) {
      if (processedSocketMessageRef.current === lastJsonMessage) {
        return;
      }
      processedSocketMessageRef.current = lastJsonMessage;
      if (lastJsonMessage.cmd) {
        switch(lastJsonMessage.cmd) {
          case 'imhere':
            seenClient(lastJsonMessage.clientId);
            if (guideSocketReadyState === ReadyState.OPEN) {
              sendJsonMessage(remoteGuideState);
            }
            toast.success('New device has been connected to upload images!');
          break;
          case 'ping':
            seenClient(lastJsonMessage.clientId);
          break;
          case 'bye':
            removeClient(lastJsonMessage.clientId);
          break;
          case 'remoteRequestGuideState':
            if (guideSocketReadyState === ReadyState.OPEN) {
              sendJsonMessage(remoteGuideState);
            }
          break;
          case 'bomCatalogChanged':
            if (
              !lastJsonMessage.project
              || normalizeBomReferenceKey(lastJsonMessage.project) === normalizeBomReferenceKey(data?.project?.slugURL)
            ) {
              refreshBomReferences({
                bomSlugURL: lastJsonMessage.bomSlugURL,
                item: lastJsonMessage.item
              });
            }
          break;
          case 'remoteSelectStep': {
            if (!isGuideEditable) {
              if (guideSocketReadyState === ReadyState.OPEN) {
                sendJsonMessage({ cmd: 'remoteActionError', message: 'The main guide is not currently in edit mode.' });
              }
              break;
            }
            const requestedStepId = (lastJsonMessage.stepId || '').toString().trim();
            if (!requestedStepId || !selectUploadingStepById(requestedStepId, { silent: true })) {
              if (guideSocketReadyState === ReadyState.OPEN) {
                sendJsonMessage({ cmd: 'remoteActionError', message: 'That step is no longer available. Refresh the uploader and try again.' });
              }
            }
          break;
          }
          case 'remoteAddStepBelow': {
            if (!isGuideEditable) {
              if (guideSocketReadyState === ReadyState.OPEN) {
                sendJsonMessage({ cmd: 'remoteActionError', message: 'The main guide is not currently in edit mode.' });
              }
              break;
            }
            if (guideSteps.length >= GUIDE_MAX_STEPS) {
              if (guideSocketReadyState === ReadyState.OPEN) {
                sendJsonMessage({ cmd: 'remoteActionError', message: `Guides can have up to ${GUIDE_MAX_STEPS} steps.` });
              }
              break;
            }
            const requestedStepId = (lastJsonMessage.stepId || '').toString().trim();
            const matchedStep = requestedStepId
              ? guideSteps.find((step) => step?.stepId === requestedStepId)
              : selectedUploadingStep;
            const insertPosition = matchedStep ? Number(matchedStep.position) + 1 : 1;
            const createdStep = insertStepAtPosition(insertPosition);
            if (!createdStep && guideSocketReadyState === ReadyState.OPEN) {
              sendJsonMessage({ cmd: 'remoteActionError', message: 'Unable to add a new step right now.' });
            }
          break;
          }
          case 'remoteDeleteStep': {
            if (!isGuideEditable) {
              if (guideSocketReadyState === ReadyState.OPEN) {
                sendJsonMessage({ cmd: 'remoteActionError', message: 'The main guide is not currently in edit mode.' });
              }
              break;
            }
            const requestedStepId = (lastJsonMessage.stepId || '').toString().trim();
            if (!requestedStepId || !removeStepByStepId(requestedStepId)) {
              if (guideSocketReadyState === ReadyState.OPEN) {
                sendJsonMessage({ cmd: 'remoteActionError', message: 'That step is no longer available. Refresh the uploader and try again.' });
              }
            }
          break;
          }
          case 'newImg': {
            const requestedStepId = (lastJsonMessage.stepId || '').toString().trim();
            const fallbackStepPosition = parseInt(lastJsonMessage.step, 10);
            const messageImages = Array.isArray(lastJsonMessage.images) ? lastJsonMessage.images : [];
            const isEditMessage = lastJsonMessage.edit === true || lastJsonMessage.edit === 'true' || lastJsonMessage.edit === 1 || lastJsonMessage.edit === '1';
            const matchedStep = requestedStepId
              ? guideSteps.find((step) => step?.stepId === requestedStepId)
              : guideSteps.find((step) => Number(step?.position) === Number(fallbackStepPosition));
            const stepPosition = Number(matchedStep?.position) || fallbackStepPosition;
            if (!Number.isFinite(stepPosition) || stepPosition < 1) {
              break;
            }
            if (isEditMessage) {
              const nextImageUrl = messageImages[0];
              if (nextImageUrl) {
                replaceImage(
                  { url: nextImageUrl },
                  stepPosition,
                  lastJsonMessage.oldPath,
                  activeImagesRef.current[stepPosition - 1]
                );
                toast.success('Image successfully edited!');
              }
              break;
            }
            messageImages.forEach((url) => {
              addImage({url}, stepPosition);
              toast.success(`New image added to step: ${matchedStep?.title || lastJsonMessage.stepTitle}!`);
            });
          break;
          }
          default:
          break;
        }
      }
    }
  }, [lastJsonMessage, guideSocketReadyState, guideSteps, isGuideEditable, remoteGuideState, selectedUploadingStep, sendJsonMessage]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!canManageGuide && editMode) {
      setEditMode(false);
    }
  }, [canManageGuide, editMode]);

  useEffect(() => {
    if (!guideStepAiState.stepId) {
      return;
    }
    if (!isGuideEditable || !guideStepAiStep) {
      closeGuideStepAiModal();
    }
  }, [guideStepAiState.stepId, guideStepAiStep, isGuideEditable, closeGuideStepAiModal]);

  const handleStepTitleClick = (index, title) => {
    if (!isEditable()) return;
        setActiveIndex(index);
        setInputValue(title);
  };

  const handleContentClick = (index, content, position) => {
    if (!isEditable()) return;
        setActiveContentIndex(index);
        setContentValue(content);
        let h = document.getElementById('renderedcontent-' + position).offsetHeight + 6;
        if (h < 399) h = 399;
        setEditorHeight(h);
  };

  // Function to handle editing content
  const handleInputChange = (event) => setInputValue(event.target.value);
  
  // Function to handle finishing editing title
  const handleBlur = (index) => {
        setData((prevData) => {
            const prevGuide = prevData?.guide || {};
            const prevSteps = Array.isArray(prevGuide.steps) ? prevGuide.steps : [];
            if (!prevSteps[index]) {
              return prevData;
            }
            const nextSteps = [...prevSteps];
            nextSteps[index] = {
              ...nextSteps[index],
              title: inputValue
            };
            return {
              ...prevData,
              guide: {
                ...prevGuide,
                steps: nextSteps
              }
            };
        });
        const currentStepId = guideSteps[index]?.stepId;
        if (currentStepId) {
          setUploadingStepId(currentStepId);
        }

        // Clear the active index
        setActiveIndex(null);
  };

  // Function to handle finishing editing content
  const handleContentBlur = (index) => {
        if (ignoreContentBlurRef.current || showMarkdownHelp) {
            ignoreContentBlurRef.current = false;
            return;
        }
        const nextContent = (contentValue || '').toString();
        setData((prevData) => {
            const prevGuide = prevData?.guide || {};
            const prevSteps = Array.isArray(prevGuide.steps) ? prevGuide.steps : [];
            if (!prevSteps[index]) {
              return prevData;
            }
            const previousContent = (prevSteps[index]?.content || '').toString();
            const contentChanged = previousContent !== nextContent;
            const nextSteps = [...prevSteps];
            nextSteps[index] = {
              ...nextSteps[index],
              content: nextContent,
              ...(contentChanged ? { aiGenerated: false } : {})
            };
            return {
              ...prevData,
              guide: {
                ...prevGuide,
                steps: nextSteps
              }
            };
        });
        const currentStepId = guideSteps[index]?.stepId;
        if (currentStepId) {
          setUploadingStepId(currentStepId);
        }

        // Clear the active index
        setActiveContentIndex(null);
  };

  const handleKeyPress = (event, index) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleBlur(index);
    }
  }

  function buildCurrentGuideDraft() {
    const currentGuide = data?.guide || {};
    const nextSteps = guideSteps.map((step, index) => {
      const nextStep = { ...step };
      if (activeIndex === index) {
        nextStep.title = inputValue;
      }
      if (activeContentIndex === index) {
        nextStep.content = contentValue;
      }
      return nextStep;
    });

    return {
      ...currentGuide,
      steps: nextSteps
    };
  }

  function openGuideStepAiModal(step, index) {
    if (!isEditable() || !step?.stepId) {
      return;
    }

    const draftGuide = buildCurrentGuideDraft();
    const draftSteps = Array.isArray(draftGuide?.steps) ? draftGuide.steps : [];
    const draftStep = draftSteps[index] || step;

    setUploadingStepId(step.stepId);
    openAiSession({
      stepId: step.stepId,
      title: (draftStep?.title || '').toString(),
      promptHint: (draftStep?.content || '').toString()
    });
  }

  function handleGuideStepAiTitleChange(nextValue) {
    setGuideStepAiState((prev) => ({
      ...prev,
      title: (nextValue || '').toString()
    }));
  }

  function handleGuideStepAiPromptHintChange(nextValue) {
    setGuideStepAiState((prev) => ({
      ...prev,
      promptHint: (nextValue || '').toString()
    }));
  }

  function handleGuideStepAiPreviousStepsContextCountChange(nextValue) {
    setGuideStepAiState((prev) => ({
      ...prev,
      previousStepsContextCount: normalizeGuideStepAiContextStepCount(
        nextValue,
        GUIDE_STEP_AI_PREVIOUS_CONTEXT_DEFAULT
      )
    }));
  }

  function handleGuideStepAiFollowingStepsContextCountChange(nextValue) {
    setGuideStepAiState((prev) => ({
      ...prev,
      followingStepsContextCount: normalizeGuideStepAiContextStepCount(
        nextValue,
        GUIDE_STEP_AI_FOLLOWING_CONTEXT_DEFAULT
      )
    }));
  }

  function toggleGuideStepAiRemoteUploader() {
    if (guideStepAiStep?.stepId) {
      setUploadingStepId(guideStepAiStep.stepId);
    }
    setGuideStepAiState((prev) => ({
      ...prev,
      showRemoteUploader: !prev.showRemoteUploader
    }));
  }

  function uploadGuideStepAiImages(fileList) {
    if (!guideStepAiStep) {
      return;
    }
    setUploadingStepId(guideStepAiStep.stepId);
    uploadFilesFromList(
      fileList,
      guideStepAiStep.position,
      guideStepAiState.title || guideStepAiStep.title,
      guideStepAiStep.stepId
    );
  }

  async function generateGuideStepAiText() {
    if (!guideStepAiStep || guideStepAiStepIndex < 0) {
      return;
    }

    const draftGuide = buildCurrentGuideDraft();
    const draftSteps = Array.isArray(draftGuide?.steps) ? [...draftGuide.steps] : [];
    if (!draftSteps[guideStepAiStepIndex]) {
      return;
    }

    draftSteps[guideStepAiStepIndex] = {
      ...draftSteps[guideStepAiStepIndex],
      title: guideStepAiState.title
    };

    await requestGuideStepAiText(`/api/v1/projects/${project}/guides/${guide}/ai/step-text`, {
      ...draftGuide,
      steps: draftSteps
    });
  }

  function acceptGuideStepAiText() {
    if (!guideStepAiStep || guideStepAiStepIndex < 0 || !guideStepAiState.generatedText
      || guideStepAiState.connectionLoading || !guideStepAiState.available) {
      return;
    }

    const nextTitle = guideStepAiState.title;
    const nextContent = guideStepAiState.generatedText;

    setData((prevData) => {
      const prevGuide = prevData?.guide || {};
      const prevSteps = Array.isArray(prevGuide.steps) ? prevGuide.steps : [];
      if (!prevSteps[guideStepAiStepIndex]) {
        return prevData;
      }
      const nextSteps = [...prevSteps];
      nextSteps[guideStepAiStepIndex] = {
        ...nextSteps[guideStepAiStepIndex],
        title: nextTitle,
        content: nextContent,
        aiGenerated: true
      };
      return {
        ...prevData,
        guide: {
          ...prevGuide,
          steps: nextSteps
        }
      };
    });

    if (activeContentIndex === guideStepAiStepIndex) {
      setContentValue(nextContent);
    }
    if (activeIndex === guideStepAiStepIndex) {
      setInputValue(nextTitle);
    }
    if (guideStepAiStep?.stepId) {
      setUploadingStepId(guideStepAiStep.stepId);
    }

    closeGuideStepAiModal();
    toast.success('Generated step text applied.');
  }

  const handleImageClick = (imageUrl, index) => {
    setActiveImages(prevImages => {
      let newImages = [...prevImages];
      newImages[index] = imageUrl;
      return newImages;
    });
  };

  function uploadingHere(step, e) {
    if (e) return;
    if (!step?.stepId) {
      setUploadingStepId(null);
      return;
    }
    setUploadingStepId(step.stepId);
  }

  function saveNewImage (newImage, oldPath, step, stepTitle, stepId) {
    const fileName = normalizeManagedUploadFilename(oldPath);
    if (!fileName) {
      toast.error('Unable to determine which image to replace.');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('step', step);
    formData.append('stepTitle', stepTitle);
    if (stepId) {
      formData.append('stepId', stepId);
    }
    formData.append('edit', true);
    formData.append('oldPath', fileName);
    if (newImage) {
      fetch(newImage).then(response => response.blob()).then(blob => {
        formData.append("files", blob, fileName);

        const requestData = {
          method: "post",
          url: `/api/v1/projects/${project}/guides/${guide}/upload`,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" }
        };

        api(requestData).then(res => {
          setLoading(false);
          console.log(res);
          if (res.ok) {
            // ok!
          } else if (res.error) {
            alert(res.message);
          }
        }).catch (e => {
          setLoading(false);
          console.log(e);
        });
      }).catch((e) => {
        setLoading(false);
        console.log(e);
        toast.error('Unable to prepare the edited image.');
      });
    } else {
      setLoading(false);
    }
  }

  const handleImageCloseModal = () => {
    setShowImageModal(false);
  };

  const openReferenceImageLightbox = (imageUrl, altText) => {
    const normalizedUrl = (imageUrl || '').toString().trim();
    if (!normalizedUrl) return;
    setReferenceLightboxImage({
      url: normalizedUrl,
      alt: (altText || 'BOM item image').toString().trim() || 'BOM item image'
    });
  };

  const closeReferenceImageLightbox = () => {
    setReferenceLightboxImage(null);
  };

  function updateNestedProperty(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const deepRef = keys.reduce((o, key) => (o[key] = o[key] || {}), obj);
    deepRef[lastKey] = value;
    return { ...obj };
  }

  function updateState(path, newValue) {
    // console.log(`updateNestedProperty======`, updateNestedProperty({ ...data }, path, newValue) )
    setData(prevData => updateNestedProperty({ ...prevData }, path, newValue));
  }

  function getStepKey(step) {
    if (!step) return null;
    return step.stepId || `pos-${step.position}`;
  }

  function getNotesForStep(step) {
    if (!step) return [];
    const stepId = step.stepId;
    const position = step.position;
    return (Array.isArray(guideNotes) ? guideNotes : []).filter(note => {
      if (!note) return false;
      if (stepId && note.stepId) return note.stepId === stepId;
      return Number(note.stepPosition) === Number(position);
    });
  }

  function syncCommentsWithSteps(comments, prevSteps, nextSteps) {
    if (!Array.isArray(comments) || !Array.isArray(prevSteps) || !Array.isArray(nextSteps)) {
      return comments;
    }
    const oldPosToId = new Map();
    prevSteps.forEach(step => {
      if (step?.stepId) {
        oldPosToId.set(Number(step.position), step.stepId);
      }
    });
    const newIdToPos = new Map();
    nextSteps.forEach(step => {
      if (step?.stepId) {
        newIdToPos.set(step.stepId, Number(step.position));
      }
    });

    return comments.reduce((acc, comment) => {
      if (comment?.scope !== 'step') {
        acc.push(comment);
        return acc;
      }
      const inferredStepId = comment.stepId || oldPosToId.get(Number(comment.stepPosition));
      if (!inferredStepId) {
        const stillExists = nextSteps.some(step => Number(step.position) === Number(comment.stepPosition));
        if (stillExists) {
          acc.push(comment);
        }
        return acc;
      }
      const newPosition = newIdToPos.get(inferredStepId);
      if (!Number.isFinite(newPosition)) {
        return acc;
      }
      acc.push({ ...comment, stepId: inferredStepId, stepPosition: newPosition });
      return acc;
    }, []);
  }

  function syncNotesWithSteps(notes, prevSteps, nextSteps) {
    if (!Array.isArray(notes) || !Array.isArray(prevSteps) || !Array.isArray(nextSteps)) {
      return notes;
    }
    const oldPosToId = new Map();
    prevSteps.forEach(step => {
      if (step?.stepId) {
        oldPosToId.set(Number(step.position), step.stepId);
      }
    });
    const newIdToPos = new Map();
    nextSteps.forEach(step => {
      if (step?.stepId) {
        newIdToPos.set(step.stepId, Number(step.position));
      }
    });

    return notes.reduce((acc, note) => {
      const inferredStepId = note.stepId || oldPosToId.get(Number(note.stepPosition));
      if (!inferredStepId) {
        const stillExists = nextSteps.some(step => Number(step.position) === Number(note.stepPosition));
        if (stillExists) {
          acc.push(note);
        }
        return acc;
      }
      const newPosition = newIdToPos.get(inferredStepId);
      if (!Number.isFinite(newPosition)) {
        return acc;
      }
      acc.push({ ...note, stepId: inferredStepId, stepPosition: newPosition });
      return acc;
    }, []);
  }

  function normalizeBomIdentifierClient(value) {
    const raw = (value || '').toString().trim().toLowerCase();
    if (!raw) return '';
    let normalized = raw
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[._-]+/, '')
      .replace(/[._-]+$/, '');
    if (!normalized) return '';
    if (!/^[a-z0-9]/.test(normalized)) {
      normalized = `item_${normalized}`;
    }
    return normalized.slice(0, 80);
  }

  function buildBomIdentifierSuggestion(name) {
    const normalized = normalizeBomIdentifierClient(name);
    return normalized || 'item';
  }

  function ensureUniqueBomIdentifierClient(identifier, items, currentIndex = null) {
    const source = normalizeBomIdentifierClient(identifier) || 'item';
    const used = new Set(
      (Array.isArray(items) ? items : [])
        .filter((item, idx) => currentIndex === null || idx !== currentIndex)
        .map((item) => ((item?.identifier || '').toString().trim().toLowerCase()))
        .filter(Boolean)
    );
    let candidate = source;
    let suffix = 2;
    while (used.has(candidate.toLowerCase()) || !BOM_IDENTIFIER_PATTERN.test(candidate)) {
      const suffixToken = `_${suffix}`;
      const limit = Math.max(1, 80 - suffixToken.length);
      candidate = `${source.slice(0, limit)}${suffixToken}`;
      suffix += 1;
    }
    return candidate;
  }

  function parseCommaValues(input) {
    if (!input) return [];
    const seen = new Set();
    const values = [];
    input.toString().split(',').forEach((part) => {
      const value = part.trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      values.push(value);
    });
    return values;
  }

  function sanitizeBomClientUrl(rawValue) {
    const raw = (rawValue || '').toString().trim();
    if (!raw) return '';
    let candidate = raw;
    if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(candidate) && /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}([/:?#]|$)/.test(candidate)) {
      candidate = `https://${candidate}`;
    }
    try {
      const parsed = new URL(candidate);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      return parsed.toString();
    } catch (e) {
      return '';
    }
  }

  function sanitizeBomClientImageUrl(rawValue) {
    const raw = (rawValue || '').toString().trim();
    if (!raw) return '';
    if (raw.startsWith('/')) return raw;
    if (/^uploads\//i.test(raw)) return `/${raw.replace(/^\/+/, '')}`;
    if (raw.startsWith('//')) {
      return sanitizeBomClientUrl(`https:${raw}`);
    }
    return sanitizeBomClientUrl(raw);
  }

  function sanitizeBomUrlTypeClient(rawValue) {
    const text = (rawValue || '').toString().trim().replace(/\s+/g, ' ').replace(/[<>]/g, '');
    if (!text) return '';
    return text.slice(0, 80);
  }

  function sanitizeBomManualUrlEntriesClient(rawUrls, options = {}) {
    const { includeEmpty = false, dropInvalid = false } = options;
    const source = Array.isArray(rawUrls)
      ? rawUrls
      : (rawUrls === undefined || rawUrls === null ? [] : [rawUrls]);
    const entries = [];
    const seen = new Set();
    source.forEach((entryValue) => {
      if (entries.length >= 20) return;
      const entry = entryValue && typeof entryValue === 'object' ? entryValue : null;
      const type = sanitizeBomUrlTypeClient(entry?.type || entry?.label || '');
      const rawUrl = entry ? entry.url : entryValue;
      const urlText = (rawUrl || '').toString().trim().slice(0, 2048);
      if (!urlText) {
        if (includeEmpty) {
          entries.push(type ? { type, url: '' } : { url: '' });
        }
        return;
      }
      const normalizedUrl = sanitizeBomClientUrl(urlText);
      if (dropInvalid && !normalizedUrl) return;
      const safeUrl = dropInvalid ? normalizedUrl : urlText;
      const key = `${type.toLowerCase()}|${safeUrl.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push(type ? { type, url: safeUrl } : { url: safeUrl });
    });
    return entries;
  }

  function stringifyCommaValues(values) {
    if (!Array.isArray(values)) return '';
    return values.filter(Boolean).join(', ');
  }

  function getBomItems() {
    return Array.isArray(data?.guide?.steps) ? data.guide.steps : [];
  }

  function getBomItemKey(item) {
    if (!item) return '';
    return item?.stepId || `item-${item?.position}`;
  }

  function selectBomItem(item) {
    const itemKey = getBomItemKey(item);
    if (!itemKey) return;
    setBomSelectedItemKey(itemKey);
    setOpenBomItemStepIds((prev) => (prev.includes(itemKey) ? prev : [...prev, itemKey]));
    setCommentAutoOpen(null);
  }

  function getBomTrackingForItem(item) {
    const stepId = (item?.stepId || '').toString().trim();
    if (!stepId) return null;
    return bomTrackingByStepId.get(stepId) || null;
  }

  function updateBomTrackingItemState(nextItem) {
    if (!nextItem?.stepId) return;
    setBomTracking((prev) => {
      const normalizedPrev = normalizeBomTrackingPayload(prev);
      const noteMaxLength = normalizedPrev.noteMaxLength || BOM_TRACKING_DEFAULT_NOTE_MAX_LENGTH;
      const normalizedItem = normalizeBomTrackingPayload({
        noteMaxLength,
        items: [nextItem]
      }).items[0];
      if (!normalizedItem) return normalizedPrev;
      const shouldKeep = normalizedItem.owned || normalizedItem.hasNote;
      const items = normalizedPrev.items.filter((item) => item.stepId !== normalizedItem.stepId);
      if (shouldKeep) {
        items.push(normalizedItem);
      }
      return {
        noteMaxLength,
        items
      };
    });
  }

  function buildBomTrackingGoback(item) {
    if (!data?.project?.slugURL || !data?.guide?.slugURL) return '';
    const target = getBomItemFocusTarget(item);
    const base = `/projects/${data.project.slugURL}/guides/${data.guide.slugURL}`;
    if (!target) return base;
    return `${base}?${BOM_URL_ITEM_PARAM}=${encodeURIComponent(target.focusToken)}#${target.anchorId}`;
  }

  function storeBomTrackingIntent(item, action, payload = {}) {
    if (!data?.project?.slugURL || !data?.guide?.slugURL || !item?.stepId) return;
    try {
      localStorage.setItem(BOM_TRACKING_INTENT_KEY, JSON.stringify({
        projectSlug: data.project.slugURL,
        guideSlug: data.guide.slugURL,
        stepId: item.stepId,
        action,
        payload,
        createdAt: Date.now()
      }));
    } catch (e) {
      // Ignore storage failures; auth redirect still works without replay.
    }
  }

  function handleBomTrackingAuthNavigate(item, action, payload = {}) {
    storeBomTrackingIntent(item, action, payload);
    const goback = buildBomTrackingGoback(item);
    if (!goback) return;
    navigate(`${config.prefixPath}/login?goback=${encodeURIComponent(goback)}&rl=1`);
  }

  async function saveBomTrackingItem(item, updates) {
    if (!item?.stepId || !data?.project?.slugURL || !data?.guide?.slugURL) return null;
    if (!isAuthenticated) {
      handleBomTrackingAuthNavigate(item, updates?.note !== undefined ? 'openNote' : 'markOwned', updates);
      return null;
    }

    const itemKey = getBomItemKey(item);
    setBomTrackingSavingKey(itemKey);
    try {
      const response = await api.put(
        `/api/v1/projects/${project}/guides/${guide}/bom-tracking/items/${encodeURIComponent(item.stepId)}`,
        updates
      );
      if (response?.error) {
        throw new Error(response.message || 'Unable to save item tracking.');
      }
      if (response?.trackingItem) {
        updateBomTrackingItemState(response.trackingItem);
      }
      if (Number.isFinite(Number(response?.noteMaxLength))) {
        setBomTracking((prev) => ({
          ...normalizeBomTrackingPayload(prev),
          noteMaxLength: Math.floor(Number(response.noteMaxLength))
        }));
      }
      return response?.trackingItem || null;
    } catch (err) {
      toast.error(err?.message || 'Unable to save item tracking.');
      throw err;
    } finally {
      setBomTrackingSavingKey((current) => (current === itemKey ? '' : current));
    }
  }

  function handleBomOwnedToggle(item) {
    const tracking = getBomTrackingForItem(item);
    const nextOwned = !(tracking?.owned === true);
    if (!isAuthenticated) {
      handleBomTrackingAuthNavigate(item, 'markOwned', { owned: true });
      return;
    }
    saveBomTrackingItem(item, { owned: nextOwned }).catch(() => {});
  }

  function openBomMyNote(item) {
    if (!item?.stepId) return;
    if (!isAuthenticated) {
      handleBomTrackingAuthNavigate(item, 'openNote');
      return;
    }
    selectBomItem(item);
    const tracking = getBomTrackingForItem(item);
    setBomNoteEditor({
      itemKey: getBomItemKey(item),
      text: tracking?.note || '',
      error: ''
    });
  }

  async function saveBomMyNote(item) {
    const itemKey = getBomItemKey(item);
    if (!itemKey || bomNoteEditor.itemKey !== itemKey) return;
    const noteMaxLength = Number(bomTracking?.noteMaxLength) || BOM_TRACKING_DEFAULT_NOTE_MAX_LENGTH;
    const text = (bomNoteEditor.text || '').toString();
    if (text.length > noteMaxLength) {
      setBomNoteEditor((prev) => ({
        ...prev,
        error: `My note must be ${noteMaxLength} characters or less.`
      }));
      return;
    }
    try {
      await saveBomTrackingItem(item, { note: text });
      setBomNoteEditor({ itemKey: '', text: '', error: '' });
    } catch (e) {
      setBomNoteEditor((prev) => ({
        ...prev,
        error: e?.message || 'Unable to save my note.'
      }));
    }
  }

  function getBomViewportAnchor() {
    const itemRows = Array.from(document.querySelectorAll('[data-bom-item-anchor]'));
    if (!itemRows.length) return null;

    const viewportCenter = window.innerHeight / 2;
    let bestMatch = null;

    itemRows.forEach((row) => {
      const anchorId = (row?.id || '').toString().trim();
      const anchorToken = (row?.dataset?.bomItemAnchor || '').toString().trim();
      if (!anchorId || !anchorToken) return;

      const rect = row.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

      const rowCenter = rect.top + (rect.height / 2);
      const distance = Math.abs(rowCenter - viewportCenter);

      if (!bestMatch || distance < bestMatch.distance) {
        bestMatch = {
          anchorId,
          top: rect.top,
          distance
        };
      }
    });

    return bestMatch ? { anchorId: bestMatch.anchorId, top: bestMatch.top } : null;
  }

  function handleEditModeToggle() {
    const nextEditMode = !editMode;

    if (isBomGuide) {
      const anchor = getBomViewportAnchor();
      bomEditModeScrollAnchorRef.current = anchor
        ? {
            ...anchor,
            targetEditMode: nextEditMode,
            attempts: 0
          }
        : null;
    } else {
      bomEditModeScrollAnchorRef.current = null;
    }

    if (bomEditModeScrollRestoreRafRef.current !== null) {
      window.cancelAnimationFrame(bomEditModeScrollRestoreRafRef.current);
      bomEditModeScrollRestoreRafRef.current = null;
    }

    setEditMode(nextEditMode);
  }

  function getBomCategorySuggestions(items) {
    const seen = new Set();
    const values = [];
    BOM_DEFAULT_CATEGORIES.forEach((category) => {
      const key = category.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      values.push(category);
    });
    (Array.isArray(items) ? items : []).forEach((item) => {
      const category = (item?.category || '').toString().trim();
      if (!category) return;
      const key = category.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      values.push(category);
    });
    return values;
  }

  function getBomModuleSuggestions(items) {
    const seen = new Set();
    const values = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
      const moduleName = (item?.module || '').toString().trim();
      if (!moduleName) return;
      const key = moduleName.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      values.push(moduleName);
    });
    return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  function getBomUrlTypeSuggestions(items) {
    const seen = new Set();
    const values = [];
    BOM_DEFAULT_URL_TYPES.forEach((type) => {
      const key = type.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      values.push(type);
    });
    (Array.isArray(items) ? items : []).forEach((item) => {
      const manualUrls = sanitizeBomManualUrlEntriesClient(item?.urls, { includeEmpty: false, dropInvalid: false });
      manualUrls.forEach((entry) => {
        const type = sanitizeBomUrlTypeClient(entry?.type);
        if (!type) return;
        const key = type.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        values.push(type);
      });
    });
    return values;
  }

  function getBomVersionOptions(items) {
    const seen = new Set();
    const versions = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
      const list = Array.isArray(item?.versions) ? item.versions : [];
      list.forEach((version) => {
        const value = (version || '').toString().trim();
        if (!value) return;
        const key = value.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        versions.push(value);
      });
    });
    return versions.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  function normalizeCsvExportVersions(rawVersions, availableVersions = null) {
    const list = Array.isArray(rawVersions) ? rawVersions : [];
    const allowedMap = Array.isArray(availableVersions)
      ? availableVersions.reduce((acc, version) => {
        const value = (version || '').toString().trim();
        if (!value) return acc;
        const key = value.toLowerCase();
        if (!Object.prototype.hasOwnProperty.call(acc, key)) {
          acc[key] = value;
        }
        return acc;
      }, {})
      : null;
    const normalized = [];
    const seen = new Set();
    list.forEach((version) => {
      const value = (version || '').toString().trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (allowedMap && !Object.prototype.hasOwnProperty.call(allowedMap, key)) return;
      if (seen.has(key)) return;
      seen.add(key);
      normalized.push(allowedMap ? allowedMap[key] : value);
    });
    return normalized;
  }

  function formatBomLabel(value) {
    const text = (value || '').toString().trim();
    if (!text) return '';
    return text
      .split(/\s+/)
      .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ''))
      .join(' ');
  }

  function sanitizeBomAutoUrlProvidersClient(rawProviders) {
    const source = Array.isArray(rawProviders)
      ? rawProviders
      : (rawProviders === undefined || rawProviders === null ? [] : [rawProviders]);
    const providers = [];
    const seen = new Set();
    source.forEach((entry) => {
      const text = (entry || '').toString();
      text.split(',').forEach((candidate) => {
        const key = candidate.toLowerCase().trim();
        if (!key || !BOM_AUTO_URL_PROVIDER_KEYS.has(key) || seen.has(key)) return;
        seen.add(key);
        providers.push(key);
      });
    });
    return providers;
  }

  function getBomItemImages(item) {
    const source = [];
    if (Array.isArray(item?.images)) {
      item.images.forEach((image) => {
        if (typeof image === 'string') source.push(image);
        if (image?.url) source.push(image.url);
      });
    }
    if (item?.image?.url) {
      source.push(item.image.url);
    }
    const seen = new Set();
    const images = [];
    source.forEach((candidate) => {
      const url = sanitizeBomClientImageUrl(candidate);
      if (!url) return;
      const key = url.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      images.push({ url });
    });
    return images;
  }

  function getBomItemGeneratedUrls(item) {
    const name = (item?.name || item?.title || '').toString().trim();
    const selectedProviders = sanitizeBomAutoUrlProvidersClient(item?.autoUrlProviders);
    if (!name || !selectedProviders.length) return [];
    const query = encodeURIComponent(name);
    return selectedProviders.map((providerKey) => {
      const provider = BOM_AUTO_URL_PROVIDER_MAP[providerKey];
      if (!provider || typeof provider.buildSearchUrl !== 'function') return null;
      const url = sanitizeBomClientUrl(provider.buildSearchUrl(query));
      if (!url) return null;
      return {
        provider: provider.key,
        label: provider.label,
        url
      };
    }).filter(Boolean);
  }

  function navigateWithBomQuery(updater) {
    const params = new URLSearchParams(location.search);
    updater(params);
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash || ''}`);
  }

  function setBomViewFromControl(nextView, focusedGroup = '') {
    const normalizedView = normalizeBomView(nextView);
    const normalizedFocus = (focusedGroup || '').toString().trim();
    setBomGroupBy(normalizedView);
    setBomFocusedGroup(normalizedView === 'none' ? '' : normalizedFocus);
    navigateWithBomQuery((params) => {
      if (normalizedView === 'none') {
        params.delete(BOM_URL_VIEW_PARAM);
        params.delete(BOM_URL_FOCUS_PARAM);
        return;
      }
      params.set(BOM_URL_VIEW_PARAM, normalizedView);
      if (normalizedFocus) {
        params.set(BOM_URL_FOCUS_PARAM, normalizedFocus);
      } else {
        params.delete(BOM_URL_FOCUS_PARAM);
      }
    });
  }

  function setBomVersionFilterFromControl(nextVersion) {
    const normalizedVersion = (nextVersion || '').toString().trim();
    setBomVersionFilter(normalizedVersion);
    navigateWithBomQuery((params) => {
      if (normalizedVersion) {
        params.set(BOM_URL_VERSION_PARAM, normalizedVersion);
      } else {
        params.delete(BOM_URL_VERSION_PARAM);
      }
    });
  }

  function normalizeBomItem(item, position, allItems, currentIndex = null) {
    const name = (item?.name || item?.title || '').toString().trim() || `Item ${position}`;
    const description = ((item?.description ?? item?.content) || '').toString();
    const identifierInput = (item?.identifier || buildBomIdentifierSuggestion(name)).toString();
    const identifier = ensureUniqueBomIdentifierClient(identifierInput, allItems, currentIndex);
    const versionsInput = item?.versionsInput !== undefined && item?.versionsInput !== null
      ? item.versionsInput.toString()
      : stringifyCommaValues(Array.isArray(item?.versions) ? item.versions : parseCommaValues(item?.versions || ''));
    const versions = parseCommaValues(versionsInput);
    const urls = (() => {
      const fromStructured = sanitizeBomManualUrlEntriesClient(item?.urls, { includeEmpty: true, dropInvalid: false });
      if (fromStructured.length) return fromStructured;
      const legacyInput = item?.urlsInput !== undefined && item?.urlsInput !== null
        ? item.urlsInput.toString()
        : '';
      if (!legacyInput) return [];
      return parseCommaValues(legacyInput)
        .map((url) => sanitizeBomClientUrl(url))
        .filter(Boolean)
        .map((url) => ({ url }));
    })();
    const images = getBomItemImages(item);
    const autoUrlProviders = sanitizeBomAutoUrlProvidersClient(item?.autoUrlProviders);
    const normalized = {
      ...item,
      stepId: item?.stepId || createStepId(),
      position,
      title: name,
      name,
      content: description,
      description,
      identifier,
      quantity: (item?.quantity || '').toString(),
      category: (item?.category || '').toString(),
      module: (item?.module || '').toString(),
      versionsInput,
      versions,
      urls,
      autoUrlProviders
    };
    if (images.length) {
      normalized.image = { url: images[0].url };
      normalized.images = images;
    } else {
      normalized.image = undefined;
      normalized.images = [];
    }
    return normalized;
  }

  function setBomItems(updater) {
    setData((prevData) => {
      if (!prevData?.guide) return prevData;
      const currentItems = Array.isArray(prevData.guide.steps) ? prevData.guide.steps : [];
      const updatedItems = updater(currentItems.map((item) => ({ ...item })));
      const normalizedItems = (Array.isArray(updatedItems) ? updatedItems : []).map((item, index, arr) =>
        normalizeBomItem(item, index + 1, arr, index)
      );
      return {
        ...prevData,
        guide: {
          ...prevData.guide,
          steps: normalizedItems
        }
      };
    });
  }

  function handleBomQuickItemChange(field, value) {
    setBomQuickItem((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'name') {
        const suggested = buildBomIdentifierSuggestion(value);
        if (!prev.identifier || prev.identifier === buildBomIdentifierSuggestion(prev.name)) {
          next.identifier = suggested;
        }
      }
      if (field === 'identifier') {
        next.identifier = normalizeBomIdentifierClient(value);
      }
      return next;
    });
  }

  function addBomQuickUrlEntry() {
    setBomQuickItem((prev) => {
      const currentUrls = sanitizeBomManualUrlEntriesClient(prev?.urls, { includeEmpty: true, dropInvalid: false });
      return { ...prev, urls: [...currentUrls, { type: '', url: '' }] };
    });
  }

  function updateBomQuickUrlEntry(urlIndex, field, value) {
    setBomQuickItem((prev) => {
      const currentUrls = sanitizeBomManualUrlEntriesClient(prev?.urls, { includeEmpty: true, dropInvalid: false });
      const nextUrls = currentUrls.map((entry, entryIndex) => {
        if (entryIndex !== urlIndex) return entry;
        const updated = { ...(entry || {}) };
        if (field === 'type') {
          updated.type = sanitizeBomUrlTypeClient(value);
        } else {
          updated.url = (value || '').toString().slice(0, 2048);
        }
        return updated;
      });
      return { ...prev, urls: nextUrls };
    });
  }

  function removeBomQuickUrlEntry(urlIndex) {
    setBomQuickItem((prev) => {
      const currentUrls = sanitizeBomManualUrlEntriesClient(prev?.urls, { includeEmpty: true, dropInvalid: false });
      return { ...prev, urls: currentUrls.filter((_, entryIndex) => entryIndex !== urlIndex) };
    });
  }

  function addBomItem() {
    if (!isEditable()) return;
    const items = getBomItems();
    const name = bomQuickItem.name.trim();
    if (!name) {
      toast.error('Item name is required.');
      return;
    }
    const draftItem = {
      name,
      identifier: bomQuickItem.identifier || buildBomIdentifierSuggestion(name),
      quantity: bomQuickItem.quantity,
      category: bomQuickItem.category,
      module: bomQuickItem.module,
      description: bomQuickItem.description,
      versions: parseCommaValues(bomQuickItem.versions),
      urls: sanitizeBomManualUrlEntriesClient(bomQuickItem.urls, { includeEmpty: false, dropInvalid: true })
    };
    const normalizedDraft = normalizeBomItem(draftItem, items.length + 1, items, null);
    setBomItems((prevItems) => [...prevItems, normalizedDraft]);
    setBomSelectedItemKey(getBomItemKey(normalizedDraft));
    setBomQuickItem({
      name: '',
      identifier: '',
      quantity: '',
      category: bomQuickItem.category || '',
      module: bomQuickItem.module || '',
      versions: bomQuickItem.versions || '',
      urls: [{ type: '', url: '' }],
      description: ''
    });
  }

  function updateBomItemField(index, field, value) {
    setBomItems((prevItems) => {
      const next = [...prevItems];
      const current = { ...(next[index] || {}) };
      if (field === 'versions') {
        current.versionsInput = value;
        current.versions = parseCommaValues(value);
      } else if (field === 'urls') {
        const nextUrls = Array.isArray(value)
          ? sanitizeBomManualUrlEntriesClient(value, { includeEmpty: true, dropInvalid: false })
          : parseCommaValues(value).map((url) => sanitizeBomClientUrl(url)).filter(Boolean).map((url) => ({ url }));
        current.urls = nextUrls;
      } else if (field === 'identifier') {
        current.identifier = normalizeBomIdentifierClient(value);
      } else if (field === 'description') {
        current.description = value;
      } else if (field === 'autoUrlProviders') {
        current.autoUrlProviders = sanitizeBomAutoUrlProvidersClient(value);
      } else {
        current[field] = value;
      }
      next[index] = current;
      return next;
    });
  }

  function addBomItemUrlEntry(index) {
    if (!isEditable()) return;
    setBomItems((prevItems) => {
      const next = [...prevItems];
      const current = { ...(next[index] || {}) };
      const urls = sanitizeBomManualUrlEntriesClient(current?.urls, { includeEmpty: true, dropInvalid: false });
      current.urls = [...urls, { url: '' }];
      next[index] = current;
      return next;
    });
  }

  function updateBomItemUrlEntry(index, urlIndex, field, value) {
    if (!isEditable()) return;
    setBomItems((prevItems) => {
      const next = [...prevItems];
      const current = { ...(next[index] || {}) };
      const urls = sanitizeBomManualUrlEntriesClient(current?.urls, { includeEmpty: true, dropInvalid: false });
      const nextUrls = urls.map((entry, entryIndex) => {
        if (entryIndex !== urlIndex) return entry;
        const updated = { ...(entry || {}) };
        if (field === 'type') {
          updated.type = sanitizeBomUrlTypeClient(value);
        } else {
          updated.url = (value || '').toString().slice(0, 2048);
        }
        return updated;
      });
      current.urls = nextUrls;
      next[index] = current;
      return next;
    });
  }

  function removeBomItemUrlEntry(index, urlIndex) {
    if (!isEditable()) return;
    setBomItems((prevItems) => {
      const next = [...prevItems];
      const current = { ...(next[index] || {}) };
      const urls = sanitizeBomManualUrlEntriesClient(current?.urls, { includeEmpty: true, dropInvalid: false });
      current.urls = urls.filter((_, entryIndex) => entryIndex !== urlIndex);
      next[index] = current;
      return next;
    });
  }

  function removeBomItem(index) {
    if (!isEditable()) return;
    setBomItems((prevItems) => prevItems.filter((_, itemIndex) => itemIndex !== index));
  }

  function moveBomItem(fromIndex, toIndex) {
    if (!isEditable()) return;
    setBomItems((prevItems) => {
      if (toIndex < 0 || toIndex >= prevItems.length) return prevItems;
      return arrayMoveImmutable(prevItems, fromIndex, toIndex);
    });
  }

  function appendBomItemImages(index, imageUrls) {
    setBomItems((prevItems) => {
      const next = [...prevItems];
      const current = { ...(next[index] || {}) };
      const existingImages = getBomItemImages(current);
      const incomingImages = (Array.isArray(imageUrls) ? imageUrls : [])
        .map((url) => sanitizeBomClientImageUrl(url))
        .filter(Boolean)
        .map((url) => ({ url }));
      const merged = [...existingImages, ...incomingImages];
      const seen = new Set();
      current.images = merged.filter((image) => {
        const key = (image?.url || '').toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      current.image = current.images[0] ? { url: current.images[0].url } : undefined;
      next[index] = current;
      return next;
    });
  }

  function removeBomItemImage(itemIndex, imageUrl) {
    if (!isEditable()) return;
    const normalizedTarget = sanitizeBomClientImageUrl(imageUrl);
    if (!normalizedTarget) return;
    setBomItems((prevItems) => {
      const next = [...prevItems];
      const current = { ...(next[itemIndex] || {}) };
      const filtered = getBomItemImages(current).filter((image) => image.url !== normalizedTarget);
      current.images = filtered;
      current.image = filtered[0] ? { url: filtered[0].url } : undefined;
      next[itemIndex] = current;
      return next;
    });
  }

  async function uploadBomItemImages(event, itemIndex) {
    if (!isEditable()) return;
    const files = Array.from(event?.target?.files || []).filter((file) => file?.type?.startsWith('image/'));
    if (!files.length) return;
    const currentItems = getBomItems();
    const item = currentItems[itemIndex];
    if (!item) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('step', item.position);
      formData.append('stepTitle', item.name || item.title || `Item ${item.position}`);
      if (item.stepId) {
        formData.append('stepId', item.stepId);
      }
      formData.append('nows', '1');
      files.forEach((file) => {
        formData.append('files', file, file.name);
      });
      const response = await api({
        method: 'post',
        url: `/api/v1/projects/${project}/guides/${guide}/upload`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response?.ok && Array.isArray(response.images) && response.images.length) {
        appendBomItemImages(itemIndex, response.images);
      } else {
        toast.error(response?.message || 'Unable to upload images.');
      }
    } catch (err) {
      toast.error('Unable to upload images.');
    } finally {
      setLoading(false);
      if (event?.target) {
        event.target.value = '';
      }
    }
  }

  useEffect(() => {
    pendingCommentIndexRef.current = 0;
  }, [pendingModerationCount, data?.guide?.id]);

  useEffect(() => {
    pendingTodoIndexRef.current = 0;
  }, [pendingTodoCount, data?.guide?.id]);

  function scrollToNextPendingComment() {
    if (!pendingModerationCount) return;
    setOpenAllCommentsSignal((prev) => prev + 1);

    let attempts = 0;
    const findAndScroll = () => {
      const pendingNodes = Array.from(document.querySelectorAll('[data-comment-status="pending"]'));
      if (!pendingNodes.length) {
        attempts += 1;
        if (attempts < 8) {
          setTimeout(findAndScroll, 200);
        }
        return;
      }
      const index = pendingCommentIndexRef.current % pendingNodes.length;
      const target = pendingNodes[index];
      pendingCommentIndexRef.current = (index + 1) % pendingNodes.length;
      try {
        target.classList.add('comment-pending-focus');
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          try { target.classList.remove('comment-pending-focus'); } catch (e) {}
        }, 1600);
      } catch (e) {}
    };

    setTimeout(findAndScroll, 100);
  }

  function scrollToNextPendingTodo() {
    if (!pendingTodoCount) return;
    const openTodos = (Array.isArray(guideNotes) ? guideNotes : [])
      .filter(note => note?.isTodo && note?.todoStatus !== 'done')
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    if (!openTodos.length) return;

    const nextIndex = pendingTodoIndexRef.current % openTodos.length;
    const target = openTodos[nextIndex];
    pendingTodoIndexRef.current = (nextIndex + 1) % openTodos.length;

    const stepsArray = Array.isArray(data?.guide?.steps) ? data.guide.steps : [];
    let targetStep = null;
    if (target.stepId) {
      targetStep = stepsArray.find(step => step?.stepId === target.stepId) || null;
    }
    if (!targetStep && target.stepPosition) {
      targetStep = stepsArray.find(step => Number(step?.position) === Number(target.stepPosition)) || null;
    }
    const stepKey = targetStep ? getStepKey(targetStep) : (target.stepPosition ? `pos-${target.stepPosition}` : null);
    if (!stepKey) return;
    setTodoFocusNoteId(target.id);
    setOpenNotesStepKey(stepKey);
    if (isBomGuide && targetStep) {
      const bomItemKey = targetStep.stepId || `item-${targetStep.position}`;
      setBomSelectedItemKey(bomItemKey);
      setOpenBomItemStepIds((prev) => (prev.includes(bomItemKey) ? prev : [...prev, bomItemKey]));
      setTimeout(() => {
        const anchor = document.getElementById(`bom-item-${bomItemKey}`);
        if (anchor) {
          try { anchor.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        }
      }, 0);
      return;
    }
    const stepPosition = targetStep?.position || target.stepPosition;
    if (stepPosition) {
      setTimeout(() => {
        const anchor = document.getElementById(`step${stepPosition}`);
        if (anchor) {
          try { anchor.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
        }
      }, 0);
    }
  }

  function openGuideCommentsPanel() {
    setCommentAutoOpen({ scope: 'guide', key: Date.now() });
    setTimeout(() => {
      const target = document.getElementById('guide-comments');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  function handleCommentsAction() {
    if (canModerateComments && pendingModerationCount > 0) {
      scrollToNextPendingComment();
      return;
    }
    openGuideCommentsPanel();
  }

  function storeExportIntent(mode = 'pdf', options = null) {
    if (!data?.project?.slugURL || !data?.guide?.slugURL) {
      return;
    }
    let normalizedMode = 'pdf';
    if (mode === 'csv') normalizedMode = 'csv';
    if (mode === 'markdown') normalizedMode = 'markdown';
    if (mode === 'stepwisedocs') normalizedMode = 'stepwisedocs';
    const intent = {
      type: 'guide-export',
      mode: normalizedMode,
      project: data.project.slugURL,
      guide: data.guide.slugURL,
      options: options || (
        normalizedMode === 'csv'
          ? csvExportOptions
          : (normalizedMode === 'markdown'
            ? markdownExportOptions
            : (normalizedMode === 'pdf' ? exportOptions : null))
      ),
      createdAt: new Date().toISOString()
    };
    localStorage.setItem('guideExportIntent', JSON.stringify(intent));
  }

  function storeCommentIntent(scope, stepPosition) {
    if (!data?.project?.slugURL || !data?.guide?.slugURL) {
      return;
    }
    const numericStep = Number(stepPosition);
    const intent = {
      type: 'guide-comment',
      project: data.project.slugURL,
      guide: data.guide.slugURL,
      scope: scope === 'step' ? 'step' : 'guide',
      stepPosition: scope === 'step' && Number.isFinite(numericStep) ? numericStep : undefined,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem('guideCommentIntent', JSON.stringify(intent));
  }

  function storeProgressIntent() {
    if (!data?.project?.slugURL || !data?.guide?.slugURL) {
      return;
    }
    const intent = {
      type: 'guide-progress',
      project: data.project.slugURL,
      guide: data.guide.slugURL,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem('guideProgressIntent', JSON.stringify(intent));
  }

  function buildProgressGoback() {
    if (!data?.project?.slugURL || !data?.guide?.slugURL) {
      return null;
    }
    return `/projects/${data.project.slugURL}/guides/${data.guide.slugURL}`;
  }

  function handleProgressAuthNavigate() {
    storeProgressIntent();
    const goback = buildProgressGoback();
    if (!goback) return;
    navigate(`${config.prefixPath}/login?goback=${encodeURIComponent(goback)}&rl=1`);
  }

  function buildCommentGoback(scope, stepPosition) {
    if (!data?.project?.slugURL || !data?.guide?.slugURL) {
      return null;
    }
    const base = `/projects/${data.project.slugURL}/guides/${data.guide.slugURL}`;
    const params = new URLSearchParams();
    if (scope === 'step') {
      params.set('comment', 'step');
      if (stepPosition !== undefined && stepPosition !== null) {
        const numericStep = Number(stepPosition);
        if (Number.isFinite(numericStep)) {
          params.set('commentStep', numericStep);
        }
      }
    } else {
      params.set('comment', 'guide');
    }
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  }

  function handleCommentAuthNavigate(mode, scope, stepPosition) {
    const targetScope = scope === 'step' ? 'step' : 'guide';
    storeCommentIntent(targetScope, stepPosition);
    const goback = buildCommentGoback(targetScope, stepPosition);
    if (!goback) return;
    const destination = mode === 'register' ? 'register' : 'login';
    navigate(`${config.prefixPath}/${destination}?goback=${encodeURIComponent(goback)}&rl=1`);
  }

  function updateComments(updater) {
    setData(prevData => {
      if (!prevData) return prevData;
      const current = Array.isArray(prevData.comments) ? prevData.comments : [];
      const updated = updater(current);
      return { ...prevData, comments: updated };
    });
  }

  function handleCommentCreated(comment) {
    if (!comment) return;
    updateComments(prev => {
      const next = [...prev, comment];
      return next.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });
  }

  function handleCommentApproved(updated) {
    if (!updated) return;
    const updates = Array.isArray(updated) ? updated : [updated];
    if (!updates.length) return;
    updateComments(prev => prev.map(comment => {
      const match = updates.find(item => item && item.id === comment.id);
      return match ? { ...comment, ...match } : comment;
    }));
  }

  function handleCommentDeleted(payload) {
    if (!payload) return;
    if (typeof payload === 'number') {
      const deletedId = payload;
      updateComments(prev => prev.filter(comment => {
        if (!Array.isArray(comment.path)) {
          return comment.id !== deletedId;
        }
        return !comment.path.includes(deletedId);
      }));
      return;
    }

    const deletedId = payload.deletedId || payload.id;
    if (payload.softDeleted) {
      const updatedComment = payload.comment;
      if (!deletedId) return;
      updateComments(prev => prev.map(comment => {
        if (comment.id !== deletedId) return comment;
        if (updatedComment) {
          return { ...comment, ...updatedComment };
        }
        return { ...comment, body: 'deleted by author' };
      }));
      return;
    }

    if (!deletedId) return;
    updateComments(prev => prev.filter(comment => {
      if (!Array.isArray(comment.path)) {
        return comment.id !== deletedId;
      }
      return !comment.path.includes(deletedId);
    }));
  }

  function sortNotesList(notes) {
    if (!Array.isArray(notes)) return [];
    return [...notes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }

  function updateNotes(updater) {
    setGuideNotes(prevNotes => {
      const current = Array.isArray(prevNotes) ? prevNotes : [];
      const updated = updater(current);
      return sortNotesList(updated);
    });
  }

  function handleNoteCreated(note) {
    if (!note) return;
    updateNotes(prev => [...prev, note]);
  }

  function handleNoteUpdated(updatedNote) {
    if (!updatedNote) return;
    updateNotes(prev => prev.map(note => (note.id === updatedNote.id ? { ...note, ...updatedNote } : note)));
  }

  function handleNoteDeleted(noteId) {
    if (!noteId) return;
    updateNotes(prev => prev.filter(note => note.id !== noteId));
  }

  function handleExportClick() {
    if (!data?.project || !data?.guide) {
      return;
    }
    if (!data.guide.published) {
      toast.error(`This ${entityLabel} is not published yet.`);
      return;
    }
    if (!isAuthenticated) {
      storeExportIntent('pdf', exportOptions);
      const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?export=1`);
      navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
      return;
    }
    setExportError('');
    setShowExportModal(true);
  }

  function handleMarkdownExportClick() {
    if (!data?.project || !data?.guide) {
      return;
    }
    if (!canManageGuide) {
      toast.error('Markdown export is available only to guide managers.');
      return;
    }
    setMarkdownExportError('');
    if (isBomGuide) {
      setMarkdownExportOptions((prev) => ({ ...prev, includeRelatedBoms: false }));
    }
    setShowMarkdownExportModal(true);
  }

  function handleCsvExportClick() {
    if (!data?.project || !data?.guide) {
      return;
    }
    if (!data.guide.published) {
      toast.error(`This ${entityLabel} is not published yet.`);
      return;
    }
    if (!isBomGuide) {
      toast.error('CSV export is available only for BOM guides.');
      return;
    }
    if (!isAuthenticated) {
      storeExportIntent('csv', csvExportOptions);
      const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?exportCsv=1`);
      navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
      return;
    }
    setCsvExportError('');
    setShowCsvExportModal(true);
  }

  function handleStepwisedocsExportClick() {
    if (!data?.project || !data?.guide) {
      return;
    }
    if (!data.guide.published) {
      toast.error(`This ${entityLabel} is not published yet.`);
      return;
    }
    if (!isBomGuide) {
      toast.error('StepWiseDocs export is available only for BOM guides.');
      return;
    }
    if (!isAuthenticated) {
      storeExportIntent('stepwisedocs', null);
      const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?exportStepwisedocs=1`);
      navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
      return;
    }
    downloadStepwisedocsExport();
  }

  function toggleCsvField(fieldKey) {
    setCsvExportOptions((prev) => {
      const currentFields = Array.isArray(prev.fields) ? prev.fields : [];
      if (currentFields.includes(fieldKey)) {
        const remaining = currentFields.filter((key) => key !== fieldKey);
        if (!remaining.length) {
          return prev;
        }
        return { ...prev, fields: remaining };
      }
      return { ...prev, fields: [...currentFields, fieldKey] };
    });
  }

  function toggleCsvVersion(version, availableVersions) {
    const value = (version || '').toString().trim();
    const allVersions = Array.isArray(availableVersions) ? availableVersions : [];
    if (!value || !allVersions.length) return;
    const targetKey = value.toLowerCase();
    setCsvExportOptions((prev) => {
      const currentSelected = normalizeCsvExportVersions(prev?.versions, allVersions);
      const allSelected = currentSelected.length === 0;
      let nextSelected = [];
      if (allSelected) {
        nextSelected = allVersions.filter((candidate) => (candidate || '').toString().trim().toLowerCase() !== targetKey);
      } else {
        const currentSet = new Set(currentSelected.map((candidate) => candidate.toLowerCase()));
        if (currentSet.has(targetKey)) {
          nextSelected = currentSelected.filter((candidate) => candidate.toLowerCase() !== targetKey);
        } else {
          nextSelected = [...currentSelected, value];
        }
      }
      const normalizedNext = normalizeCsvExportVersions(nextSelected, allVersions);
      if (normalizedNext.length === allVersions.length) {
        return { ...prev, versions: [] };
      }
      return { ...prev, versions: normalizedNext };
    });
  }

  function getExportFilename(disposition) {
    if (!disposition) return null;
    const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const filename = match ? (match[1] || match[2]) : null;
    if (!filename) return null;
    try {
      return decodeURIComponent(filename);
    } catch (e) {
      return filename;
    }
  }

  async function downloadGuidePdf() {
    if (!data?.project || !data?.guide) {
      return;
    }
    setExportLoading(true);
    setExportError('');
    try {
      const token = localStorage.getItem('token');
      const response = await Axios.post(
        `/api/v1/projects/${project}/guides/${guide}/export/pdf`,
        {
          includeProjectDescription: exportOptions.includeProjectDescription,
          includeIndex: exportOptions.includeIndex,
          images: exportOptions.images
        },
        {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const filename = getExportFilename(response?.headers?.['content-disposition']) || `${data.guide.slug || 'guide'}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
      toast.success('Download started.');
    } catch (err) {
      console.log(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        storeExportIntent('pdf', exportOptions);
        const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?export=1`);
        navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
        return;
      }
      const message = err?.response?.data?.message || err?.message || 'Unable to generate PDF.';
      setExportError(message);
      toast.error(message);
    } finally {
      setExportLoading(false);
    }
  }

  async function downloadGuideMarkdown() {
    if (!data?.project || !data?.guide || !canManageGuide) {
      return;
    }
    setMarkdownExportLoading(true);
    setMarkdownExportError('');
    try {
      const token = localStorage.getItem('token');
      const response = await Axios.post(
        `/api/v1/projects/${project}/guides/${guide}/export/markdown`,
        {
          includeProjectDescription: markdownExportOptions.includeProjectDescription,
          includeIndex: markdownExportOptions.includeIndex,
          includeRelatedBoms: !isBomGuide && markdownExportOptions.includeRelatedBoms,
          images: normalizeMarkdownExportImageSize(markdownExportOptions.images)
        },
        {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const filename = getExportFilename(response?.headers?.['content-disposition']) || `${data.guide.slug || 'guide'}-markdown.zip`;
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowMarkdownExportModal(false);
      toast.success('Markdown export download started.');
    } catch (err) {
      console.log(err);
      if (err?.response?.status === 401) {
        storeExportIntent('markdown', markdownExportOptions);
        const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?exportMarkdown=1`);
        navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
        return;
      }
      const message = err?.response?.status === 403
        ? 'Markdown export is available only to guide managers.'
        : (err?.response?.data?.message || err?.message || 'Unable to generate Markdown export.');
      setMarkdownExportError(message);
      toast.error(message);
    } finally {
      setMarkdownExportLoading(false);
    }
  }

  async function downloadBomCsv() {
    if (!data?.project || !data?.guide || !isBomGuide) {
      return;
    }
    setCsvExportLoading(true);
    setCsvExportError('');
    try {
      const token = localStorage.getItem('token');
      const bomItems = getBomItems();
      const versionOptions = getBomVersionOptions(bomItems);
      const selectedVersions = normalizeCsvExportVersions(csvExportOptions?.versions, versionOptions);
      const response = await Axios.post(
        `/api/v1/projects/${project}/guides/${guide}/export/csv`,
        {
          includeHeader: csvExportOptions.includeHeader,
          fields: csvExportOptions.fields,
          versions: selectedVersions
        },
        {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const filename = getExportFilename(response?.headers?.['content-disposition']) || `${data.guide.slug || 'bom'}.csv`;
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowCsvExportModal(false);
      toast.success('CSV download started.');
    } catch (err) {
      console.log(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        storeExportIntent('csv', csvExportOptions);
        const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?exportCsv=1`);
        navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
        return;
      }
      const message = err?.response?.data?.message || err?.message || 'Unable to generate CSV.';
      setCsvExportError(message);
      toast.error(message);
    } finally {
      setCsvExportLoading(false);
    }
  }

  async function downloadStepwisedocsExport() {
    if (!data?.project || !data?.guide || !isBomGuide) {
      return;
    }
    if (stepwisedocsExportLoading) {
      return;
    }
    setStepwisedocsExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await Axios.post(
        `/api/v1/projects/${project}/guides/${guide}/export/stepwisedocs`,
        {},
        {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const filename = getExportFilename(response?.headers?.['content-disposition']) || `${data.guide.slug || 'bom'}-stepwisedocs.json`;
      const blob = new Blob([response.data], { type: 'application/json;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('StepWiseDocs export download started.');
    } catch (err) {
      console.log(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        storeExportIntent('stepwisedocs', null);
        const goback = window.encodeURI(`/projects/${data.project.slugURL}/guides/${data.guide.slugURL}?exportStepwisedocs=1`);
        navigate(`${config.prefixPath}/login?goback=${goback}&rl=1`);
        return;
      }
      const message = err?.response?.data?.message || err?.message || 'Unable to generate StepWiseDocs export.';
      toast.error(message);
    } finally {
      setStepwisedocsExportLoading(false);
    }
  }

  function resetBomImportState(options = {}) {
    const keepMethod = options.keepMethod === true;
    const nextMethod = keepMethod ? bomImportMethod : '';
    setBomImportMethod(nextMethod);
    setBomImportPrompt('');
    setBomImportSourceUrl('');
    setBomImportSourceText('');
    setBomImportFile(null);
    setBomImportErrors([]);
    setBomImportStatus('');
    setBomImportStrategy(hasExistingBomItems ? BOM_IMPORT_STRATEGY.MERGE_BY_IDENTIFIER : BOM_IMPORT_STRATEGY.REPLACE_ALL);
    setBomImportPromptGenerated(false);
    setBomImportPromptCopied(false);
  }

  function openBomImportModal() {
    if (!isBomGuide || !isEditable()) return;
    resetBomImportState();
    setShowBomImportModal(true);
  }

  function closeBomImportModal() {
    if (bomImportLoading) return;
    setShowBomImportModal(false);
    resetBomImportState();
  }

  function selectBomImportMethod(methodKey) {
    if (!Object.values(BOM_IMPORT_METHOD).includes(methodKey)) return;
    setBomImportMethod(methodKey);
    setBomImportPrompt('');
    setBomImportSourceUrl('');
    setBomImportSourceText('');
    setBomImportFile(null);
    setBomImportErrors([]);
    setBomImportStatus('');
    setBomImportPromptGenerated(false);
    setBomImportPromptCopied(false);
  }

  function handleBomImportSourceUrlChange(value) {
    setBomImportSourceUrl(value);
    setBomImportPrompt('');
    setBomImportErrors([]);
    setBomImportStatus('');
    setBomImportPromptGenerated(false);
    setBomImportPromptCopied(false);
  }

  function handleBomImportSourceTextChange(value) {
    setBomImportSourceText(value);
    setBomImportPrompt('');
    setBomImportErrors([]);
    setBomImportStatus('');
    setBomImportPromptGenerated(false);
    setBomImportPromptCopied(false);
  }

  function buildBomImportPrompt(methodKey, payloadValue = '') {
    const providerKeys = BOM_AUTO_URL_PROVIDERS.map((provider) => provider.key);
    const urlTypes = BOM_DEFAULT_URL_TYPES;
    const noInternetFallback = methodKey === BOM_IMPORT_METHOD.EXTERNAL_URL
      ? [
        '',
        'Internet access guardrail (URL method only):',
        '- If you cannot access SOURCE_URL because your environment has no internet access, explicitly state that in output JSON and do not invent BOM items.',
        '- In that case, return this exact fallback shape:',
        '{',
        `  "schema": "${BOM_IMPORT_SCHEMA}",`,
        `  "version": ${BOM_IMPORT_SCHEMA_VERSION},`,
        '  "meta": {',
        '    "error": "NO_INTERNET_ACCESS",',
        '    "message": "I cannot access external URLs in this environment."',
        '  },',
        '  "items": []',
        '}'
      ]
      : [];
    const sourceInstructions = (() => {
      if (methodKey === BOM_IMPORT_METHOD.EXTERNAL_URL) {
        return `SOURCE_URL: ${payloadValue}\nUse the URL above as the canonical source. Crawl that page and relevant linked sub-pages that contain BOM/content details.`;
      }
      return `SOURCE_TEXT:\n<<<BEGIN_SOURCE_TEXT\n${payloadValue}\nEND_SOURCE_TEXT>>>\nUse only the source text above.`;
    })();
    return [
      'You are a technical BOM extraction assistant.',
      'Return ONLY valid JSON (no markdown fences, no explanations).',
      '',
      sourceInstructions,
      '',
      'Goal:',
      'Convert the source BOM information into the StepWiseDocs BOM import schema below.',
      '',
      'Strict output schema:',
      '{',
      `  "schema": "${BOM_IMPORT_SCHEMA}",`,
      `  "version": ${BOM_IMPORT_SCHEMA_VERSION},`,
      '  "items": [',
      '    {',
      '      "name": "string (required, concise part name)",',
      '      "identifier": "string (optional but recommended; lowercase [a-z0-9._-], unique if possible)",',
      '      "quantity": "string (optional, ex: 2 or 1 pair)",',
      '      "category": "string (optional)",',
      '      "module": "string (optional)",',
      '      "description": "string (optional, markdown allowed)",',
      '      "versions": ["string", "string"],',
      '      "urls": [',
      '        { "type": "supplier|source|model file|datasheet|documentation|download|custom", "url": "https://..." }',
      '      ],',
      `      "autoUrlProviders": ["${providerKeys.join('", "')}"],`,
      '      "images": [',
      '        { "url": "https://example.com/image.jpg" }',
      '      ]',
      '    }',
      '  ]',
      '}',
      '',
      'Field requirements:',
      '- Extract ALL identifiable BOM fields for each item.',
      '- Preserve manufacturer references, SKU/MPN clues, and module grouping when available.',
      '- Include all useful URLs; assign the most accurate type label when possible.',
      `- Allowed autoUrlProviders values only: ${providerKeys.join(', ')}.`,
      '- For searchable off-the-shelf items, set autoUrlProviders so StepWiseDocs can auto-generate supplier links.',
      '- Electronics components (resistors, capacitors, ICs, connectors, transistors, diodes, regulators, inductors, sensors, switches) should usually include "digikey" and often "mouser".',
      '- Generic marketplace parts should usually include "amazon" and/or "aliexpress".',
      '- PCB fabrication/assembly entries should include "pcbway".',
      '- Only leave autoUrlProviders empty for custom/non-purchasable items or when no supplier search would make sense.',
      `- Preferred URL type values: ${urlTypes.join(', ')}.`,
      `- Images must be direct http/https links to JPG, PNG, or WEBP files and should be <= ${BOM_IMPORT_PROMPT_IMAGE_MAX_KB} KB each when possible.`,
      '- Do not use data: URLs, javascript: URLs, localhost URLs, or private-network URLs.',
      '- versions must be an array of strings. If unknown, use an empty array.',
      '- If identifier is unknown, generate a reasonable lowercase identifier.',
      '- Ensure each item has at least a valid "name".',
      '',
      'Example output fragment:',
      '{',
      `  "schema": "${BOM_IMPORT_SCHEMA}",`,
      `  "version": ${BOM_IMPORT_SCHEMA_VERSION},`,
      '  "items": [',
      '    {',
      '      "name": "M3x8 socket screw",',
      '      "identifier": "m3x8_socket_screw",',
      '      "quantity": "24",',
      '      "category": "fasteners",',
      '      "module": "frame",',
      '      "description": "Black steel screws for frame assembly.",',
      '      "versions": ["v1", "v2"],',
      '      "urls": [',
      '        { "type": "supplier", "url": "https://example.com/product/m3x8" },',
      '        { "type": "datasheet", "url": "https://example.com/datasheet/m3x8.pdf" }',
      '      ],',
      '      "autoUrlProviders": ["amazon", "aliexpress"],',
      '      "images": [',
      '        { "url": "https://example.com/images/m3x8.jpg" }',
      '      ]',
      '    },',
      '    {',
      '      "name": "10k resistor 1% 0603",',
      '      "identifier": "resistor_10k_0603_1pct",',
      '      "quantity": "20",',
      '      "category": "electronics",',
      '      "module": "control board",',
      '      "description": "SMD resistor used in pull-up networks.",',
      '      "versions": ["v1"],',
      '      "urls": [],',
      '      "autoUrlProviders": ["digikey", "mouser"],',
      '      "images": []',
      '    }',
      '  ]',
      '}',
      '',
      'Quality constraints:',
      '- Deduplicate identical items when they clearly refer to the same part.',
      '- Keep quantities exactly when provided by source.',
      '- Avoid hallucinating unavailable facts; leave optional fields empty when unknown.',
      '- Output must be parseable JSON with double quotes.',
      ...noInternetFallback
    ].join('\n');
  }

  function generateBomImportPrompt() {
    setBomImportErrors([]);
    setBomImportStatus('');
    if (bomImportMethod === BOM_IMPORT_METHOD.EXTERNAL_URL) {
      const candidate = (bomImportSourceUrl || '').toString().trim();
      if (!candidate) {
        setBomImportErrors(['A public URL is required.']);
        return;
      }
      try {
        const parsed = new URL(candidate);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          setBomImportErrors(['Only public http/https URLs are allowed.']);
          return;
        }
      } catch (e) {
        setBomImportErrors(['Please enter a valid URL.']);
        return;
      }
      setBomImportPrompt(buildBomImportPrompt(bomImportMethod, candidate));
      setBomImportStatus('Prompt ready. Copy it, run it in your preferred AI model, then upload the generated JSON file.');
      setBomImportPromptGenerated(true);
      setBomImportPromptCopied(false);
      return;
    }

    if (bomImportMethod === BOM_IMPORT_METHOD.RAW_TEXT) {
      const candidate = (bomImportSourceText || '').toString().trim();
      if (!candidate) {
        setBomImportErrors(['Please paste your current BOM text before generating the prompt.']);
        return;
      }
      if (candidate.length < 20) {
        setBomImportErrors(['The pasted text is too short. Please provide more BOM details.']);
        return;
      }
      setBomImportPrompt(buildBomImportPrompt(bomImportMethod, candidate));
      setBomImportStatus('Prompt ready. Copy it, run it in your preferred AI model, then upload the generated JSON file.');
      setBomImportPromptGenerated(true);
      setBomImportPromptCopied(false);
    }
  }

  async function copyBomImportPrompt() {
    if (!bomImportPrompt) return;
    try {
      await navigator.clipboard.writeText(bomImportPrompt);
      setBomImportPromptCopied(true);
      toast.success('Prompt copied to clipboard.');
    } catch (e) {
      toast.error('Unable to copy the prompt. Please copy it manually.');
    }
  }

  function handleBomImportFileChange(event) {
    const selectedFile = event?.target?.files?.[0] || null;
    setBomImportFile(selectedFile);
    setBomImportErrors([]);
    setBomImportStatus('');
  }

  function normalizeBomImportErrors(responsePayload) {
    const details = Array.isArray(responsePayload?.details) ? responsePayload.details : [];
    const mapped = details
      .map((detail) => (detail || '').toString().trim())
      .filter(Boolean);
    if (mapped.length) return mapped.slice(0, 20);
    const fallback = (responsePayload?.message || '').toString().trim();
    return fallback ? [fallback] : ['Unable to import BOM.'];
  }

  async function submitBomImport(event) {
    if (event?.preventDefault) event.preventDefault();
    if (!isBomGuide || !isEditable()) return;
    setBomImportErrors([]);
    setBomImportStatus('');

    if (!bomImportFile) {
      setBomImportErrors(['Please select a JSON file to import.']);
      return;
    }
    if (!/\.json$/i.test((bomImportFile.name || '').toString())) {
      setBomImportErrors(['Only JSON files (.json) are accepted.']);
      return;
    }
    if (isBomImportPromptMethod && !bomImportPrompt) {
      setBomImportErrors(['Generate and use the AI prompt before uploading the file.']);
      return;
    }

    const formData = new FormData();
    formData.append('file', bomImportFile, bomImportFile.name || 'bom-import.json');
    formData.append('strategy', effectiveBomImportStrategy);
    if (bomImportMethod) {
      formData.append('method', bomImportMethod);
    }

    setBomImportLoading(true);
    try {
      const response = await api({
        method: 'post',
        url: `/api/v1/projects/${project}/guides/${guide}/import`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response?.ok) {
        await fetchData();
        setOpenBomItemStepIds([]);
        setShowBomImportModal(false);
        resetBomImportState();
        toast.success(`BOM imported successfully (${response?.importedItems || 0} items processed).`);
        return;
      }
      setBomImportErrors(normalizeBomImportErrors(response));
      if (response?.message) {
        setBomImportStatus(response.message);
      }
    } catch (err) {
      const normalizedErrors = normalizeBomImportErrors(err);
      setBomImportErrors(normalizedErrors);
      const fallbackMessage = (err?.message || '').toString().trim();
      if (fallbackMessage) {
        setBomImportStatus(fallbackMessage);
      }
    } finally {
      setBomImportLoading(false);
    }
  }

  function isEditable() {
    return isGuideEditable;
  }

  function getUploaderURL() {
    return `${config.siteURL}${config.prefixPath}/projects/${data.project.slugURL}/guides/${data.guide.slugURL}/remote-upload`;
  }

  async function copyURL () {
    try {
        await navigator.clipboard.writeText(getUploaderURL());
        alert("URL copied! Share it with the person who will upload the images.");
    } catch (err) {
        alert('Error copying URL, please copy it manually and share it.');
    }
  }

  const ListTypeContext = createContext('ul');

  function resolveItemReference(reference) {
    const bomSlugKey = normalizeBomReferenceKey(reference?.bomSlug);
    const identifierKey = normalizeBomReferenceKey(reference?.identifier);

    const bom = bomReferenceLookup.get(bomSlugKey);
    if (!bom) {
      return {
        found: false,
        bom: null,
        item: null
      };
    }

    const item = bom.itemMap.get(identifierKey) || null;
    if (!item) {
      return {
        found: false,
        bom,
        item: null
      };
    }

    return {
      found: true,
      bom,
      item
    };
  }

  function resolveBomTagReference(reference) {
    const bomSlugKey = normalizeBomReferenceKey(reference?.slug);
    const bom = bomReferenceLookup.get(bomSlugKey);
    return {
      found: !!bom,
      bom: bom || null
    };
  }

  function resolveGuideTagReference(reference) {
    const guideSlugKey = normalizeBomReferenceKey(reference?.slug);
    const guideEntry = guideReferenceLookup.get(guideSlugKey);
    return {
      found: !!guideEntry,
      guide: guideEntry || null
    };
  }

  function getItemReferenceDisplayName(reference, resolved) {
    const optionalName = (reference?.optionalName || '').toString().trim();
    if (optionalName) {
      return optionalName;
    }
    if (resolved?.found && resolved?.item) {
      return (resolved.item.name || resolved.item.title || reference?.identifier || 'unknown item').toString();
    }
    return 'unknown item';
  }

  function getEntityReferenceDisplayName(reference, resolved) {
    const optionalName = (reference?.optionalName || '').toString().trim();
    if (optionalName) return optionalName;

    if (reference?.type === 'bom') {
      if (resolved?.found && resolved?.bom) {
        return (resolved.bom.title || resolved.bom.slugURL || reference?.slug || 'unknown BOM').toString();
      }
      return 'unknown BOM';
    }

    if (reference?.type === 'guide') {
      if (resolved?.found && resolved?.guide) {
        return (resolved.guide.title || resolved.guide.slugURL || reference?.slug || 'unknown guide').toString();
      }
      return 'unknown guide';
    }

    return 'unknown reference';
  }

  function buildBomReferenceItemUrl(resolved) {
    if (!resolved?.found || !resolved?.bom || !resolved?.item || !data?.project?.slugURL) return '';
    const focusToken = resolved.item.stepId || resolved.item.identifier;
    if (!focusToken) return '';
    const anchorToken = resolved.item.stepId || `item-${resolved.item.position || ''}`;
    const basePath = `${config.prefixPath}/projects/${data.project.slugURL}/guides/${resolved.bom.slugURL}`;
    return `${basePath}?bomItem=${encodeURIComponent(focusToken)}#bom-item-${anchorToken}`;
  }

  function getBomItemFocusTarget(item) {
    if (!item) return null;
    const focusToken = (item.stepId || item.identifier || '').toString().trim();
    if (!focusToken) return null;
    const anchorToken = item.stepId || `item-${item.position || ''}`;
    const itemKey = item.stepId || `item-${item.position || ''}`;
    if (!anchorToken || !itemKey) return null;
    return {
      focusToken,
      anchorId: `bom-item-${anchorToken}`,
      itemKey
    };
  }

  function focusCurrentBomItem(item, { updateUrl = false } = {}) {
    if (!isBomGuide) return false;
    const target = getBomItemFocusTarget(item);
    if (!target) return false;

    setOpenBomItemStepIds((prev) => (prev.includes(target.itemKey) ? prev : [...prev, target.itemKey]));
    bomItemIntentHandledRef.current = `${data?.guide?.slugURL || ''}|${target.focusToken}`;

    if (updateUrl) {
      const params = new URLSearchParams(location.search);
      params.set(BOM_URL_ITEM_PARAM, target.focusToken);
      const query = params.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ''}#${target.anchorId}`);
    }

    window.setTimeout(() => {
      const element = document.getElementById(target.anchorId);
      if (element) {
        try {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          // no-op
        }
      }
    }, 120);

    return true;
  }

  function buildEntityReferenceUrl(reference, resolved) {
    if (!data?.project?.slugURL) return '';
    if (reference?.type === 'bom') {
      const slugURL = resolved?.bom?.slugURL || '';
      if (!slugURL) return '';
      const params = new URLSearchParams();
      const filterType = normalizeBomFilterType(reference?.filterType);
      const filterValue = (reference?.filterValue || '').toString().trim();
      if (filterType && filterValue) {
        if (filterType === 'version') {
          params.set(BOM_URL_VERSION_PARAM, filterValue);
        } else {
          params.set(BOM_URL_VIEW_PARAM, filterType);
          params.set(BOM_URL_FOCUS_PARAM, filterValue);
        }
      }
      const query = params.toString();
      return `${config.prefixPath}/projects/${data.project.slugURL}/guides/${slugURL}${query ? `?${query}` : ''}`;
    }
    if (reference?.type === 'guide') {
      const slugURL = resolved?.guide?.slugURL || '';
      if (!slugURL) return '';
      return `${config.prefixPath}/projects/${data.project.slugURL}/guides/${slugURL}`;
    }
    return '';
  }

  function BomReferenceInline({ reference }) {
    const [showPopover, setShowPopover] = useState(false);
    const resolved = resolveItemReference(reference);
    const displayName = getItemReferenceDisplayName(reference, resolved);
    if (!resolved?.found) {
      return <span className="bom-reference-missing">{displayName}</span>;
    }

    const itemTitle = (resolved.item.name || resolved.item.title || resolved.item.identifier || displayName).toString();
    const versionsLabel = Array.isArray(resolved.item.versions) ? resolved.item.versions.join(', ') : '';
    const bomItemUrl = buildBomReferenceItemUrl(resolved);
    const referenceKey = `${normalizeBomReferenceKey(resolved.bom.slugURL)}|${normalizeBomReferenceKey(resolved.item.identifier)}`;
    const isCurrentBomReference = isBomGuide
      && normalizeBomReferenceKey(data?.guide?.slugURL) === normalizeBomReferenceKey(resolved.bom.slugURL);
    const popoverBom = {
      title: resolved.bom.title
    };
    const popoverItem = {
      imageUrl: resolved.item.imageUrl,
      identifier: resolved.item.identifier,
      category: resolved.item.category,
      module: resolved.item.module,
      versionsLabel,
      description: resolved.item.description
    };
    const handleOpenInCurrentBom = (event) => {
      if (!isCurrentBomReference) return;
      event.preventDefault();
      event.stopPropagation();
      if (focusCurrentBomItem(resolved.item, { updateUrl: true })) {
        setShowPopover(false);
      }
    };
    const renderPopover = (overlayProps) => (
      <Popover
        {...overlayProps}
        key={referenceKey}
        className={['bom-reference-popover', overlayProps?.className].filter(Boolean).join(' ')}
      >
        <Popover.Header as="h3">{itemTitle}</Popover.Header>
        <Popover.Body>
          {popoverItem.imageUrl ? (
            <button
              type="button"
              className="bom-reference-popover-image-button"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openReferenceImageLightbox(popoverItem.imageUrl, itemTitle);
              }}
              aria-label={`Expand image for ${itemTitle}`}
            >
              <LazyImage
                src={popoverItem.imageUrl}
                alt={itemTitle}
                className="bom-reference-popover-image"
                eager
              />
            </button>
          ) : null}
          <div><strong>BOM:</strong> {popoverBom.title}</div>
          <div><strong>Identifier:</strong> <code>{popoverItem.identifier}</code></div>
          {popoverItem.category ? <div><strong>Category:</strong> {popoverItem.category}</div> : null}
          {popoverItem.module ? <div><strong>Module:</strong> {popoverItem.module}</div> : null}
          {popoverItem.versionsLabel ? <div><strong>Versions:</strong> {popoverItem.versionsLabel}</div> : null}
          {popoverItem.description ? (
            <div className="bom-reference-popover-description mt-2 small">
              <MyMarkdownComponent content={popoverItem.description} />
            </div>
          ) : null}
          {bomItemUrl ? (
            <div className="mt-2">
              <a
                href={bomItemUrl}
                target={isCurrentBomReference ? undefined : '_blank'}
                rel={isCurrentBomReference ? undefined : 'noopener noreferrer'}
                onClick={isCurrentBomReference ? handleOpenInCurrentBom : undefined}
              >
                Open in BOM
              </a>
            </div>
          ) : null}
        </Popover.Body>
      </Popover>
    );

    return (
      <OverlayTrigger
        key={referenceKey}
        trigger="click"
        rootClose
        show={showPopover}
        onToggle={setShowPopover}
        placement="auto"
        overlay={renderPopover}
      >
        <button
          type="button"
          className="bom-reference-inline"
          data-bom-reference-key={referenceKey}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
        >
          {displayName}
        </button>
      </OverlayTrigger>
    );
  }

  function MarkdownEntityReferenceLink({ reference }) {
    const resolved = reference?.type === 'bom'
      ? resolveBomTagReference(reference)
      : resolveGuideTagReference(reference);
    const displayName = getEntityReferenceDisplayName(reference, resolved);
    const href = buildEntityReferenceUrl(reference, resolved);
    if (!href) {
      return <span className="bom-reference-missing">{displayName}</span>;
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {displayName}
      </a>
    );
  }

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function getFirstTextValue(node) {
    if (!node) return '';
    if (node.type === 'text' && typeof node.value === 'string') return node.value;
    if (Array.isArray(node.children)) {
      for (const child of node.children) {
        const textValue = getFirstTextValue(child);
        if (textValue && textValue.trim()) return textValue;
      }
    }
    return '';
  }

  function isExternalMarkdownLink(href) {
    const rawHref = (href || '').toString().trim();
    if (!rawHref || rawHref.startsWith('#')) return false;
    if (!/^(https?:)?\/\//i.test(rawHref)) return false;

    try {
      const browserOrigin = typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : '';
      const parsed = new URL(rawHref, browserOrigin || 'https://stepwisedocs.local');
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
      if (!browserOrigin) return true;
      return parsed.origin !== browserOrigin;
    } catch (e) {
      return false;
    }
  }

  function MarkdownLink({ href, children, ...props }) {
    const markdownReference = parseMarkdownReferenceHref(href);
    if (markdownReference) {
      if (markdownReference.type === 'item') {
        return <BomReferenceInline reference={markdownReference} />;
      }
      if (markdownReference.type === 'bom' || markdownReference.type === 'guide') {
        return <MarkdownEntityReferenceLink reference={markdownReference} />;
      }
    }
    const isExternal = isExternalMarkdownLink(href);
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  }

  function stripLeadingFromReactChildren(children, pattern) {
    if (!pattern) return children;
    let stripped = false;

    const stripChild = (child) => {
      if (stripped) return child;
      if (typeof child === 'string') {
        const next = child.replace(pattern, () => {
          stripped = true;
          return '';
        });
        return next;
      }
      if (React.isValidElement(child) && child.props?.children) {
        const nextChildren = React.Children.map(child.props.children, stripChild);
        return React.cloneElement(child, { ...child.props }, nextChildren);
      }
      return child;
    };

    return React.Children.map(children, stripChild);
  }

  function CustomListItem(props) {
    const listType = useContext(ListTypeContext); // Consume the context to get the list type
    const insideOL = listType === 'ol';
    // console.log(listType);
    // console.log(props.node.parentNode);
    const children = props.children;
    // console.log(children)
    let color = 'black';
    let text = children;
    // Map the colors to FontAwesomeIcon icons
    const colorIconMap = {
      blue: faCircle,
      red: faCircle,
      pink: faCircle,
      black: faCircle,
      green: faCircle,
      violet: faCircle,
      orange: faCircle,
      yellow: faCircle,
      lightblue: faCircle,
      alert: faTriangleExclamation,
      info: faCircleInfo,
      hint: faLightbulb,
    };

    let hexColor = '';

    const firstText = getFirstTextValue(props.node);
    const match = firstText.trimStart().match(/^(\S+)\s+/);
    const candidateToken = match ? match[1] : null;
    const isHexColor = candidateToken && /^#[0-9A-Fa-f]{6}$/.test(candidateToken);
    const isNamedColor = candidateToken && colorIconMap[candidateToken];

    let stripPattern = null;
    if (candidateToken && (isNamedColor || isHexColor)) {
      color = candidateToken;
      if (isHexColor) {
        hexColor = candidateToken;
      }
      stripPattern = new RegExp(`^\\s*${escapeRegExp(candidateToken)}\\s+`);
    }

    const colorClassMap = {
      alert: 'red',
      info: 'info'
    }

    if (stripPattern) {
      text = stripLeadingFromReactChildren(children, stripPattern);
    }

    return (
      <li>
            {!insideOL && (
              hexColor ? (<FontAwesomeIcon style={{color: hexColor}} className={`bullet-dynamic`} icon={faCircle} />) : (
                <FontAwesomeIcon className={`bullet-${colorClassMap[color] || color}`} icon={colorIconMap[color]} />)
            )}
            <span className={`step-text${insideOL ? '-ol' : ''}${colorClassMap[color] ? '-' + colorClassMap[color] : ''}`}>{text}</span>
        </li>
    );
  }

  function MyMarkdownComponent({ content, editable }) {
    return (
      <ReactMarkdown 
        remarkPlugins={markdownRemarkPlugins}
        components={{ 
          a: MarkdownLink,
          li: CustomListItem, 
          ul: ({ node, ...props }) => (
                    <ListTypeContext.Provider value="ul">
                        <ul className="step-items" {...props} />
                    </ListTypeContext.Provider>
                ),
                ol: ({ node, ...props }) => (
                    <ListTypeContext.Provider value="ol">
                        <ol className="step-itemso" {...props} />
                    </ListTypeContext.Provider>
                )
        }} 
      >
        {content}
      </ReactMarkdown>
    );
  }

  function MarkdownHelpModal({ show, onHide }) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        centered
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Markdown help</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Here you can use markdown. See the documentation:{' '}
            <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noreferrer">
              Markdown Guide
            </a>
          </p>
          <div>
            <strong>Colored bullets shortcuts:</strong><br/>
            [dash] [color_name] Your text here
            <pre className="bullet-shortcuts">{[
              '- red Your text here',
              '- yellow Your text here',
              '- blue Your text here',
              '- black Your text here',
              '- green Your text here',
              '- pink Your text here',
              '- orange Your text here',
              '- lightblue Your text here',
              '- violet Your text here',
              '- alert Your text here',
              '- info Your text here',
              '- hint Your text here',
              '- #RRGGBB Your text here'
            ].join('\n')}</pre>
          </div>
          <div className="mt-2">
            <strong>Reference tags:</strong>
            <ul className="mt-2">
              <li><code>!item[bom-slug](item-identifier)</code></li>
              <li><code>!item[bom-slug](item-identifier)(Optional Name)</code></li>
              <li><code>!bom[bom-slug]</code></li>
              <li><code>!bom[bom-slug](Optional Name)</code></li>
              <li><code>!bom[bom-slug, category=Electronics]</code></li>
              <li><code>!bom[bom-slug, module=Frame]</code></li>
              <li><code>!bom[bom-slug, version=v2](Optional Name)</code></li>
              <li><code>!guide[guide-slug]</code></li>
              <li><code>!guide[guide-slug](Optional Name)</code></li>
            </ul>
            <div className="small text-muted">
              BOM filters open the BOM with the matching category/module view or version filter applied.
            </div>
          </div>
          <p className="mb-0">
            Tip: type <code>!item</code>, <code>!bom</code>, or <code>!guide</code> to open autocomplete while editing.
          </p>
        </Modal.Body>
      </Modal>
    );
  }

  function CenteredModal(props) {
    return (
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Upload Image from a different Device
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Container>
            <div className="row">
              <div className="col-12" style={{textAlign: "center"}}>
                <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "30%" }}
                    value={getUploaderURL()}
                    viewBox={`0 0 256 256`}
                  />
                <h4 style={{marginTop: 10}}>Scan the QR to open the remote uploader on another device</h4>
                <div>
                  <span>The remote page can switch steps, add steps, delete the current step, and upload images. Or click this URL to copy and share it:</span>
                </div>
                
                  <span alt="Click to Copy" className="copyText" onClick={(e) => {copyURL()}}>{getUploaderURL()}</span>
                
                <h3 style={{marginTop: 10}}>Connected Clients: {connectedClients.length}</h3>
              </div>
            </div>

            <div className="row">
             <div className="col-12">
             </div>
            </div>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-sm btn-outline-dark" onClick={props.onHide}>Close</button>
        </Modal.Footer>
      </Modal>
    );
  }

  const [activeHash, setActiveHash] = useState(window.location.hash);
  const activeHashRef = useRef(window.location.hash);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveHash(window.location.hash);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    activeHashRef.current = activeHash;
  }, [activeHash]);

  useEffect(() => {
    if (!data?.guide?.steps?.length || editMode) {
      return;
    }

    const stepHeadings = data.guide.steps
      .map(step => document.getElementById(`step${step.position}`))
      .filter(Boolean);

    if (!stepHeadings.length) {
      return;
    }

    const scrollOffset = 160;
    let rafId = null;

    const updateActiveHashFromScroll = () => {
      rafId = null;

      let currentHeading = stepHeadings[0] || null;

      for (const heading of stepHeadings) {
        const rectTop = heading.getBoundingClientRect().top;
        if (rectTop - scrollOffset <= 0) {
          currentHeading = heading;
        }
      }

      if (!currentHeading) {
        return;
      }

      const nextHash = `#${currentHeading.id}`;

      if (nextHash !== activeHashRef.current) {
        setActiveHash(nextHash);
        if (window.location.hash !== nextHash) {
          window.history.replaceState(null, '', nextHash);
        }
      }
    };

    const handleScroll = () => {
      if (rafId !== null) {
        return;
      }
      rafId = window.requestAnimationFrame(updateActiveHashFromScroll);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [data, editMode]);

  if (error === '404') return (<Page404/>);
  if (!data) return;

  function onSortEnd (oldIndex, newIndex, stepIndex) {
    //console.log('oldIndex', oldIndex);
    //console.log('newIndex', newIndex);
    //console.log('stepIndex', stepIndex);

    setData(prevData => {
      let newSteps = [...prevData.guide.steps];
      // console.log('antes', newSteps[stepIndex].images);
      newSteps[stepIndex].images = arrayMoveImmutable(newSteps[stepIndex].images, oldIndex, newIndex)
      // console.log('despues', newSteps[stepIndex].images);

      return {
        ...prevData,
        guide: {
          ...prevData.guide,
          steps: newSteps
        },
      };
    });

  }

  function getProgressStorageKey(guideId) {
    const rawUser = (userInfo?.username || userInfo?.email || '').toString().trim().toLowerCase();
    if (!rawUser || !guideId) return null;
    return `progress_${rawUser}_${guideId}`;
  }

  function getLegacyProgressKey(guideId) {
    if (!guideId) return null;
    return `progress_${guideId}`;
  }

  function readStoredProgress(storageKey) {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredProgress(guideId, steps) {
    const key = getProgressStorageKey(guideId);
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(steps));
  }

  function clearStoredProgress(guideId) {
    const key = getProgressStorageKey(guideId);
    if (!key) return;
    localStorage.removeItem(key);
  }

  function sanitizeProgressSteps(rawSteps, guideData) {
    if (!guideData?.steps) return [];
    const validPositions = new Set(
      guideData.steps
        .map(step => Number(step?.position))
        .filter(position => Number.isFinite(position))
    );
    const stepsArray = Array.isArray(rawSteps) ? rawSteps : [];
    const normalized = [];
    const seen = new Set();
    stepsArray.forEach((value) => {
      const position = Number(value);
      if (!Number.isFinite(position)) return;
      if (!validPositions.has(position)) return;
      if (seen.has(position)) return;
      seen.add(position);
      normalized.push(position);
    });
    normalized.sort((a, b) => a - b);
    return normalized;
  }

  function queueProgressSync(payload, guideData) {
    if (!isAuthenticated) return;
    if (!guideData?.id) return;
    if ((guideData?.type || '').toString().toLowerCase() === 'bom') return;
    progressSyncRef.current.pending = {
      payload,
      projectSlug: project,
      guideSlug: guide,
      guideId: guideData.id
    };
    if (!progressSyncRef.current.inFlight) {
      flushProgressSync();
    }
  }

  async function flushProgressSync() {
    const state = progressSyncRef.current;
    if (state.inFlight) return;
    const next = state.pending;
    if (!next) return;
    state.pending = null;
    state.inFlight = true;
    try {
      await api.put(`/api/v1/projects/${next.projectSlug}/guides/${next.guideSlug}/progress`, next.payload);
    } catch (err) {
      console.log('Error saving guide progress', err);
      toast.error('Unable to save progress. Please try again.');
    } finally {
      state.inFlight = false;
      if (state.pending) {
        flushProgressSync();
      }
    }
  }

  function loadProgress(progressPayload, guideData) {
    if (!guideData?.id) return;
    if ((guideData?.type || '').toString().toLowerCase() === 'bom') {
      setGuideProgress({ tracking: false, steps: [] });
      return;
    }

    if (progressPayload?.tracking === false) {
      setGuideProgress({ tracking: false, steps: [] });
      clearStoredProgress(guideData.id);
      return;
    }

    if (Array.isArray(progressPayload?.steps)) {
      const sanitized = sanitizeProgressSteps(progressPayload.steps, guideData);
      setGuideProgress({ tracking: true, steps: sanitized });
      writeStoredProgress(guideData.id, sanitized);
      return;
    }

    if (!isAuthenticated) {
      setGuideProgress({ tracking: false, steps: [] });
      return;
    }

    const userKey = getProgressStorageKey(guideData.id);
    const stored = readStoredProgress(userKey);
    if (stored) {
      const sanitized = sanitizeProgressSteps(stored, guideData);
      setGuideProgress({ tracking: true, steps: sanitized });
      queueProgressSync({ tracking: true, steps: sanitized }, guideData);
      return;
    }

    const legacyKey = getLegacyProgressKey(guideData.id);
    const legacyStored = readStoredProgress(legacyKey);
    if (legacyStored) {
      const sanitized = sanitizeProgressSteps(legacyStored, guideData);
      setGuideProgress({ tracking: true, steps: sanitized });
      writeStoredProgress(guideData.id, sanitized);
      if (legacyKey) {
        localStorage.removeItem(legacyKey);
      }
      queueProgressSync({ tracking: true, steps: sanitized }, guideData);
      return;
    }

    setGuideProgress({ tracking: false, steps: [] });
  }

  function startTracking() {
    if (!data?.guide || !data?.project) return;
    if ((data?.guide?.type || '').toString().toLowerCase() === 'bom') return;
    if (!isAuthenticated) {
      handleProgressAuthNavigate();
      return;
    }
    const steps = [];
    setGuideProgress({ tracking: true, steps });
    writeStoredProgress(data.guide.id, steps);
    queueProgressSync({ tracking: true, steps }, data.guide);
  }

  function checkAllTracking() {
    if (!data?.guide || !data?.project) return;
    if ((data?.guide?.type || '').toString().toLowerCase() === 'bom') return;
    if (!isAuthenticated) {
      handleProgressAuthNavigate();
      return;
    }
    const positions = (Array.isArray(data.guide.steps) ? data.guide.steps : [])
      .map(step => Number(step?.position))
      .filter(position => Number.isFinite(position));
    const uniquePositions = Array.from(new Set(positions)).sort((a, b) => a - b);
    setGuideProgress({ tracking: true, steps: uniquePositions });
    writeStoredProgress(data.guide.id, uniquePositions);
    queueProgressSync({ tracking: true, steps: uniquePositions }, data.guide);
    jsConfetti.addConfetti({
      emojis: ['🦄', '⭐️', '🎊', '💥'],
      confettiNumber: 100,
    });
  }

  function resetTracking() {
    if (!data?.guide || !data?.project) return;
    if ((data?.guide?.type || '').toString().toLowerCase() === 'bom') return;
    if (!isAuthenticated) {
      handleProgressAuthNavigate();
      return;
    }
    setGuideProgress({ tracking: false, steps: [] });
    clearStoredProgress(data.guide.id);
    queueProgressSync({ tracking: false }, data.guide);
  }

  function checkStep(position) {
    if (!data?.guide || !data?.project) return;
    if ((data?.guide?.type || '').toString().toLowerCase() === 'bom') return;
    if (!isAuthenticated) {
      handleProgressAuthNavigate();
      return;
    }
    if (!guideProgress?.tracking) return;
    const numericPosition = Number(position);
    if (!Number.isFinite(numericPosition)) return;
    const currentSteps = Array.isArray(guideProgress.steps) ? guideProgress.steps : [];
    const hasStep = currentSteps.includes(numericPosition);
    let nextSteps = hasStep
      ? currentSteps.filter(item => item !== numericPosition)
      : [...currentSteps, numericPosition];
    nextSteps = sanitizeProgressSteps(nextSteps, data.guide);
    setGuideProgress({ tracking: true, steps: nextSteps });
    writeStoredProgress(data.guide.id, nextSteps);
    queueProgressSync({ tracking: true, steps: nextSteps }, data.guide);

    if (!hasStep) {
      jsConfetti.addConfetti();
      const totalSteps = Array.isArray(data.guide.steps) ? data.guide.steps.length : 0;
      if (totalSteps && nextSteps.length === totalSteps) {
        jsConfetti.addConfetti({
          emojis: ['🦄', '⭐️', '🎊', '💥'],
          confettiNumber: 100,
        });
      }
    }
  }

  function getProgress() {
    if (!data?.guide?.steps) return null;
    return (
      guideProgress?.tracking ? (
        <span>
          {guideProgress?.steps.length}/{data.guide.steps.length} {guideProgress?.steps.length === data.guide.steps.length ? <span className="badge text-bg-success">Completed</span> : <span className="badge text-bg-warning">In Progress</span>}{' '}
          <button type="button" className="btn btn-link p-0 align-baseline text-decoration-none" onClick={() => resetTracking()}>[Reset]</button>{' '}
          <button type="button" className="btn btn-link p-0 align-baseline text-decoration-none" onClick={() => checkAllTracking()}>[Check All]</button>
        </span>
      ) : (
        <button type="button" className="btn btn-link p-0 align-baseline text-decoration-none" onClick={() => startTracking()}>Start Tracking</button>
      )
    );
  }

  async function showAddVideo (e, stepPosition) {
   let videoURL = prompt("Paste the Youtube video URL here:");
   if (videoURL && videoURL.trim() && (videoURL.trim().indexOf('https://www.youtube.com/') === 0 || videoURL.trim().indexOf('https://youtu.be/') === 0)) {
    const newVideo = {
      url: videoURL.trim(),
      type: 'video'
    };

    await setData(prevData => {
        let newSteps = [...prevData.guide.steps];

        // Find index of the step whose `position` matches `stepPosition`
        let stepIndex = newSteps.findIndex(step => step.position === stepPosition);

        // If such step is found, add the image
        if (stepIndex !== -1) {
            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                images: [...newSteps[stepIndex].images, newVideo]
            };
        }
        return {
            ...prevData,
            guide: {
                ...prevData.guide,
                steps: newSteps
            },
        };
    })

    // Update active image of the step to the new image
   
    await setActiveImages(prevImages => {
      let newImages = [...prevImages];
      newImages[stepPosition - 1] = newVideo.url;
      return newImages;
    })

   } else {
    alert("Invalid URL. Must be a Youtube URL.");
   }
  }

function getYouTubeVideoID(url) {
  try {
    let parsedUrl = new URL(url);
    
    // Check if the URL has a 'v' query parameter
    let videoID = parsedUrl.searchParams.get("v");
    
    if (videoID) {
      return videoID;
    }
    
    // Extract pathname and split it by '/'
    let pathnameSegments = parsedUrl.pathname.split("/");

    // Check if it's a short URL (youtube.com/shorts)
    if (pathnameSegments[1] === "shorts" && pathnameSegments.length > 2) {
      return pathnameSegments[2];
    }
    
    // Assume it's a short URL format if no 'v' parameter and not a "shorts" URL
    if (pathnameSegments.length > 1) {
      return pathnameSegments[1];
    }
    
    return null; // No valid video ID found
  } catch (e) {
    console.error(e);
    return null;
  }
}

  function getVideoThumb (url, size) {
    const vidId = getYouTubeVideoID(url);
    const thumbURL = `https://img.youtube.com/vi/${vidId}/${size}.jpg`;
    return thumbURL;
  }

  function getStepAlt(step, variant) {
    const stepNumber = step?.position ? `Step ${step.position}` : 'Step';
    const stepTitle = step?.title ? `: ${step.title}` : '';
    const base = `${stepNumber}${stepTitle}`;
    if (variant === 'video') return `${base} video thumbnail`;
    if (variant === 'thumbnail') return `${base} image thumbnail`;
    if (variant === 'full') return `${base} full image`;
    if (variant === 'placeholder') return `${base} placeholder`;
    return `${base} image`;
  }

  function getVideoPlayer (url) {
    const id = getYouTubeVideoID(url);
    return (<iframe style={{maxHeight: 383, width: 'inherit', maxWidth: 'inherit', height: 370}} src={`https://www.youtube.com/embed/${id}`} title="YouTube video player" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>)
  }

  function drawVideoThumb(stepImage, indexImage, index, step) {
    const thumbURL = getVideoThumb(stepImage.url, 'hqdefault');
    return (
      <LazyImage
        alt={getStepAlt(step, 'video')}
        wrapperClassName="imgitem-media-wrapper"
        style={{
          userSelect: 'none', 
          width: '100%',
          height: '100%',
          cursor: 'pointer', 
          objectFit: 'cover'
        }}
        placeholderStyles={{minHeight: 0}}
        src={thumbURL}
        key={indexImage}
        className={activeImages[index] === stepImage.url ? 'border-tumb' : ''}
        onClick={() => handleImageClick(stepImage.url, index)}
      />
      );
  }

  function isVideo (url) {
    return isVideoUrl(url);
  }

  function openImageMarkupEditor(targetImage, step, stepTitle, stepId) {
    if (!targetImage) return;

    const markerArea = new markerjs2.MarkerArea(targetImage);
    const sourceImageUrl = targetImage.getAttribute('src') || targetImage.src || '';
    patchPopupScrollLock(markerArea);
    applyNaturalSizeGuideImageRenderSettings(markerArea);
    markerArea.settings.displayMode = 'popup';
    markerArea.settings.popupMargin = 100;
    markerArea.settings.defaultStrokeWidth = 5;
    markerArea.settings.defaultColorSet = ['#515ce6', '#feff00', '#ff4040', '#000000', '#FFFFFF', '#03bd9e', '#ff5583', '#ffbb3b', '#00a9ff', '#9e5fff', '#03ff00', '#feaaff'];
    markerArea.addEventListener("render", (event) => {
      saveNewImage (event.dataUrl, sourceImageUrl, step, stepTitle, stepId);
    });
    markerArea.show();
    // Bring popup above sticky navbars
    setTimeout(raiseEditorOverlays, 0);
    return markerArea;
  }

  function newEditor (e, step, stepTitle, stepId) {
    e.stopPropagation();
    e.preventDefault();
    openImageMarkupEditor(e.currentTarget || e.target, step, stepTitle, stepId);
  }

  function GuidePagination ({data}) {
    let prevGuide = '';
    let nextGuide = '';
    let prevFlag = true;
    data?.guides.forEach((guide) => {
      if (guide.id === data?.guide?.id) {
        // flit
        prevFlag = false;
      } else {
         if (prevFlag) {
          prevGuide = guide;
        } else {
          if (nextGuide === '') nextGuide = guide;
        }
      }
    });
    return (<div className="col-12 text-center">
              {prevGuide ? <div style={{float: 'left', display: 'inline', margin: 10}}><a className="text-decoration-none" href={`${config.prefixPath}/projects/${data.project.slugURL}/guides/${prevGuide.slugURL}`}>« {prevGuide.title}</a></div> : null}
              {nextGuide ? <div style={{float: 'right', display: 'inline', margin: 10}}><a className="text-decoration-none" href={`${config.prefixPath}/projects/${data.project.slugURL}/guides/${nextGuide.slugURL}`}>{nextGuide.title} »</a></div> : null}
            </div>)
  }

  function renderBomContent() {
    const bomItems = getBomItems();
    const categorySuggestions = getBomCategorySuggestions(bomItems);
    const moduleSuggestions = getBomModuleSuggestions(bomItems);
    const urlTypeSuggestions = getBomUrlTypeSuggestions(bomItems);
    const versionOptions = getBomVersionOptions(bomItems);
    const quickUrlEntries = sanitizeBomManualUrlEntriesClient(bomQuickItem.urls, { includeEmpty: true, dropInvalid: false });
    const activeVersionFilter = (bomVersionFilter || '').toString().trim();
    const groupedByCategory = bomGroupBy === 'category';
    const groupedByModule = bomGroupBy === 'module';
    const groupedProperty = groupedByCategory ? 'category' : (groupedByModule ? 'module' : null);
    const searchTerm = (bomSearchTerm || '').toString().trim().toLowerCase();
    const noteMaxLength = Number(bomTracking?.noteMaxLength) || BOM_TRACKING_DEFAULT_NOTE_MAX_LENGTH;
    const ownedCount = bomItems.filter((item) => getBomTrackingForItem(item)?.owned === true).length;
    const versionFilteredItems = activeVersionFilter
      ? bomItems.filter((item) => (Array.isArray(item?.versions) ? item.versions : [])
        .some((version) => version.toString().toLowerCase() === activeVersionFilter.toLowerCase()))
      : bomItems;
    const trackingFilteredItems = versionFilteredItems.filter((item) => {
      const tracking = getBomTrackingForItem(item);
      if (bomOwnershipFilter === BOM_OWNERSHIP_FILTER.OWNED && tracking?.owned !== true) return false;
      if (bomOwnershipFilter === BOM_OWNERSHIP_FILTER.MISSING && tracking?.owned === true) return false;
      if (bomNotesOnlyFilter && !tracking?.hasNote) return false;
      return true;
    });
    const filteredItems = searchTerm
      ? trackingFilteredItems.filter((item) => {
        const searchable = [
          item?.position,
          item?.name,
          item?.title,
          item?.identifier,
          item?.quantity,
          item?.category,
          item?.module,
          stringifyCommaValues(Array.isArray(item?.versions) ? item.versions : []),
          item?.description,
          item?.content
        ].map((value) => (value || '').toString()).join(' ').toLowerCase();
        return searchable.includes(searchTerm);
      })
      : trackingFilteredItems;
    const isReorderDisabled = !!activeVersionFilter
      || !!groupedProperty
      || !!searchTerm
      || bomOwnershipFilter !== BOM_OWNERSHIP_FILTER.ALL
      || bomNotesOnlyFilter;
    const categoryCount = new Set(bomItems.map((item) => (item?.category || '').toString().trim().toLowerCase()).filter(Boolean)).size;
    const moduleCount = new Set(bomItems.map((item) => (item?.module || '').toString().trim().toLowerCase()).filter(Boolean)).size;
    const selectedItem = filteredItems.find((item) => getBomItemKey(item) === bomSelectedItemKey) || filteredItems[0] || null;
    const selectedKey = getBomItemKey(selectedItem);
    const selectedIndex = selectedKey
      ? bomItems.findIndex((candidate) => getBomItemKey(candidate) === selectedKey)
      : -1;
    const listRows = (() => {
      if (!groupedProperty) {
        return filteredItems.map((item) => ({ type: 'item', item }));
      }
      const groups = new Map();
      filteredItems.forEach((item) => {
        const value = (item?.[groupedProperty] || '').toString().trim() || (groupedProperty === 'module' ? 'Unassigned module' : 'Uncategorized');
        if (!groups.has(value)) groups.set(value, []);
        groups.get(value).push(item);
      });
      const rows = [];
      groups.forEach((items, groupValue) => {
        rows.push({ type: 'group', groupValue, count: items.length });
        items.forEach((item) => rows.push({ type: 'item', item }));
      });
      return rows;
    })();

    function getBomItemCommentMeta(item) {
      const stepComments = guideComments.filter((comment) => {
        if ((comment?.scope || '').toString() !== 'step') return false;
        if (item?.stepId && comment?.stepId) return comment.stepId === item.stepId;
        return Number(comment?.stepPosition) === Number(item?.position);
      });
      return {
        count: stepComments.length,
        pendingCount: stepComments.filter((comment) => comment?.status === 'pending').length
      };
    }

    function getBomItemNoteMeta(item) {
      const notes = getNotesForStep(item);
      return {
        count: notes.length,
        todoCount: notes.filter((note) => note?.isTodo && note?.todoStatus !== 'done').length
      };
    }

    function handleSelectedItemDelete() {
      if (!isEditable() || selectedIndex < 0) return;
      if (!window.confirm('Delete this BOM item?')) return;
      const fallback = bomItems[selectedIndex + 1] || bomItems[selectedIndex - 1] || null;
      setBomSelectedItemKey(getBomItemKey(fallback));
      removeBomItem(selectedIndex);
    }

    function getExternalLinkDomain(url) {
      const value = (url || '').toString().trim();
      if (!value) return '';
      try {
        return new URL(value).hostname.replace(/^www\./i, '') || 'External site';
      } catch (error) {
        return 'External link';
      }
    }

    function getDetailValue(value) {
      const text = (value || '').toString().trim();
      return text || <span className="bom-muted-value">Not set</span>;
    }

    function getBomListMetaParts(item, versions) {
      return [
        (item?.quantity || '').toString().trim(),
        formatBomLabel(item?.category),
        formatBomLabel(item?.module),
        stringifyCommaValues(versions)
      ].filter(Boolean);
    }

    function getBomLinkActions(itemKey, manualEntries, autoEntries) {
      const manualActions = manualEntries.map((entry, entryIndex) => {
        const domain = getExternalLinkDomain(entry.url);
        return {
          key: `${itemKey}-manual-${entry.url}-${entryIndex}`,
          href: entry.url,
          title: formatBomLabel(entry.type) || domain,
          meta: domain,
          note: entry?.type ? 'Saved link' : 'Saved source'
        };
      });
      const generatedActions = autoEntries.map((entry) => {
        const domain = getExternalLinkDomain(entry.url);
        return {
          key: `${itemKey}-generated-${entry.provider}-${entry.url}`,
          href: entry.url,
          title: entry.label || domain,
          meta: domain,
          note: 'Auto-generated search'
        };
      });
      return [...manualActions, ...generatedActions];
    }

    function renderQuickAdd() {
      if (!isEditable()) return null;
      return (
        <section className="bom-quick-add-panel" aria-label="Quick add BOM item">
          <div className="bom-quick-add-main">
            <input
              className="form-control form-control-sm"
              placeholder="Name *"
              value={bomQuickItem.name}
              onChange={(e) => handleBomQuickItemChange('name', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBomItem();
                }
              }}
            />
            <input
              className="form-control form-control-sm"
              placeholder="Qty"
              value={bomQuickItem.quantity}
              onChange={(e) => handleBomQuickItemChange('quantity', e.target.value)}
            />
            <input
              list="bom-category-options"
              className="form-control form-control-sm"
              placeholder="Category"
              value={bomQuickItem.category}
              onChange={(e) => handleBomQuickItemChange('category', e.target.value)}
            />
            <input
              list="bom-module-options"
              className="form-control form-control-sm"
              placeholder="Module"
              value={bomQuickItem.module}
              onChange={(e) => handleBomQuickItemChange('module', e.target.value)}
            />
            <button type="button" className="btn btn-sm btn-dark" onClick={addBomItem}>
              Add item
            </button>
          </div>
          <details className="bom-quick-add-more">
            <summary>More fields</summary>
            <div className="bom-quick-add-more-grid">
              <input
                className="form-control form-control-sm"
                placeholder="Identifier"
                value={bomQuickItem.identifier}
                onChange={(e) => handleBomQuickItemChange('identifier', e.target.value)}
              />
              <input
                className="form-control form-control-sm"
                placeholder="Versions (v1, v2)"
                value={bomQuickItem.versions}
                onChange={(e) => handleBomQuickItemChange('versions', e.target.value)}
              />
              <textarea
                className="form-control form-control-sm"
                rows={3}
                placeholder="Description (markdown)"
                value={bomQuickItem.description}
                onChange={(e) => handleBomQuickItemChange('description', e.target.value)}
              />
              <div className="bom-quick-url-list">
                {quickUrlEntries.map((entry, urlIndex) => (
                  <div className="bom-url-edit-row" key={`quick-url-edit-${urlIndex}`}>
                    <input
                      list="bom-url-type-options"
                      className="form-control form-control-sm"
                      value={entry?.type || ''}
                      onChange={(e) => updateBomQuickUrlEntry(urlIndex, 'type', e.target.value)}
                      placeholder="Type"
                    />
                    <input
                      className="form-control form-control-sm"
                      value={(entry?.url || '').toString()}
                      onChange={(e) => updateBomQuickUrlEntry(urlIndex, 'url', e.target.value)}
                      placeholder="https://example.com/part"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeBomQuickUrlEntry(urlIndex)}
                      aria-label="Remove quick URL"
                      title="Remove URL"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-dark" onClick={addBomQuickUrlEntry}>
                  Add URL
                </button>
              </div>
            </div>
          </details>
        </section>
      );
    }

    function renderItemInspector() {
      if (!selectedItem || selectedIndex < 0) {
        return (
          <aside className="bom-item-inspector" aria-label="BOM item details">
            <div className="bom-inspector-empty">
              <div className="bom-inspector-empty-title">No item selected</div>
              <div className="text-muted small">
                {bomItems.length ? 'Choose an item from the list.' : 'This BOM has no items yet.'}
              </div>
              {isEditable() && bomItems.length === 0 ? (
                <button type="button" className="btn btn-sm btn-outline-dark mt-3" onClick={openBomImportModal}>
                  Import your BOM
                </button>
              ) : null}
            </div>
          </aside>
        );
      }

      const itemKey = getBomItemKey(selectedItem);
      const identifierValue = (selectedItem?.identifier || '').toString();
      const identifierInvalid = !!identifierValue && !BOM_IDENTIFIER_PATTERN.test(identifierValue);
      const images = getBomItemImages(selectedItem);
      const itemVersions = Array.isArray(selectedItem?.versions)
        ? selectedItem.versions.map((version) => (version || '').toString().trim()).filter(Boolean)
        : [];
      const editableManualUrlEntries = sanitizeBomManualUrlEntriesClient(selectedItem?.urls, { includeEmpty: true, dropInvalid: false });
      const manualUrlEntries = sanitizeBomManualUrlEntriesClient(selectedItem?.urls, { includeEmpty: false, dropInvalid: true });
      const generatedUrls = getBomItemGeneratedUrls(selectedItem);
      const linkActions = getBomLinkActions(itemKey, manualUrlEntries, generatedUrls);
      const autoUrlProviders = sanitizeBomAutoUrlProvidersClient(selectedItem?.autoUrlProviders);
      const commentMeta = getBomItemCommentMeta(selectedItem);
      const noteMeta = getBomItemNoteMeta(selectedItem);
      const stepKey = getStepKey(selectedItem);
      const inspectorNotesOpen = openNotesStepKey === stepKey;

      return (
        <aside className="bom-item-inspector" aria-label="BOM item details">
          <div className="bom-inspector-header">
            <div>
              <div className="bom-inspector-kicker">Item {selectedItem.position || selectedIndex + 1}</div>
              <h4>{selectedItem?.name || selectedItem?.title || 'Untitled item'}</h4>
              <code>{identifierValue || 'no_identifier'}</code>
            </div>
            {isEditable() ? (
              <div className="bom-inspector-actions" role="toolbar" aria-label="Selected item actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark editor-action-btn"
                  disabled={isReorderDisabled || selectedIndex <= 0}
                  onClick={() => moveBomItem(selectedIndex, selectedIndex - 1)}
                  title="Move item up"
                  aria-label="Move item up"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark editor-action-btn"
                  disabled={isReorderDisabled || selectedIndex >= bomItems.length - 1}
                  onClick={() => moveBomItem(selectedIndex, selectedIndex + 1)}
                  title="Move item down"
                  aria-label="Move item down"
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger editor-action-btn"
                  onClick={handleSelectedItemDelete}
                  title="Delete item"
                  aria-label="Delete item"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ) : null}
          </div>

          {isReorderDisabled && isEditable() ? (
            <div className="alert alert-info py-2 small">
              Reordering is disabled while search, version filtering, or grouping is active.
            </div>
          ) : null}

          <section className="bom-inspector-section">
            <h5>Details</h5>
            {isEditable() ? (
              <div className="bom-detail-form">
                <label>
                  <span>Name</span>
                  <input
                    className="form-control form-control-sm"
                    value={selectedItem?.name || selectedItem?.title || ''}
                    onChange={(e) => updateBomItemField(selectedIndex, 'name', e.target.value)}
                  />
                </label>
                <label>
                  <span>Identifier</span>
                  <input
                    className={`form-control form-control-sm ${identifierInvalid ? 'is-invalid' : ''}`}
                    value={identifierValue}
                    onChange={(e) => updateBomItemField(selectedIndex, 'identifier', e.target.value)}
                  />
                  {identifierInvalid ? (
                    <div className="invalid-feedback d-block">
                      Use lowercase letters, numbers, underscores, hyphens, and dots.
                    </div>
                  ) : null}
                </label>
                <div className="bom-detail-form-grid">
                  <label>
                    <span>Quantity</span>
                    <input
                      className="form-control form-control-sm"
                      value={selectedItem?.quantity || ''}
                      onChange={(e) => updateBomItemField(selectedIndex, 'quantity', e.target.value)}
                    />
                  </label>
                  <label>
                    <span>Category</span>
                    <input
                      list="bom-category-options"
                      className="form-control form-control-sm"
                      value={selectedItem?.category || ''}
                      onChange={(e) => updateBomItemField(selectedIndex, 'category', e.target.value)}
                    />
                  </label>
                  <label>
                    <span>Module</span>
                    <input
                      list="bom-module-options"
                      className="form-control form-control-sm"
                      value={selectedItem?.module || ''}
                      onChange={(e) => updateBomItemField(selectedIndex, 'module', e.target.value)}
                    />
                  </label>
                  <label>
                    <span>Versions</span>
                    <input
                      className="form-control form-control-sm"
                      value={selectedItem?.versionsInput ?? stringifyCommaValues(selectedItem?.versions)}
                      onChange={(e) => updateBomItemField(selectedIndex, 'versions', e.target.value)}
                      placeholder="v1, v2"
                    />
                  </label>
                </div>
              </div>
            ) : (
              <dl className="bom-detail-list">
                <div><dt>Quantity</dt><dd>{getDetailValue(selectedItem?.quantity)}</dd></div>
                <div><dt>Category</dt><dd>{getDetailValue(formatBomLabel(selectedItem?.category))}</dd></div>
                <div><dt>Module</dt><dd>{getDetailValue(formatBomLabel(selectedItem?.module))}</dd></div>
                <div><dt>Versions</dt><dd>{getDetailValue(stringifyCommaValues(itemVersions))}</dd></div>
              </dl>
            )}
          </section>

          <section className="bom-inspector-section">
            <h5>Description</h5>
            {isEditable() ? (
              <BomMarkdownTextarea
                className="form-control form-control-sm"
                rows={5}
                value={selectedItem?.description ?? selectedItem?.content ?? ''}
                onValueChange={(nextValue) => updateBomItemField(selectedIndex, 'description', nextValue)}
                style={{ paddingRight: 36 }}
                wrapperClassName="editable-textarea-wrapper"
                bomAutocompleteCatalog={bomReferenceCatalog}
                guideAutocompleteCatalog={guideReferenceCatalog}
                onCreateBomItem={createBomItemFromAutocomplete}
              >
                <button
                  type="button"
                  className="markdown-help-trigger"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => setShowMarkdownHelp(true)}
                  aria-label="Markdown help"
                >
                  <FontAwesomeIcon icon={faCircleQuestion} />
                </button>
              </BomMarkdownTextarea>
            ) : (
              <div className="bom-item-description-content">
                {selectedItem?.description || selectedItem?.content ? (
                  <MyMarkdownComponent content={selectedItem.description || selectedItem.content}/>
                ) : (
                  <span className="text-muted">No description.</span>
                )}
              </div>
            )}
          </section>

          <section className="bom-inspector-section">
            <h5>Links</h5>
            {linkActions.length ? (
              <div className="bom-link-actions">
                {linkActions.map((entry) => (
                  <a
                    key={entry.key}
                    className="bom-link-action"
                    href={entry.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="bom-link-action-copy">
                      <span className="bom-link-action-title">{entry.title}</span>
                      <span className="bom-link-action-meta">{entry.meta} · {entry.note}</span>
                    </span>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
                  </a>
                ))}
              </div>
            ) : (
              !isEditable() ? <div className="small text-muted">No links.</div> : null
            )}
            {isEditable() ? (
              <details className="bom-url-editor" open={!linkActions.length}>
                <summary>Manage links</summary>
                <div className="bom-url-list">
                  {editableManualUrlEntries.length ? (
                    editableManualUrlEntries.map((entry, urlIndex) => (
                      <div className="bom-url-edit-row" key={`${itemKey}-manual-url-edit-${urlIndex}`}>
                        <input
                          list="bom-url-type-options"
                          className="form-control form-control-sm"
                          value={entry?.type || ''}
                          onChange={(e) => updateBomItemUrlEntry(selectedIndex, urlIndex, 'type', e.target.value)}
                          placeholder="Type"
                        />
                        <input
                          className="form-control form-control-sm"
                          value={(entry?.url || '').toString()}
                          onChange={(e) => updateBomItemUrlEntry(selectedIndex, urlIndex, 'url', e.target.value)}
                          placeholder="https://example.com/part"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeBomItemUrlEntry(selectedIndex, urlIndex)}
                          aria-label="Remove URL"
                          title="Remove URL"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="small text-muted">No manual URLs yet.</div>
                  )}
                </div>
                <button type="button" className="btn btn-sm btn-outline-dark mt-2" onClick={() => addBomItemUrlEntry(selectedIndex)}>
                  Add URL
                </button>
                <div className="bom-provider-options">
                  {BOM_AUTO_URL_PROVIDERS.map((provider) => (
                    <label key={`${itemKey}-provider-${provider.key}`} className="form-check-label small">
                      <input
                        className="form-check-input me-1"
                        type="checkbox"
                        checked={autoUrlProviders.includes(provider.key)}
                        onChange={(e) => {
                          const nextProviders = e.target.checked
                            ? [...autoUrlProviders, provider.key]
                            : autoUrlProviders.filter((key) => key !== provider.key);
                          updateBomItemField(selectedIndex, 'autoUrlProviders', nextProviders);
                        }}
                      />
                      {provider.label}
                    </label>
                  ))}
                </div>
              </details>
            ) : null}
          </section>

          <section className="bom-inspector-section">
            <h5>Images</h5>
            {images.length ? (
              <div className="bom-inspector-images">
                {images.map((image) => (
                  <div key={`${itemKey}-image-${image.url}`} className="bom-inspector-image-tile">
                    <button
                      type="button"
                      className="bom-item-image-button"
                      onClick={() => openImageLightbox(selectedItem, selectedIndex, image.url)}
                      aria-label={`Expand image for ${selectedItem?.name || selectedItem?.title || 'item'}`}
                    >
                      <LazyImage
                        src={image.url}
                        alt={`${selectedItem?.name || selectedItem?.title || 'Item'} image`}
                      />
                    </button>
                    {isEditable() ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeBomItemImage(selectedIndex, image.url)}
                        aria-label="Remove image"
                        title="Remove image"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="small text-muted">No images.</div>
            )}
            {isEditable() ? (
              <label className="btn btn-sm btn-outline-dark mt-2">
                Upload images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => uploadBomItemImages(e, selectedIndex)}
                />
              </label>
            ) : null}
          </section>

          {isEditable() ? (
            <section className="bom-inspector-section">
              <button
                type="button"
                className={`btn btn-sm ${inspectorNotesOpen ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => {
                  if (!stepKey) return;
                  setTodoFocusNoteId(null);
                  setOpenNotesStepKey((prev) => (prev === stepKey ? null : stepKey));
                }}
              >
                <FontAwesomeIcon icon={faListCheck} className="me-1" />
                Notes ({noteMeta.count})
                {noteMeta.todoCount > 0 ? `, ${noteMeta.todoCount} TODO` : ''}
              </button>
              {inspectorNotesOpen ? (
                <GuideNotes
                  notes={guideNotes}
                  stepPosition={selectedItem.position}
                  stepId={selectedItem.stepId}
                  canManage={canManageNotes}
                  projectSlug={data.project.slugURL}
                  guideSlug={data.guide.slugURL}
                  onNoteCreated={handleNoteCreated}
                  onNoteUpdated={handleNoteUpdated}
                  onNoteDeleted={handleNoteDeleted}
                  mode="inline"
                  focusNoteId={todoFocusNoteId}
                  onFocusHandled={() => setTodoFocusNoteId(null)}
                />
              ) : null}
            </section>
          ) : null}

          <section className="bom-inspector-section">
            <h5>Comments ({commentMeta.count})</h5>
            <GuideComments
              scope="step"
              stepPosition={selectedItem.position}
              stepId={selectedItem.stepId}
              comments={guideComments}
              isAuthenticated={isAuthenticated}
              canModerate={canModerateComments}
              currentUsername={userInfo?.username}
              projectSlug={data.project.slugURL}
              guideSlug={data.guide.slugURL}
              onAuthNavigate={handleCommentAuthNavigate}
              onCommentCreated={handleCommentCreated}
              onCommentApproved={handleCommentApproved}
              onCommentDeleted={handleCommentDeleted}
              autoOpen={commentAutoOpen?.scope === 'step' && Number(commentAutoOpen?.stepPosition) === Number(selectedItem.position) ? commentAutoOpen : null}
              openAllSignal={openAllCommentsSignal}
            />
          </section>
        </aside>
      );
    }

    return (
      <>
        <div className="bom-workspace">
          <section className="bom-overview-panel" aria-label="BOM overview">
            <div className="bom-overview-body">
              <div className="bom-overview-media">
                <EditableImage
                  editable={isEditable()}
                  name="bomMainImage"
                  value={data.guide.mainImage}
                  index={0}
                  onValueChange={updateState}
                  path={'guide.mainImage'}
                  multiple={false}
                  uploadURL={`/api/v1/projects/${project}/guides/${guide}/upload`}
                  containerStyle={{height: '100%', minHeight: 0}}
                  contentStyle={{width: '100%', height: '100%', maxHeight: 'none', objectFit: 'cover'}}
                />
              </div>
              <div className="bom-overview-main">
                <div className="bom-overview-summary">
                  <div className="bom-overview-stats" aria-label="BOM summary">
                    <span>
                      <strong>{isAuthenticated ? `${ownedCount}/${bomItems.length}` : bomItems.length}</strong>
                      <small>{isAuthenticated ? `You have ${ownedCount} of ${bomItems.length} items` : 'Items'}</small>
                    </span>
                    <span><strong>{categoryCount}</strong><small>Categories</small></span>
                    <span><strong>{moduleCount}</strong><small>Modules</small></span>
                  </div>
                  <div className="bom-overview-export">
                    <div className="bom-overview-export-label">Export</div>
                    <div className="bom-overview-export-actions" role="toolbar" aria-label="BOM export actions">
                      <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => handleExportClick()}>
                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                        PDF
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => handleCsvExportClick()}>
                        <FontAwesomeIcon icon={faFileCsv} className="me-1" />
                        CSV
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark"
                        style={{opacity: stepwisedocsExportLoading ? 0.7 : 1}}
                        disabled={stepwisedocsExportLoading}
                        onClick={() => {
                          if (!stepwisedocsExportLoading) {
                            handleStepwisedocsExportClick();
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faFileCode} className="me-1" />
                        {stepwisedocsExportLoading ? 'JSON…' : 'JSON'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="bom-overview-meta">
                  {(isEditable() || (Array.isArray(data?.guide?.tags) && data.guide.tags.length > 0)) ? (
                    <div className="bom-overview-meta-row">
                      <FontAwesomeIcon icon={faTag} />
                      <EditableTags
                        editable={isEditable()}
                        name="tags"
                        editStyle={{}}
                        value={Array.isArray(data?.guide?.tags) ? data.guide.tags : []}
                        index={0}
                        onValueChange={updateState}
                        path={'guide.tags'}
                        linkPrefix={`${config.prefixPath}/projects/${data.project.slugURL}`}
                      />
                    </div>
                  ) : null}
                  <div className="bom-overview-meta-row">
                    <FontAwesomeIcon icon={faPalette} />
                    <EditableLicense
                      editable={isEditable()}
                      name="license"
                      contentStyle={{}}
                      editStyle={{}}
                      value={data.guide.license}
                      index={0}
                      onValueChange={updateState}
                      path={'guide.license'}
                    />
                  </div>
                </div>
              </div>
            </div>
            {(isEditable() || data.guide.description) ? (
              <div className="bom-overview-description-row">
                <div className="bom-overview-kicker">Description</div>
                <div className="bom-overview-description">
                  <EditableTextArea
                    editable={isEditable()}
                    name="bomDescription"
                    editStyle={{width: '100%', minHeight: 120}}
                    value={data.guide.description}
                    index={0}
                    onValueChange={updateState}
                    path={'guide.description'}
                    markdownComponents={{ a: MarkdownLink }}
                    markdownRemarkPlugins={markdownRemarkPlugins}
                    bomAutocompleteCatalog={bomReferenceCatalog}
                    guideAutocompleteCatalog={guideReferenceCatalog}
                    onCreateBomItem={createBomItemFromAutocomplete}
                  />
                </div>
              </div>
            ) : null}
            <div className="bom-overview-footer">
              <div className="bom-overview-comments">
                <GuideComments
                  scope="guide"
                  comments={guideComments}
                  isAuthenticated={isAuthenticated}
                  canModerate={canModerateComments}
                  currentUsername={userInfo?.username}
                  projectSlug={data.project.slugURL}
                  guideSlug={data.guide.slugURL}
                  onAuthNavigate={handleCommentAuthNavigate}
                  onCommentCreated={handleCommentCreated}
                  onCommentApproved={handleCommentApproved}
                  onCommentDeleted={handleCommentDeleted}
                  autoOpen={commentAutoOpen?.scope === 'guide' ? commentAutoOpen : null}
                  openAllSignal={openAllCommentsSignal}
                  containerId="guide-comments"
                />
              </div>
            </div>
          </section>

          {renderQuickAdd()}

          <section className="bom-board">
            <div className="bom-list-panel">
              <div className="bom-list-toolbar">
                <div>
                  <h4>Items</h4>
                  <div className="small text-muted">
                    Showing {filteredItems.length} of {bomItems.length}
                  </div>
                </div>
                <div className="bom-search-control">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    value={bomSearchTerm}
                    onChange={(e) => setBomSearchTerm(e.target.value)}
                    placeholder="Search items"
                    aria-label="Search BOM items"
                  />
                </div>
              </div>

              <div className="bom-filter-strip" aria-label="BOM filters">
                <button
                  type="button"
                  className={`btn btn-sm ${groupedByCategory ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setBomViewFromControl(groupedByCategory ? 'none' : 'category')}
                >
                  Category
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${groupedByModule ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setBomViewFromControl(groupedByModule ? 'none' : 'module')}
                >
                  Module
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${activeVersionFilter ? 'btn-outline-dark' : 'btn-dark'}`}
                  onClick={() => setBomVersionFilterFromControl('')}
                >
                  All versions
                </button>
                {versionOptions.map((version) => (
                  <button
                    key={version}
                    type="button"
                    className={`btn btn-sm ${activeVersionFilter.toLowerCase() === version.toLowerCase() ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => setBomVersionFilterFromControl(version)}
                  >
                    {version}
                  </button>
                ))}
                <span className="bom-filter-separator" aria-hidden="true" />
                <button
                  type="button"
                  className={`btn btn-sm ${bomOwnershipFilter === BOM_OWNERSHIP_FILTER.ALL ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setBomOwnershipFilter(BOM_OWNERSHIP_FILTER.ALL)}
                >
                  All items
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${bomOwnershipFilter === BOM_OWNERSHIP_FILTER.OWNED ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setBomOwnershipFilter(BOM_OWNERSHIP_FILTER.OWNED)}
                >
                  Owned
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${bomOwnershipFilter === BOM_OWNERSHIP_FILTER.MISSING ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setBomOwnershipFilter(BOM_OWNERSHIP_FILTER.MISSING)}
                >
                  Need
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${bomNotesOnlyFilter ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setBomNotesOnlyFilter((current) => !current)}
                >
                  With my notes
                </button>
              </div>

              <div className="bom-list" role="list" aria-label="BOM items">
                {filteredItems.length === 0 ? (
                  <div className="bom-list-empty">
                    <div>No items found for the selected view.</div>
                    {isEditable() && bomItems.length === 0 ? (
                      <button type="button" className="btn btn-sm btn-outline-dark mt-2" onClick={openBomImportModal}>
                        Import your BOM
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {listRows.map((row, rowIndex) => {
                  if (row.type === 'group') {
                    const groupLabel = groupedByModule ? 'Module' : 'Category';
                    return (
                      <div
                        key={`group-${row.groupValue}-${rowIndex}`}
                        id={`bom-group-${groupedProperty}-${buildBomGroupAnchorToken(row.groupValue)}`}
                        className={normalizeBomReferenceKey(bomFocusedGroup) === normalizeBomReferenceKey(row.groupValue) ? 'bom-list-group is-focused' : 'bom-list-group'}
                      >
                        <span>{groupLabel}: {formatBomLabel(row.groupValue)}</span>
                        <strong>{row.count}</strong>
                      </div>
                    );
                  }

                  const item = row.item;
                  const itemKey = getBomItemKey(item);
                  const resolvedIndex = bomItems.findIndex((candidate) => getBomItemKey(candidate) === itemKey);
                  if (resolvedIndex < 0) return null;
                  const rowAnchorId = `bom-item-${itemKey}`;
                  const images = getBomItemImages(item);
                  const imageUrl = images[0]?.url || '';
                  const itemVersions = Array.isArray(item?.versions)
                    ? item.versions.map((version) => (version || '').toString().trim()).filter(Boolean)
                    : [];
                  const metaParts = getBomListMetaParts(item, itemVersions);
                  const commentMeta = getBomItemCommentMeta(item);
                  const noteMeta = getBomItemNoteMeta(item);
                  const tracking = getBomTrackingForItem(item);
                  const isOwned = tracking?.owned === true;
                  const hasMyNote = tracking?.hasNote === true;
                  const isTrackingSaving = bomTrackingSavingKey === itemKey;
                  const isMyNoteEditorOpen = bomNoteEditor.itemKey === itemKey;
                  const isSelected = itemKey === selectedKey;

                  return (
                    <div
                      key={itemKey}
                      id={rowAnchorId}
                      className={`bom-list-item${isSelected ? ' is-selected' : ''}`}
                      data-bom-item-anchor={itemKey}
                      role="listitem"
                      aria-current={isSelected ? 'true' : undefined}
                    >
                      <button
                        type="button"
                        className="bom-list-item-select"
                        onClick={() => selectBomItem(item)}
                      >
                        <span className="bom-list-item-index">{item.position}</span>
                        <span className="bom-list-item-main">
                          <span className="bom-list-item-title">{item?.name || item?.title || 'Untitled item'}</span>
                          {item?.identifier ? <code>{item.identifier}</code> : null}
                          {metaParts.length ? (
                            <span className="bom-list-item-meta">
                              {metaParts.map((metaPart, metaIndex) => <span key={`${metaPart}-${metaIndex}`}>{metaPart}</span>)}
                            </span>
                          ) : null}
                        </span>
                      </button>
                      <div className="bom-list-item-signals">
                        <span className="bom-my-note-slot">
                          {hasMyNote ? (
                            <button
                              type="button"
                              className="bom-list-badge is-my-note"
                              onClick={() => openBomMyNote(item)}
                              aria-label="Open my note"
                              title="Open my note"
                            >
                              <FontAwesomeIcon icon={faNoteSticky} />
                            </button>
                          ) : null}
                        </span>
                        <span className={`bom-tracking-control${isOwned ? ' is-owned' : ''}`}>
                          <button
                            type="button"
                            className="bom-owned-toggle"
                            onClick={() => handleBomOwnedToggle(item)}
                            disabled={isTrackingSaving}
                            aria-pressed={isOwned}
                            aria-label={isOwned ? 'Mark item as still needed' : 'Mark item as owned'}
                            title={isOwned ? 'You have this item' : 'Still need this item'}
                          >
                            <FontAwesomeIcon icon={isOwned ? faSquareCheck : faSquare} />
                            <span>{isOwned ? 'Have' : 'Need'}</span>
                          </button>
                          <details className="bom-tracking-details">
                            <summary aria-label="Item tracking options" title="Item tracking options">
                              <FontAwesomeIcon icon={faChevronDown} />
                            </summary>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.currentTarget.closest('details')?.removeAttribute('open');
                                openBomMyNote(item);
                              }}
                            >
                              My note
                            </button>
                          </details>
                        </span>
                        {imageUrl ? (
                          <button
                            type="button"
                            className="bom-list-thumb bom-list-thumb-button"
                            onClick={() => {
                              selectBomItem(item);
                              openImageLightbox(item, resolvedIndex, imageUrl);
                            }}
                            aria-label={`Open images for ${item?.name || item?.title || 'item'}`}
                            title="Open item images"
                          >
                            <LazyImage src={imageUrl} alt={`${item?.name || item?.title || 'Item'} image`} />
                          </button>
                        ) : (
                          <span className="bom-list-thumb is-empty" aria-label="No image">
                            <FontAwesomeIcon icon={faImage} />
                          </span>
                        )}
                        {commentMeta.count > 0 ? (
                          <span className={`bom-list-badge${commentMeta.pendingCount > 0 ? ' is-pending' : ''}`}>
                            <FontAwesomeIcon icon={faComments} />
                            {commentMeta.count}
                          </span>
                        ) : null}
                        {noteMeta.todoCount > 0 ? (
                          <span className="bom-list-badge is-todo">
                            <FontAwesomeIcon icon={faListCheck} />
                            {noteMeta.todoCount}
                          </span>
                        ) : null}
                        {isMyNoteEditorOpen ? (
                          <div className="bom-my-note-popover" role="dialog" aria-label="My BOM item note">
                            <div className="bom-my-note-header">
                              <strong>My note</strong>
                              <button
                                type="button"
                                className="bom-my-note-close"
                                onClick={() => setBomNoteEditor({ itemKey: '', text: '', error: '' })}
                                aria-label="Close my note"
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                            </div>
                            <textarea
                              className="form-control form-control-sm"
                              rows={4}
                              maxLength={noteMaxLength}
                              value={bomNoteEditor.text}
                              onChange={(event) => setBomNoteEditor((prev) => ({
                                ...prev,
                                text: event.target.value,
                                error: ''
                              }))}
                              placeholder="Add a private note for this item"
                            />
                            <div className="bom-my-note-footer">
                              <span className={bomNoteEditor.text.length >= noteMaxLength ? 'text-danger' : ''}>
                                {bomNoteEditor.text.length}/{noteMaxLength}
                              </span>
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-dark"
                                  onClick={() => setBomNoteEditor({ itemKey: '', text: '', error: '' })}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-dark ms-2"
                                  disabled={isTrackingSaving}
                                  onClick={() => saveBomMyNote(item)}
                                >
                                  {isTrackingSaving ? 'Saving…' : 'Save'}
                                </button>
                              </div>
                            </div>
                            {bomNoteEditor.error ? (
                              <div className="bom-my-note-error">{bomNoteEditor.error}</div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {renderItemInspector()}
          </section>

          <datalist id="bom-category-options">
            {categorySuggestions.map((category) => (
              <option key={category} value={formatBomLabel(category)} />
            ))}
          </datalist>
          <datalist id="bom-module-options">
            {moduleSuggestions.map((moduleName) => (
              <option key={moduleName} value={formatBomLabel(moduleName)} />
            ))}
          </datalist>
          <datalist id="bom-url-type-options">
            {urlTypeSuggestions.map((urlType) => (
              <option key={urlType} value={formatBomLabel(urlType)} />
            ))}
          </datalist>
        </div>

        <GuidePagination data={data}/>
        {renderPublishValidationModal()}
        {renderExportModal()}
        {renderMarkdownExportModal()}
        {renderCsvExportModal()}
        {renderBomImportModal()}
      </>
    );
  }

  function renderExportModal() {
    return (
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Download {entityLabel} as PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              id="export-include-project-description"
              label="Include project description"
              checked={exportOptions.includeProjectDescription}
              onChange={(e) => setExportOptions(prev => ({ ...prev, includeProjectDescription: e.target.checked }))}
            />
            <Form.Check
              type="checkbox"
              id="export-include-index"
              label={isBomGuide ? 'Include item index' : 'Include guide index'}
              checked={exportOptions.includeIndex}
              onChange={(e) => setExportOptions(prev => ({ ...prev, includeIndex: e.target.checked }))}
              className="mt-2"
            />
            <Form.Group className="mt-3">
              <Form.Label>Images</Form.Label>
              <Form.Select
                value={exportOptions.images}
                onChange={(e) => setExportOptions(prev => ({ ...prev, images: e.target.value }))}
              >
                <option value="big">Big</option>
                <option value="medium">Medium</option>
                <option value="small">Small</option>
                <option value="none">No images</option>
              </Form.Select>
            </Form.Group>
            {exportError ? (
              <div className="alert alert-danger mt-3" role="alert">
                {exportError}
              </div>
            ) : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-dark" onClick={() => setShowExportModal(false)} disabled={exportLoading}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={downloadGuidePdf} disabled={exportLoading}>
            {exportLoading ? 'Preparing PDF…' : `Download ${entityLabel}`}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  function renderPublishValidationModal() {
    return (
      <Modal
        show={showPublishValidationModal}
        onHide={closePublishValidationModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Guide publishing requires complete steps</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {publishValidationMessage || 'This guide cannot be published until every step has both text and at least one image.'}
          </p>
          <p className="text-muted">
            The goal is to encourage publication of complete guides rather than works in progress.
          </p>
          {publishValidationSteps.length ? (
            <>
              <div className="fw-semibold mt-3 mb-2">Incomplete steps</div>
              <ul className="mb-0">
                {publishValidationSteps.map((step) => {
                  const missingParts = [];
                  if (step?.missingText) missingParts.push('text');
                  if (step?.missingImages) missingParts.push('an image');
                  return (
                    <li key={`${step?.position || 0}-${step?.title || ''}`}>
                      Step {step?.position || '?'}{step?.title ? `: ${step.title}` : ''} is missing {missingParts.join(' and ')}.
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-dark" onClick={closePublishValidationModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  function renderMarkdownExportModal() {
    if (!canManageGuide) return null;
    return (
      <Modal
        show={showMarkdownExportModal}
        onHide={() => setShowMarkdownExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Download {entityLabel} as Markdown ZIP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              id="markdown-export-include-project-description"
              label="Include project description"
              checked={markdownExportOptions.includeProjectDescription}
              onChange={(e) => setMarkdownExportOptions((prev) => ({ ...prev, includeProjectDescription: e.target.checked }))}
            />
            <Form.Check
              type="checkbox"
              id="markdown-export-include-index"
              label={isBomGuide ? 'Include item index' : 'Include guide index'}
              checked={markdownExportOptions.includeIndex}
              onChange={(e) => setMarkdownExportOptions((prev) => ({ ...prev, includeIndex: e.target.checked }))}
              className="mt-2"
            />
            {!isBomGuide ? (
              <Form.Check
                type="checkbox"
                id="markdown-export-include-related-boms"
                label="Include related BOMs"
                checked={markdownExportOptions.includeRelatedBoms}
                onChange={(e) => setMarkdownExportOptions((prev) => ({ ...prev, includeRelatedBoms: e.target.checked }))}
                className="mt-2"
              />
            ) : (
              <div className="small text-muted mt-3">
                Current BOM exports include only this BOM. Related guides and BOMs are not added.
              </div>
            )}
            <Form.Group className="mt-3">
              <Form.Label>Images</Form.Label>
              <Form.Select
                value={normalizeMarkdownExportImageSize(markdownExportOptions.images)}
                onChange={(e) => setMarkdownExportOptions((prev) => ({
                  ...prev,
                  images: normalizeMarkdownExportImageSize(e.target.value)
                }))}
              >
                {MARKDOWN_EXPORT_IMAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Form.Select>
              <div className="small text-muted mt-2">
                Original size keeps the raw Markdown image syntax. Big, medium, and small use HTML image tags for cleaner layout. No images omits image files from the ZIP.
              </div>
            </Form.Group>
            <div className="small text-muted mt-3">
              Managed local upload images selected for export are bundled automatically. External image URLs remain external links.
            </div>
            {hasUnsavedChanges ? (
              <div className="alert alert-warning mt-3 mb-0" role="alert">
                Unsaved changes are not included. Save the {entityLabel.toLowerCase()} first if you want them in the export.
              </div>
            ) : null}
            {markdownExportError ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {markdownExportError}
              </div>
            ) : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-dark" onClick={() => setShowMarkdownExportModal(false)} disabled={markdownExportLoading}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={downloadGuideMarkdown} disabled={markdownExportLoading}>
            {markdownExportLoading ? 'Preparing Markdown ZIP…' : 'Download Markdown ZIP'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  function renderCsvExportModal() {
    if (!isBomGuide) return null;
    const selectedFields = Array.isArray(csvExportOptions.fields) ? csvExportOptions.fields : [];
    const versionOptions = getBomVersionOptions(getBomItems());
    const selectedVersions = normalizeCsvExportVersions(csvExportOptions?.versions, versionOptions);
    const hasVersionFilter = selectedVersions.length > 0;
    const selectedVersionSet = new Set(selectedVersions.map((version) => version.toLowerCase()));
    return (
      <Modal
        show={showCsvExportModal}
        onHide={() => setShowCsvExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Export BOM as CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              id="csv-include-header"
              label="Include header row"
              checked={csvExportOptions.includeHeader}
              onChange={(e) => setCsvExportOptions((prev) => ({ ...prev, includeHeader: e.target.checked }))}
            />
            <div className="mt-3">
              <Form.Label>Fields</Form.Label>
              <div className="d-flex flex-column gap-1">
                {BOM_CSV_EXPORT_FIELDS.map((field) => (
                  <Form.Check
                    key={`csv-field-${field.key}`}
                    type="checkbox"
                    id={`csv-field-${field.key}`}
                    label={field.label}
                    checked={selectedFields.includes(field.key)}
                    onChange={() => toggleCsvField(field.key)}
                    disabled={selectedFields.includes(field.key) && selectedFields.length <= 1}
                  />
                ))}
              </div>
            </div>
            <div className="mt-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0">Versions</Form.Label>
                {versionOptions.length ? (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    onClick={() => setCsvExportOptions((prev) => ({ ...prev, versions: [] }))}
                  >
                    Export all versions
                  </button>
                ) : null}
              </div>
              {versionOptions.length ? (
                <>
                  <div className="small text-muted mb-2">
                    {hasVersionFilter
                      ? `Exporting ${selectedVersions.length} selected version${selectedVersions.length === 1 ? '' : 's'}.`
                      : 'Exporting all versions.'}
                  </div>
                  <div className="d-flex flex-column gap-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {versionOptions.map((version) => {
                      const versionKey = version.toLowerCase();
                      const versionId = versionKey.replace(/[^a-z0-9_-]+/g, '-');
                      const checked = !hasVersionFilter || selectedVersionSet.has(versionKey);
                      return (
                        <Form.Check
                          key={`csv-version-${version}`}
                          type="checkbox"
                          id={`csv-version-${versionId}`}
                          label={version}
                          checked={checked}
                          onChange={() => toggleCsvVersion(version, versionOptions)}
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="small text-muted">No item versions found. All items will be exported.</div>
              )}
            </div>
            {csvExportError ? (
              <div className="alert alert-danger mt-3" role="alert">
                {csvExportError}
              </div>
            ) : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-dark" onClick={() => setShowCsvExportModal(false)} disabled={csvExportLoading}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={downloadBomCsv} disabled={csvExportLoading}>
            {csvExportLoading ? 'Preparing CSV…' : 'Download CSV'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  function renderBomImportModal() {
    if (!isBomGuide) return null;
    const showUploadSection = bomImportMethod === BOM_IMPORT_METHOD.DIRECT_FILE
      || (isBomImportPromptMethod && !!bomImportPrompt);
    const uploadDisabled = bomImportLoading || !bomImportFile || (isBomImportPromptMethod && !bomImportPrompt);
    const promptWizardStep1Done = isBomImportPromptMethod && bomImportPromptGenerated;
    const promptWizardStep2Done = isBomImportPromptMethod && bomImportPromptGenerated;
    const promptWizardStep3Done = isBomImportPromptMethod && bomImportPromptCopied;
    const promptWizardStep4Done = isBomImportPromptMethod && !!bomImportFile;
    const renderPromptWizardStepBadge = (stepNumber, isDone) => (
      <span className={`bom-import-step-indicator${isDone ? ' is-done' : ''}`} aria-hidden="true">
        {isDone ? '✓' : stepNumber}
      </span>
    );
    return (
      <Modal
        show={showBomImportModal}
        onHide={closeBomImportModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Import BOM</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!bomImportMethod ? (
            <>
              <p className="mb-3">
                Choose how you want to import your BOM.
              </p>
              <div className="bom-import-choice-grid">
                {BOM_IMPORT_METHOD_OPTIONS.map((option) => {
                  const isAiAssisted = option.key === BOM_IMPORT_METHOD.EXTERNAL_URL || option.key === BOM_IMPORT_METHOD.RAW_TEXT;
                  return (
                    <button
                      type="button"
                      className="bom-import-choice-card"
                      key={option.key}
                      onClick={() => selectBomImportMethod(option.key)}
                    >
                      <span className="bom-import-choice-title">
                        {option.title}
                        {isAiAssisted ? (
                          <FontAwesomeIcon icon={faWandMagicSparkles} className="bom-import-choice-title-icon" />
                        ) : null}
                      </span>
                      <span className="bom-import-choice-description">{option.description}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
                <div>
                  <div className="small text-muted">Selected method</div>
                  <div className="fw-semibold">{bomImportSelectedMethod?.title || 'Import method'}</div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark"
                  onClick={() => resetBomImportState()}
                  disabled={bomImportLoading}
                >
                  Change method
                </button>
              </div>

              {hasExistingBomItems ? (
                <div className="mb-3">
                  <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
                    {isBomImportPromptMethod ? renderPromptWizardStepBadge(1, promptWizardStep1Done) : null}
                    <span>How should imported items affect the current BOM?</span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {BOM_IMPORT_STRATEGY_OPTIONS.map((option) => (
                      <label key={option.key} className="bom-import-strategy-option">
                        <input
                          className="form-check-input mt-1"
                          type="radio"
                          name="bom-import-strategy"
                          value={option.key}
                          checked={bomImportStrategy === option.key}
                          onChange={(e) => setBomImportStrategy(e.target.value)}
                          disabled={bomImportLoading}
                        />
                        <span>
                          <span className="d-block fw-semibold">{option.title}</span>
                          <span className="small text-muted">{option.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="alert alert-info py-2">
                  {isBomImportPromptMethod ? (
                    <span className="d-inline-flex align-items-center gap-2 me-2">
                      {renderPromptWizardStepBadge(1, promptWizardStep1Done)}
                    </span>
                  ) : null}
                  This BOM has no items yet. The import will create all items from the uploaded file.
                </div>
              )}

              {bomImportMethod === BOM_IMPORT_METHOD.EXTERNAL_URL ? (
                <div className="mb-3">
                  <Form.Label htmlFor="bom-import-source-url" className="fw-semibold d-flex align-items-center gap-2">
                    {isBomImportPromptMethod ? renderPromptWizardStepBadge(2, promptWizardStep2Done) : null}
                    <span>Public source URL</span>
                  </Form.Label>
                  <Form.Control
                    id="bom-import-source-url"
                    type="url"
                    value={bomImportSourceUrl}
                    onChange={(e) => handleBomImportSourceUrlChange(e.target.value)}
                    placeholder="https://github.com/... or https://www.instructables.com/..."
                    disabled={bomImportLoading}
                  />
                  <div className="small text-muted mt-1">
                    Tip: You can use links from GitHub, GitBuilding, Instructables, product pages, or any public site.
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark mt-2"
                    onClick={generateBomImportPrompt}
                    disabled={bomImportLoading}
                  >
                    Generate AI Prompt
                  </button>
                </div>
              ) : null}

              {bomImportMethod === BOM_IMPORT_METHOD.RAW_TEXT ? (
                <div className="mb-3">
                  <Form.Label htmlFor="bom-import-source-text" className="fw-semibold d-flex align-items-center gap-2">
                    {isBomImportPromptMethod ? renderPromptWizardStepBadge(2, promptWizardStep2Done) : null}
                    <span>Paste your current BOM text</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    id="bom-import-source-text"
                    rows={8}
                    value={bomImportSourceText}
                    onChange={(e) => handleBomImportSourceTextChange(e.target.value.slice(0, 60000))}
                    placeholder="Paste your BOM content here (any format is accepted)."
                    disabled={bomImportLoading}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark mt-2"
                    onClick={generateBomImportPrompt}
                    disabled={bomImportLoading}
                  >
                    Generate AI Prompt
                  </button>
                </div>
              ) : null}

              {isBomImportPromptMethod && bomImportPrompt ? (
                <div className="mb-3">
                  <div className="fw-semibold mb-1 d-flex align-items-center gap-2">
                    {renderPromptWizardStepBadge(3, promptWizardStep3Done)}
                    <span>AI prompt</span>
                  </div>
                  <div className="small text-muted mb-2">
                    Copy this prompt, run it with your preferred AI model, and save the model output as a JSON file.
                    {bomImportMethod === BOM_IMPORT_METHOD.EXTERNAL_URL
                      ? ' Ensure your model/session has internet access enabled.'
                      : ''}
                    {' '}We have obtained good results with GPT-5.1.
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={7}
                    readOnly
                    value={bomImportPrompt}
                    className="bom-import-prompt-box"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark mt-2"
                    onClick={copyBomImportPrompt}
                  >
                    Copy to clipboard
                  </button>
                </div>
              ) : null}

              {showUploadSection ? (
                <Form onSubmit={submitBomImport}>
                  <Form.Group controlId="bom-import-file">
                    <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                      {isBomImportPromptMethod ? renderPromptWizardStepBadge(4, promptWizardStep4Done) : null}
                      <span>Upload StepWiseDocs BOM JSON</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".json,application/json"
                      onChange={handleBomImportFileChange}
                      disabled={bomImportLoading}
                    />
                  </Form.Group>
                  {bomImportFile ? (
                    <div className="small text-muted mt-1">
                      Selected file: <strong>{bomImportFile.name}</strong>
                    </div>
                  ) : null}
                  <div className="small text-muted mt-2">
                    Expected schema: <code>{BOM_IMPORT_SCHEMA}</code> (version {BOM_IMPORT_SCHEMA_VERSION}).
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary mt-3"
                    disabled={uploadDisabled}
                  >
                    {bomImportLoading ? 'Importing…' : 'Import BOM'}
                  </button>
                </Form>
              ) : (
                <div className="small text-muted">
                  Generate the AI prompt first. The upload step will appear afterwards.
                </div>
              )}
            </>
          )}

          {bomImportStatus ? (
            <div className="alert alert-info py-2 mt-3 mb-0">{bomImportStatus}</div>
          ) : null}

          {bomImportErrors.length ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">
              <div className="fw-semibold mb-1">Import errors</div>
              <ul className="mb-0 ps-3">
                {bomImportErrors.map((errorDetail, index) => (
                  <li key={`bom-import-error-${index}`}>{errorDetail}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-outline-dark"
            onClick={closeBomImportModal}
            disabled={bomImportLoading}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  const historyCurrentStep = historyMode?.type === 'step'
    ? (Array.isArray(data?.guide?.steps) ? data.guide.steps.find(step => {
      if (!step) return false;
      if (historyMode?.stepId && step.stepId === historyMode.stepId) return true;
      if (historyMode?.stepPosition && Number(step.position) === Number(historyMode.stepPosition)) return true;
      return false;
    }) : null)
    : null;
  const historySelectHandler = historyMode?.type === 'guide' ? selectGuideRevision : selectStepRevision;

  return (
    <>
      {/* Main site header */}
      <HeaderNavbar />

      {historyMode ? (
        <HistoryViewer
          mode={historyMode.type}
          currentGuide={data?.guide}
          currentStep={historyCurrentStep}
          historyEntries={historyEntries}
          selectedRevision={historySelectedData}
          selectedRev={historySelectedRev}
          onSelectRevision={historySelectHandler}
          onRestoreRevision={historyMode.type === 'guide'
            ? restoreGuideRevision
            : (rev) => restoreStepRevision(historyMode.stepId, historyMode.stepPosition, rev, true)}
          onClose={exitHistoryMode}
          loading={historyLoading}
        />
      ) : (
        <>
          {/* Editor toolbar (visible only to users with edit permissions) */}
          {canManageGuide && (
            <nav className="navbar sticky-top navbar-light top-nav editor-top-nav">
              <Container className="d-flex justify-content-end align-items-center flex-nowrap">
                <div className="editor-action-bar ms-auto">
                  <div className="editor-action-strip" role="toolbar" aria-label={`${entityLabel} editor actions`}>
                    {showGuideHistoryAction ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark editor-action-btn"
                        onClick={enterGuideHistory}
                        disabled={historyLoading}
                        aria-label={`${entityLabel} history`}
                        title={`${entityLabel} history`}
                      >
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                    ) : null}
                    {showCommentsAction ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark editor-action-btn"
                        onClick={handleCommentsAction}
                        aria-label="Comments"
                        title={pendingModerationCount > 0 ? 'Review pending comments' : `Open ${entityLabel} comments`}
                      >
                        <FontAwesomeIcon icon={faComments} />
                        {pendingModerationCount > 0 ? (
                          <span className="editor-action-badge">{pendingModerationCount}</span>
                        ) : null}
                      </button>
                    ) : null}
                    {showTodoAction ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark editor-action-btn"
                        onClick={scrollToNextPendingTodo}
                        disabled={pendingTodoCount === 0}
                        aria-label="Pending TODOs"
                        title={pendingTodoCount > 0 ? pendingTodoLabel : 'No pending TODOs'}
                      >
                        <FontAwesomeIcon icon={faListCheck} />
                        {pendingTodoCount > 0 ? (
                          <span className="editor-action-badge todo">{pendingTodoCount}</span>
                        ) : null}
                      </button>
                    ) : null}
                    {showReviewToolbarActions && showEditModeToolbarActions ? (
                      <span className="editor-action-divider" aria-hidden="true" />
                    ) : null}

                    {showEditModeToolbarActions ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark editor-action-btn"
                          onClick={handleMarkdownExportClick}
                          disabled={markdownExportLoading}
                          aria-label={`Export ${entityLabel} as Markdown ZIP`}
                          title={`Export ${entityLabel} as Markdown ZIP`}
                        >
                          <FontAwesomeIcon icon={faFileZipper} />
                        </button>
                        {showBomImportAction ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-dark editor-action-btn"
                            onClick={openBomImportModal}
                            aria-label="Import BOM"
                            title="Import BOM"
                          >
                            <FontAwesomeIcon icon={faFileImport} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark editor-action-btn"
                          onClick={duplicateGuide}
                          aria-label={`Duplicate ${entityLabel}`}
                          title={`Duplicate ${entityLabel}`}
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark editor-action-btn"
                          onClick={togglePublish}
                          aria-label={data.guide.published ? `Unpublish ${entityLabel}` : `Publish ${entityLabel}`}
                          title={data.guide.published ? `Unpublish ${entityLabel}` : `Publish ${entityLabel}`}
                        >
                          <FontAwesomeIcon icon={data.guide.published ? faEye : faEyeSlash} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger editor-action-btn"
                          onClick={deleteGuide}
                          aria-label={`Delete ${entityLabel}`}
                          title={`Delete ${entityLabel}`}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    ) : null}

                    {showEditModeToolbarActions ? (
                      <>
                        <span className="editor-action-divider" aria-hidden="true" />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark editor-action-btn"
                          onClick={() => save()}
                          disabled={loading}
                          aria-label={`Save ${entityLabel}`}
                          title={hasUnsavedChanges ? `Save ${entityLabel}` : `Save ${entityLabel} (no changes yet)`}
                        >
                          {loading ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faFloppyDisk} />}
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm btn-outline-dark editor-action-btn${autoSave ? ' is-active' : ''}`}
                          onClick={() => setAutoSave((prev) => !prev)}
                          aria-pressed={autoSave}
                          aria-label={autoSave ? 'Autosave on' : 'Autosave off'}
                          title={autoSave ? 'Autosave on' : 'Autosave off'}
                        >
                          <FontAwesomeIcon icon={autoSave ? faCloudArrowUp : faCloud} />
                        </button>
                      </>
                    ) : null}

                    {showEditToggleDivider ? (
                      <span className="editor-action-divider" aria-hidden="true" />
                    ) : null}

                    <button
                      type="button"
                      className={`btn btn-sm btn-outline-dark editor-action-btn${editMode ? ' is-active' : ''}`}
                      onClick={handleEditModeToggle}
                      aria-pressed={editMode}
                      aria-label={editMode ? 'Exit edit mode' : 'Enter edit mode'}
                      title={editMode ? 'Exit edit mode' : 'Enter edit mode'}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                  </div>

                  <span className={`editor-status-pill ${isDraft ? 'is-draft' : 'is-published'}`}>
                    {isDraft ? 'Draft' : 'Published'}
                  </span>
                </div>
              </Container>
            </nav>
          )}

      {/* Breadcrumb */}
      <ol className="breadcrumb navi">
        <li className="breadcrumb-item"><a href={`${config.prefixPath}/projects`}>Projects</a></li>
        <li className="breadcrumb-item"><a href={`${config.prefixPath}/projects/${data.project.slugURL}`}>{data.project.title}</a></li>
        <li className="breadcrumb-item active" aria-current="page">{data.guide.title}</li>
      </ol>

      {/* Project title */} 
      <h1 className="guide-page-project-title">
        <a href={`${config.prefixPath}/projects/${data.project.slugURL}`} className="text-reset text-decoration-none">{data.project.title}</a>
        {isBomGuide ? <span className="guide-page-type-pill">BOM</span> : null}
      </h1>
      <h2><a href={`${config.prefixPath}/projects/${data.project.slugURL}`} className="text-reset text-decoration-none">{data.project.subTitle}</a></h2>
      <h3>
        <EditableInput editable={isEditable()} name='titleDescription' editStyle={{}} value={data.guide.title} index={0} onValueChange={updateState} path={'guide.title'}/>
      </h3>

      {/* Guide */}
      <div className="row">

        {/* Left Navigation Menu */}
        <div className={`col-12 col-md-3 guide-nav-container${showGuideIndex ? '' : ' is-collapsed'}`}>
          <div
            id="navid"
            className={`row guide-nav${showGuideIndex ? '' : ' guide-nav-collapsed'}`}
            style={{ top: canManageGuide ? '127px' : '70px' }}
          >
            <ul>
              <li className="guide-nav-project">
                <span className="guide-nav-project-title" title={data.project.title}>{data.project.title}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-link guide-nav-toggle-btn"
                  onClick={() => setShowGuideIndex((prev) => !prev)}
                  aria-pressed={showGuideIndex}
                  aria-label={showGuideIndex ? 'Hide guides index' : 'Show guides index'}
                  title={showGuideIndex ? 'Hide guides index' : 'Show guides index'}
                >
                  <span className="guide-nav-toggle-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faTableColumns} />
                    <FontAwesomeIcon className="guide-nav-toggle-chevron" icon={showGuideIndex ? faChevronLeft : faChevronRight} />
                  </span>
                </button>
              </li>
              {showGuideIndex ? data.guides.map((guideNav, indexGuideNav) => (
                <React.Fragment key={indexGuideNav}>
                  {guideNav.id === data.guide.id ? (
                    <li className="guide-nav-guide">
                      <a href="#top" className="text-decoration-none text-reset">{guideNav.title}</a>
                      {(guideNav?.type || '').toString().toLowerCase() === 'bom' ? (
                        <small className="guide-nav-type-bom"> (BOM)</small>
                      ) : null}
                      {guideNav.published ? '' : <small style={{ color: 'red', fontWeight: 'normal' }}> (draft)</small>}
                    </li>
                  ) : (
                    <li className="guide-nav-guide guide-nav-guide-other">
                      <a href={`${config.prefixPath}/projects/${data.project.slugURL}/guides/${guideNav.slugURL}`}>{guideNav.title}</a>
                      {(guideNav?.type || '').toString().toLowerCase() === 'bom' ? (
                        <small className="guide-nav-type-bom"> (BOM)</small>
                      ) : null}
                      {guideNav.published ? '' : <small style={{ color: 'red', fontWeight: 'normal' }}> (draft)</small>}
                    </li>
                  )}

                  {data.guide?.steps && guideNav.id === data.guide.id && !isBomGuide ? data.guide.steps.map((stepnav, indexnav) => (
                    <li key={indexnav} className={`guide-nav-step${activeHash === `#step${stepnav.position}` ? ' active-nav' : ''}`}>
                      <a href={`#step${stepnav.position}`}> {stepnav.position}. {stepnav.title} {guideProgress?.tracking ? (guideProgress.steps.includes(stepnav.position) ? <FontAwesomeIcon icon={faCheck}/> : null) : null}</a>
                    </li>
                  )) : null}
               </React.Fragment>
               )) : null}
            </ul>
          </div>
        </div>

        {/* Guide main content */}
        <div className={showGuideIndex ? "col-12 col-md-9 guide-main-content" : "col guide-main-content"}>
          {isBomGuide ? renderBomContent() : (
            <>

          <GuideMetadata
            isEditable={isEditable}
            data={data}
            project={project}
            guide={guide}
            updateState={updateState}
            getProgress={getProgress}
            config={config}
            onExportGuide={handleExportClick}
            markdownComponents={{ a: MarkdownLink }}
            markdownRemarkPlugins={markdownRemarkPlugins}
            bomAutocompleteCatalog={bomReferenceCatalog}
            guideAutocompleteCatalog={guideReferenceCatalog}
            onCreateBomItem={createBomItemFromAutocomplete}
          />

          {(isEditable() || data.guide.description) && (
            <div className="row">
              <div className="col-12">
                <h4>Guide description</h4>
                <div><EditableTextArea editable={isEditable()} name='guideDescription' editStyle={{width: '100%', height:100}} value={data.guide.description} index={0} onValueChange={updateState} path={'guide.description'} markdownComponents={{ a: MarkdownLink }} markdownRemarkPlugins={markdownRemarkPlugins} bomAutocompleteCatalog={bomReferenceCatalog} guideAutocompleteCatalog={guideReferenceCatalog} onCreateBomItem={createBomItemFromAutocomplete}/></div>
              </div>
            </div>
          )}

          <GuideComments
            scope="guide"
            comments={guideComments}
            isAuthenticated={isAuthenticated}
            canModerate={canModerateComments}
            currentUsername={userInfo?.username}
            projectSlug={data.project.slugURL}
            guideSlug={data.guide.slugURL}
            onAuthNavigate={handleCommentAuthNavigate}
            onCommentCreated={handleCommentCreated}
            onCommentApproved={handleCommentApproved}
            onCommentDeleted={handleCommentDeleted}
            autoOpen={commentAutoOpen?.scope === 'guide' ? commentAutoOpen : null}
            openAllSignal={openAllCommentsSignal}
            containerId="guide-comments"
          />

          {renderPublishValidationModal()}
          {renderExportModal()}
          {renderMarkdownExportModal()}

          {/* Top-level insert step button */}
          <div className="row">
            {isEditable() ? <div className="hr-with-button-top"><button onClick={(e) => addStep(1)} type="button" className="btn btn-sm btn-outline-dark">Insert Step here</button></div> : <div className="align-items-center"><hr className="styled-hr-top"/></div>}
          </div>

          {/* Steps */}
          {(data.guide?.steps || []).map((step, index) => {
            const stepNotes = getNotesForStep(step);
            const stepNotesCount = stepNotes.length;
            const stepTodoOpenCount = stepNotes.filter(note => note?.isTodo && note?.todoStatus !== 'done').length;
            const stepKey = getStepKey(step);
            const isNotesOpen = openNotesStepKey === stepKey;
            return (
            <div className="row" key={index}>

              {/* Step container */}
              <div className="col-12" onClick={(e) => uploadingHere(step)}>

                {/* Step title */}
                <div style={{height: 39}}>
                    <h3
                      className="anchor-top"
                      id={`step${step.position}`}
                      style={{
                        fontWeight: 850,
                        display: 'inline',
                        marginBottom: 10,
                        scrollMarginTop: canManageGuide ? 127 : 70
                      }}
                    >
                    {guideProgress?.tracking ? (guideProgress.steps.includes(step.position) ? <FontAwesomeIcon onClick={(e) => checkStep(step.position)} icon={faSquareCheck}/> : <FontAwesomeIcon onClick={(e) => checkStep(step.position)} icon={faSquare}/>) : null} &nbsp; <a className="text-decoration-none" href={`#step${step.position}`}>Step {step.position}</a> &nbsp;&nbsp; 

                    {(activeIndex === index && isEditable()) ? (
                      <input 
                        id={`titleinput-${index}`}
                        key={`titleinput-${index}`}
                        type="text" 
                        value={inputValue} 
                        onChange={handleInputChange} 
                        onBlur={() => handleBlur(index)}
                        onKeyPress={(event) => handleKeyPress(event, index)}
                        style={{fontWeight: "normal"}}
                      />
                    ) : (
                      <small style={{cursor: isEditable() ? "context-menu" : "", fontWeight: "normal"}} id={`title-${step.position}`} onClick={() => handleStepTitleClick(index, step.title)}>{step.title}</small>
                    )}
                  </h3>
                  {isEditable() ? (
                    <div className="step-actions">
                      <div className="btn-group">
                        {step?.aiGenerated ? (
                          <span
                            className="step-ai-generated-indicator"
                            title="This text was 100% generated by AI."
                            aria-label="This text was 100% generated by AI."
                          >
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                          </span>
                        ) : null}
                        <button className="btn btn-sm btn-outline-dark" onClick={(e) => {removeStep(step.position, e); return false;}}>Delete</button>
                        <EditableMoveStep value={step.position} name="moveto" index={index} onValueChange={moveStep}/>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            enterStepHistory(step);
                          }}
                          disabled={historyLoading}
                        >
                          History
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            undoStep(step);
                          }}
                          disabled={historyLoading}
                        >
                          Undo
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!stepKey) return;
                            setTodoFocusNoteId(null);
                            setOpenNotesStepKey(prev => (prev === stepKey ? null : stepKey));
                          }}
                        >
                          Notes ({stepNotesCount})
                          {stepTodoOpenCount > 0 ? (
                            <span className="badge text-bg-warning ms-1">TODO {stepTodoOpenCount}</span>
                          ) : null}
                        </button>
                      </div>
                      {isNotesOpen ? (
                        <GuideNotes
                          notes={guideNotes}
                          stepPosition={step.position}
                          stepId={step.stepId}
                          canManage={canManageNotes}
                          projectSlug={data.project.slugURL}
                          guideSlug={data.guide.slugURL}
                          onNoteCreated={handleNoteCreated}
                          onNoteUpdated={handleNoteUpdated}
                          onNoteDeleted={handleNoteDeleted}
                          mode="floating"
                          focusNoteId={todoFocusNoteId}
                          onFocusHandled={() => setTodoFocusNoteId(null)}
                          onClose={() => {
                            setOpenNotesStepKey(null);
                            setTodoFocusNoteId(null);
                          }}
                        />
                      ) : null}
                    </div>
                  ) : <div style={{display: 'inline-flex', height: 3}}></div>}
                </div>

                {/* Step images and content */}
                <div style={{paddingLeft: 14, paddingRight: 14}} className="row">

                  {/* Left column */}
                  <div
                    className={`col-12 order-1 order-lg-1 imgarea guide-step-media-column${showGuideIndex ? ' col-lg-6' : ' guide-step-media-column-expanded'}`}
                    style={{paddingTop: 15}}
                  >
                    <div className="guide-step-media-viewport">

                    {/* Step main image */}
                    <div className="row guide-step-main-image-row" key={index}>
                      <div
                        className={`col-12 drop-zone${dragOverStepIndex === step.position ? ' drag-over' : ''}`}
                        style={{
                          // paddingTop: 15,
                          textAlign:'center',
                          position:"relative",
                          border: "1px solid", borderRadius: "0.5rem",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center"
                        }}
                        onDragEnter={(e) => handleDragEnter(e, step.position)}
                        onDragOver={(e) => handleDragOver(e, step.position)}
                        onDragLeave={(e) => handleDragLeave(e, step.position)}
                        onDrop={(e) => handleDrop(e, step.position, step.title, step.stepId)}
                        onMouseEnter={() => setHoveredStepIndex(index)}
                        onMouseLeave={() => setHoveredStepIndex(null)}>
                          {dragOverStepIndex === step.position && isEditable() ? (
                            <div className="drop-zone-hint">
                              <FontAwesomeIcon icon={faCloudArrowUp} size="2x"/>
                              <span>Drop image files here to add them to Step {step.position}</span>
                            </div>
                          ) : null}
                          {activeImages[index] ? (
                              <div className="step-media-frame">
                                {isVideo(activeImages[index]) ? getVideoPlayer(activeImages[index]) : (
                                  <LazyImage
                                    onClick={(e) => {
                                      if (isEditable()) {
                                        newEditor(e, step.position, step.title, step.stepId);
                                      } else {
                                        openImageLightbox(step, index, activeImages[index]);
                                      }
                                      return false;
                                    }}
                                    wrapperClassName="step-media-image-wrapper"
                                    className="step-media-main-image"
                                    alt={getStepAlt(step)}
                                    style={{
                                      cursor: 'pointer', 
                                      objectFit: 'contain',
                                      width: 'auto',
                                      height: 'auto',
                                      maxWidth: '100%',
                                      maxHeight: '100%'
                                    }}
                                    src={activeImages[index]}
                                    key={index}
                                  />
                                )}
                                {step.images.length > 1 ? (
                                  <>
                                    <button
                                      type="button"
                                      className="step-media-carousel-button is-prev"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        cycleStepMedia(step, index, -1);
                                      }}
                                      aria-label={`Show previous image for Step ${step.position}`}
                                      title="Previous image"
                                    >
                                      <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <button
                                      type="button"
                                      className="step-media-carousel-button is-next"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        cycleStepMedia(step, index, 1);
                                      }}
                                      aria-label={`Show next image for Step ${step.position}`}
                                      title="Next image"
                                    >
                                      <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                  </>
                                ) : null}
                                {index === hoveredStepIndex && isEditable() && (
                                  <div className="btn-group" style={{position: 'absolute', top: 0, right: 0, margin: 5}}>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-dark"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        deleteImage(index, activeImages[index]);
                                      }}
                                      aria-label="Delete image"
                                      title="Delete image"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                    {!isVideo(activeImages[index]) ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-dark"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openImageMarkupEditor(getImageNodeByUrl(activeImages[index]), step.position, step.title, step.stepId);
                                          }}
                                          aria-label="Markup image"
                                          title="Markup image"
                                        >
                                          <FontAwesomeIcon icon={faPencil} />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-dark"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            ReshapeImage(step.position, step.title, activeImages[index], step.stepId);
                                          }}
                                          aria-label="Reshape image"
                                          title="Reshape image"
                                        >
                                          <FontAwesomeIcon icon={faCropSimple} />
                                        </button>
                                      </>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                          ) : (
                            <div style={{position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column'}}>
                              <LazyImage
                                src={STEP_PLACEHOLDER}
                                alt={getStepAlt(step, 'placeholder')}
                                style={{
                                  objectFit: 'contain', 
                                  maxHeight: 330, 
                                  opacity: 0.5, 
                                  cursor: isEditable() ? 'pointer' : 'default'
                                }}
                                onClick={isEditable() ? (e) => showFileDialog(e, step.position) : undefined}
                              />
                              {isEditable() && (
                                <div style={{position: 'absolute', textAlign: 'center', color: '#666', fontSize: '14px', fontWeight: '500', textShadow: '0 1px 3px rgba(255,255,255,0.8)', pointerEvents: 'none'}}>
                                  Drop image files here,<br/>or click to upload.
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Image slider and counter */}
                    {step.images.length > 1 && (
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', padding: '10px 0'}}>
                        <span style={{fontSize: '14px', color: '#666', whiteSpace: 'nowrap'}}>
                          {Math.max(1, step.images.findIndex(img => img.url === activeImages[index]) + 1)} of {step.images.length}
                        </span>
                      </div>
                    )}

                    {/* Step image gallery */}
                    <div className="row guide-step-image-gallery-row">
                      <div className="imglist-wrapper">
                      {step.images.length > 1 && (
                        <SortableList
                          onSortEnd={(oldIndex, newIndex) => onSortEnd(oldIndex, newIndex, index)}
                          className="imglist"
                          autoScroll
                        >
                          {step.images.map((stepImage, indexImage) => (
                            isEditable() ? (
                              <SortableItem key={`sort-${index}-${indexImage}`}>
                                <div className="imgitem">
                                  {stepImage.type !== 'video' ? (
                                    <LazyImage
                                      alt={getStepAlt(step, 'thumbnail')}
                                      wrapperClassName="imgitem-media-wrapper"
                                      style={{
                                        userSelect: 'none', 
                                        width: '100%',
                                        height: '100%',
                                        cursor: 'pointer', 
                                        objectFit: 'cover'
                                      }}
                                      placeholderStyles={{minHeight: 0}}
                                      src={stepImage.url}
                                      key={indexImage}
                                      className={activeImages[index] === stepImage.url ? 'border-tumb' : ''}
                                      onClick={() => handleImageClick(stepImage.url, index)}
                                    />
                                  ) : (
                                    drawVideoThumb(stepImage, indexImage, index, step)
                                  )}
                                  <SortableKnob>
                                    <div title="Grab to sort" className="sort-grab-thumb"><FontAwesomeIcon icon={faArrowRightArrowLeft} /></div>
                                  </SortableKnob>
                                </div>
                              </SortableItem>
                            ) : (
                              <div className="imgitem" key={`view-${index}-${indexImage}`}>
                                {stepImage.type !== 'video' ? (
                                  <LazyImage
                                    alt={getStepAlt(step, 'thumbnail')}
                                    wrapperClassName="imgitem-media-wrapper"
                                    style={{
                                      userSelect: 'none',
                                      width: '100%',
                                      height: '100%',
                                      cursor: 'pointer',
                                      objectFit: 'cover'
                                    }}
                                    placeholderStyles={{minHeight: 0}}
                                    src={stepImage.url}
                                    key={indexImage}
                                    className={activeImages[index] === stepImage.url ? 'border-tumb' : ''}
                                    onClick={() => handleImageClick(stepImage.url, index)}
                                  />
                                ) : (
                                  drawVideoThumb(stepImage, indexImage, index, step)
                                )}
                              </div>
                            )
                          ))}
                        </SortableList>
                      )}
                      </div>
                    </div>
                    </div>
                    
                    {/* Image upload buttons */}
                    <div className="row">
                      <div style={{paddingLeft: 1}} className="col-12">
                        {isEditable() && (
                          <input
                            type="file"
                            id={`fileInput-${step.position}`}
                            accept="image/*"
                            capture
                            multiple
                            onChange={(e) => handleChangeFile(e, step.position, step.title, step.stepId)}
                            style={{display: 'none'}}
                          />
                        )}
                        {isEditable() ? 
                          <div className="btn-group" style={{marginTop: 10}}>
                            {(loading) ? <Spinner style={{height: 31}} animation="border" /> : <button onClick={(e) => showFileDialog(e, step.position)} type="button" className="btn btn-sm btn-outline-dark">Upload Image</button>}
                            <button onClick={() => setModalShow(true)} type="button" className="btn btn-sm btn-outline-dark">Upload from different Device ({connectedClients.length})</button>
                            <button onClick={(e) => showAddVideo(e, step.position)} type="button" className="btn btn-sm btn-outline-dark">Add Video</button>
                          </div>
                        : <div style={{height: 41}}></div>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right column */}
                  <div className={`col-12 order-2 order-lg-2 guide-step-text-column${showGuideIndex ? ' col-lg-6' : ' guide-step-text-column-expanded'}`}>

                    {/* Step text content */}
                    <div key={`stepContent${index}`} style={{paddingTop: 21}}>
                      {(activeContentIndex === index && isEditable()) ? (
                        <>
                          <BomMarkdownTextarea
                            id={`content-${index}`}
                            onValueChange={setContentValue}
                            onBlur={() => handleContentBlur(index)}
                            value={contentValue}
                            style={{marginTop: -5, width: '100%', height: editorHeight, paddingRight: 72}}
                            wrapperClassName="editable-textarea-wrapper"
                            bomAutocompleteCatalog={bomReferenceCatalog}
                            guideAutocompleteCatalog={guideReferenceCatalog}
                            onCreateBomItem={createBomItemFromAutocomplete}
                          >
                            <div className="step-text-editor-actions">
                              <button
                                type="button"
                                className="step-text-editor-action-trigger"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  ignoreContentBlurRef.current = true;
                                }}
                                onClick={() => openGuideStepAiModal(step, index)}
                                aria-label="Generate step text with AI"
                                title="Generate step text with AI"
                              >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                              </button>
                              <button
                                type="button"
                                className="step-text-editor-action-trigger"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  ignoreContentBlurRef.current = true;
                                }}
                                onClick={() => setShowMarkdownHelp(true)}
                                aria-label="Markdown help"
                                title="Markdown help"
                              >
                                <FontAwesomeIcon icon={faCircleQuestion} />
                              </button>
                            </div>
                          </BomMarkdownTextarea>
                        </>
                      ) : (
                        <div style={{cursor: isEditable() ? "context-menu" : "", marginTop: -9}} id={`renderedcontent-${step.position}`} onClick={(e) => handleContentClick(index, step.content, step.position)}>{(step.content ? <MyMarkdownComponent editable={isEditable()} content={step.content}/> : (isEditable() ? 'Click here to edit the step\'s content.' : ''))}</div>
                      )}
                    </div>

                  </div>
                </div>
                
                <GuideComments
                  scope="step"
                  stepPosition={step.position}
                  stepId={step.stepId}
                  comments={guideComments}
                  isAuthenticated={isAuthenticated}
                  canModerate={canModerateComments}
                  currentUsername={userInfo?.username}
                  projectSlug={data.project.slugURL}
                  guideSlug={data.guide.slugURL}
                  onAuthNavigate={handleCommentAuthNavigate}
                  onCommentCreated={handleCommentCreated}
                  onCommentApproved={handleCommentApproved}
                  onCommentDeleted={handleCommentDeleted}
                  autoOpen={commentAutoOpen?.scope === 'step' && Number(commentAutoOpen?.stepPosition) === Number(step.position) ? commentAutoOpen : null}
                  openAllSignal={openAllCommentsSignal}
                />

                {/* Insert step button */}
                {isEditable() ? <div className="hr-with-button"><button style={{marginBottom: 10}} onClick={(e) => addStep(step.position + 1, e)} type="button" className="btn btn-sm btn-outline-dark">Insert Step here</button></div> : <div className="align-items-center"><hr className="styled-hr"/></div>}

              </div>

            </div>
          );
        })}
          {/* Steps end */}

          {/* Pagination */}
          <GuidePagination data={data}/>
            </>
          )}

          <CenteredModal show={modalShow} onHide={() => setModalShow(false)}/>

          {showImageModal ? (
            <div
              ref={imageLightboxRef}
              className="guide-image-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label={isBomGuide ? 'BOM item images' : 'Step images'}
              tabIndex="-1"
              onMouseDown={handleImageCloseModal}
            >
              <div
                className="guide-image-lightbox-stage"
                style={{
                  '--guide-image-lightbox-width': `${guideImageLightboxViewportRatio * 100}vw`,
                  '--guide-image-lightbox-height': `${guideImageLightboxViewportRatio * 100}vh`
                }}
              >
                <div
                  className="guide-image-lightbox-thumbnails"
                  aria-label={isBomGuide ? 'BOM item image thumbnails' : 'Step image thumbnails'}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {lightboxImages.map((stepImage, indexImage) => (
                    <button
                      type="button"
                      key={`${stepImage.url}-${indexImage}`}
                      className={`guide-image-lightbox-thumbnail${selectedFullImageIndex === indexImage ? ' is-active' : ''}`}
                      onClick={() => {
                        setSelectedFullImageIndex(indexImage);
                        if (selectedStepIndex !== null) {
                          handleImageClick(stepImage.url, selectedStepIndex);
                        }
                      }}
                      aria-label={`Show image ${indexImage + 1}`}
                      aria-current={selectedFullImageIndex === indexImage ? 'true' : undefined}
                    >
                      <LazyImage
                        alt={getStepAlt(currentStep, 'thumbnail')}
                        src={stepImage.url}
                        className="guide-image-lightbox-thumbnail-image"
                        style={{ width: '100%', height: 92, objectFit: 'cover' }}
                        eager={indexImage < 6}
                      />
                    </button>
                  ))}
                </div>

                <div className="guide-image-lightbox-main">
                  <button
                    type="button"
                    className="guide-image-lightbox-close"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleImageCloseModal}
                    aria-label="Close image preview"
                    title="Close"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                  {selectedLightboxImage ? (
                    <img
                      src={selectedLightboxImage}
                      alt={getStepAlt(currentStep, 'full')}
                      className="guide-image-lightbox-image"
                      onMouseDown={(e) => e.stopPropagation()}
                      loading="eager"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {isReferenceLightboxOpen ? (
            <div
              ref={referenceImageLightboxRef}
              className="guide-image-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label={referenceLightboxImage?.alt || 'BOM item image'}
              tabIndex="-1"
              onMouseDown={closeReferenceImageLightbox}
            >
              <div
                className="guide-image-lightbox-stage guide-image-lightbox-stage-single"
                style={{
                  '--guide-image-lightbox-width': `${guideImageLightboxViewportRatio * 100}vw`,
                  '--guide-image-lightbox-height': `${guideImageLightboxViewportRatio * 100}vh`
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="guide-image-lightbox-main">
                  <button
                    type="button"
                    className="guide-image-lightbox-close"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={closeReferenceImageLightbox}
                    aria-label="Close BOM item image preview"
                    title="Close"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                  <img
                    src={referenceLightboxImage.url}
                    alt={referenceLightboxImage?.alt || 'BOM item image'}
                    className="guide-image-lightbox-image"
                    onMouseDown={(e) => e.stopPropagation()}
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Page footer */}
      <Footer/>
      <MarkdownHelpModal show={showMarkdownHelp} onHide={() => setShowMarkdownHelp(false)} />
        </>
      )}
      <GuideStepAiModal
        show={!!guideStepAiState.stepId && !!guideStepAiStep}
        onHide={closeGuideStepAiModal}
        step={guideStepAiStep}
        connectionLoading={guideStepAiState.connectionLoading}
        connectionError={guideStepAiState.connectionError}
        connection={guideStepAiState.connection}
        connectionNotice={guideStepAiState.connectionNotice}
        available={guideStepAiState.available}
        settingsUnavailable={guideStepAiState.settingsUnavailable}
        onRefreshConnection={refreshGuideStepAiConnectionState}
        settingsConnectionsHref={`${config.prefixPath}/settings?section=connections`}
        titleValue={guideStepAiState.title}
        onTitleChange={handleGuideStepAiTitleChange}
        promptHintValue={guideStepAiState.promptHint}
        onPromptHintChange={handleGuideStepAiPromptHintChange}
        modelOptions={guideStepAiState.models}
        selectedModel={guideStepAiState.model}
        onModelChange={handleGuideStepAiModelChange}
        contextStepOptions={GUIDE_STEP_AI_CONTEXT_OPTIONS}
        previousStepsContextCount={guideStepAiState.previousStepsContextCount}
        onPreviousStepsContextCountChange={handleGuideStepAiPreviousStepsContextCountChange}
        followingStepsContextCount={guideStepAiState.followingStepsContextCount}
        onFollowingStepsContextCountChange={handleGuideStepAiFollowingStepsContextCountChange}
        onUploadFiles={uploadGuideStepAiImages}
        showRemoteUploader={guideStepAiState.showRemoteUploader}
        onToggleRemoteUploader={toggleGuideStepAiRemoteUploader}
        uploaderUrl={guideStepAiStep ? getUploaderURL() : ''}
        connectedClients={connectedClients.length}
        onCopyUploaderUrl={copyURL}
        onOpenImageMarkup={(targetImage) => {
          if (!targetImage || !guideStepAiStep) return;
          return openImageMarkupEditor(
            targetImage,
            guideStepAiStep.position,
            guideStepAiState.title || guideStepAiStep.title,
            guideStepAiStep.stepId
          );
        }}
        generationLoading={guideStepAiState.loading}
        generationError={guideStepAiState.error}
        generatedText={guideStepAiState.generatedText}
        onGenerate={generateGuideStepAiText}
        onAccept={acceptGuideStepAiText}
      />
      {/* Toast */}
      <Toaster position="bottom-left" toastOptions={{duration: 5000, style: {background: '#e3e3e3'}}}/>
    </>
  );
}

export default GuideHome;
